import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { screenToLocal, createTransformMatrix, getPivot } from './transformMath';
import { AuthContext } from '../../App';
import { saveHistoryEntry, loadHistoryEntry, pruneHistory, clearHistory, saveRecordingFrame, loadRecordingFrame, getRecordingFrameCount, clearRecordingFrames } from './historyDB';
import axios from 'axios';
import {
  Brush,
  Eraser,
  Square,
  Circle,
  Type,
  Download,
  Save,
  Image as ImageIcon,
  Sparkles,
  Undo,
  Redo,
  Layers,
  Pipette,
  Move,
  ZoomIn,
  ZoomOut,
  Trash2,
  Check,
  Plus,
  Eye,
  EyeOff,
  Loader2,
  PaintBucket,
  Minus,
  Merge,
  MousePointer,
  Crop,
  Aperture,
  X,
  ChevronDown,
  RotateCcw,
  History,
  Keyboard,
  Command,
  Upload,
  Settings,
  Lock,
  Unlock,
  Maximize2,
  Copy,
  Scissors,
  ClipboardPaste,
  Link2,
  Unlink2,
  MoreVertical,
  ArrowUp,
  ArrowDown,
  MessageCircle,
  Palette,
  Video,
  StopCircle,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import { BUBBLE_LIBRARY } from '../../data/bubbleLibrary';

// UI Components
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { cn } from "../../lib/utils";
import { Film as AnimCanvasIcon } from 'lucide-react';
// AnimCanvas 2D animation studio — lazy-loaded so it adds nothing to the editor's initial load
const AnimCanvasEditor = React.lazy(() => import('./animation/AnimCanvasEditor'));

// Brush presets configuration
const BRUSH_PRESETS = [
  { id: 'zhong_fenghua', name: 'Zhong Fenghua', texture: '/brushes/zhong_fenghua.png', spacing: 0.1, sizeJitter: 0.1, angleJitter: 0.3 },
  { id: 'soft_round', name: 'Soft Round', texture: '/brushes/soft_round.png', spacing: 0.1, sizeJitter: 0, angleJitter: 0 },
  { id: 'hard_round', name: 'Hard Round', texture: '/brushes/hard_round.png', spacing: 0.1, sizeJitter: 0, angleJitter: 0 },
  { id: 'charcoal', name: 'Charcoal', texture: '/brushes/charcoal.png', spacing: 0.15, sizeJitter: 0.15, angleJitter: 0.5 },
  { id: 'ink', name: 'Ink', texture: '/brushes/ink.png', spacing: 0.12, sizeJitter: 0.05, angleJitter: 0.1 },
  { id: 'watercolor', name: 'Watercolor', texture: '/brushes/watercolor.png', spacing: 0.2, sizeJitter: 0.2, angleJitter: 0.4 },
  { id: 'textured', name: 'Textured', texture: '/brushes/textured.png', spacing: 0.15, sizeJitter: 0.1, angleJitter: 0.2 },
];

// Constants
const DOUBLE_CLICK_DELAY = 300;
const MAX_HISTORY = 10;
const MAX_LAYERS = 10; // Production-grade memory safety cap

// Keyboard shortcut mappings (Photoshop-style)
const KEYBOARD_SHORTCUTS = {
  'b': { tool: 'brush', label: 'Brush Tool' },
  'e': { tool: 'eraser', label: 'Eraser Tool' },
  'g': { tool: 'fill', label: 'Paint Bucket' },
  'i': { tool: 'eyedropper', label: 'Eyedropper' },
  'm': { tool: 'select', label: 'Marquee Selection' },
  'v': { tool: 'move', label: 'Move Tool' },
  'y': { tool: 'text', label: 'Text Tool' },
  't': { tool: 'transform', label: 'Transform Tool' },
  // 'u': { tool: 'rectangle', label: 'Shape Tools' },
  // 'l': { tool: 'line', label: 'Line Tool' },
  // 'c': { tool: 'crop', label: 'Crop Tool' },
  'q': { tool: 'bubble', label: 'Speech Bubble' },
};

// Available Comic Fonts
const TEXT_FONTS = [
  { name: 'Bangers', label: 'Bangers (Comic)', family: '"Bangers", system-ui' },
  { name: 'Luckiest Guy', label: 'Luckiest Guy (Fun)', family: '"Luckiest Guy", system-ui' },
  { name: 'Comic Neue', label: 'Comic Neue (Dialogue)', family: '"Comic Neue", cursive' },
  { name: 'Creepster', label: 'Creepster (Horror)', family: '"Creepster", system-ui' },
  { name: 'Permanent Marker', label: 'Permanent Marker (Rough)', family: '"Permanent Marker", cursive' },
  { name: 'Bebas Neue', label: 'Bebas Neue (Impact)', family: '"Bebas Neue", sans-serif' },
  { name: 'Ruslan Display', label: 'Ruslan Display (Tech)', family: '"Ruslan Display", system-ui' },
  { name: 'Arial', label: 'Arial (Basic)', family: 'Arial, sans-serif' },
];

// Color wheel helper functions
const hslToHex = (h, s, l) => {
  if (isNaN(h) || isNaN(s) || isNaN(l)) return '#000000';
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const hexToHsl = (hex) => {
  if (!hex || typeof hex !== 'string' || hex.length < 4) return { h: 0, s: 0, l: 0 };

  // Handle short hex
  if (hex.length === 4) {
    hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return { h: 0, s: 0, l: 0 };

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
    h *= 360;
    s *= 100;
  }
  // Always convert l to 0-100 range (was previously only inside else branch)
  l *= 100;

  return { h, s, l };
};

// Catmull-Rom Spline Interpolation Helper
const getCatmullRomPoint = (p0, p1, p2, p3, t) => {
  const t2 = t * t;
  const t3 = t2 * t;

  const f0 = -0.5 * t3 + t2 - 0.5 * t;
  const f1 = 1.5 * t3 - 2.5 * t2 + 1.0;
  const f2 = -1.5 * t3 + 2.0 * t2 + 0.5 * t;
  const f3 = 0.5 * t3 - 0.5 * t2;

  const x = p0.x * f0 + p1.x * f1 + p2.x * f2 + p3.x * f3;
  const y = p0.y * f0 + p1.y * f1 + p2.y * f2 + p3.y * f3;

  // Interpolate pressure if available
  const p0p = p0.pressure || 1;
  const p1p = p1.pressure || 1;
  const p2p = p2.pressure || 1;
  const p3p = p3.pressure || 1;
  const pressure = p0p * f0 + p1p * f1 + p2p * f2 + p3p * f3;

  return { x, y, pressure };
};

const calculateDistance = (p1, p2) => Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);

const ComicaProV2 = ({
  mode = "full",
  panelWidth,
  panelHeight,
  initialImage,
  onSavePanel,
  initialLayers,
  panelClipPath,
  panelPoints,
  onCancel,
  onProcessingChange,
  initialAnimation = null // AnimCanvas data when this panel holds an animation
}) => {
  const { API, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Project loading state
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState(null);

  // Canvas refs
  const mainCanvasRef = useRef(null);
  const compositeCanvasRef = useRef(null);

  // Recording refs
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingCanvasRef = useRef(null); // offscreen canvas for compositing at lower fps
  const containerRef = useRef(null);

  // Layer canvases stored in ref for performance
  const layerCanvasesRef = useRef({});
  const brushTexturesRef = useRef({});
  // Cached temp canvas for brush stamping to avoid creating new canvas elements on every stamp
  const stampTempCanvasRef = useRef(null);
  const stampTempCtxRef = useRef(null);

  // NEW: Offscreen canvas for the current stroke (Phase 5)
  // This allows compositing the entire stroke at once (solving alpha overlap)
  const currentStrokeCanvasRef = useRef(null);

  // Drawing state stored in ref (not state) for real-time performance
  const drawingStateRef = useRef({
    isDrawing: false,
    lastX: 0,
    lastY: 0,
    currentTool: 'brush',
    brushSize: 20,
    brushColor: '#000000',
    brushOpacity: 1,
    fillColor: '#ffffff',
    strokeWidth: 2,
    activeLayerId: 1,
    currentBrush: BRUSH_PRESETS[0], // Zhong Fenghua as default
    startPos: { x: 0, y: 0 },
    zoom: 1,
    panOffset: { x: 0, y: 0 },
    selection: null, // {x, y, width, height}
    pressure: 1.0, // Pen pressure (0.0 - 1.0)
    pressureSensitivity: true,
    pressureAffectsSize: true,
    pressureAffectsOpacity: true
  });

  // NEW: Stroke Engine State
  const strokeStateRef = useRef({
    points: [],     // Accumulated smoothed points for the current stroke
    buffer: [],     // Raw input points waiting to be processed
    isActive: false, // True while the user is physically drawing
    layerId: null,   // Target layer ID for the current stroke
    settings: {      // Snapshot of brush settings at start of stroke
      size: 20,
      color: '#000000',
      opacity: 1,
      brush: BRUSH_PRESETS[0], // Use default brush, never null
      tool: 'brush'
    },
    // Interpolation state
    lastX: 0,
    lastY: 0,
    lastTime: 0,
    lastMidX: undefined,
    lastMidY: undefined,
    lastSize: 20,
    lastOpacity: 1,
    lastVelocity: 0,
    remainder: 0 // Distance accumulator for correct stamping
  });

  // Animation frame ref
  const rafRef = useRef(null);
  const needsCompositeRef = useRef(false);
  const processStrokeRef = useRef(null); // Ref to hold the stroke processing function

  // Selection state for rendering
  const [selection, setSelection] = useState(null);

  // UI State (these can use React state as they don't affect drawing performance)
  const [currentTool, setCurrentTool] = useState('brush');
  const [brushSize, setBrushSize] = useState(20);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushOpacity, setBrushOpacity] = useState(1);
  const [fillColor, setFillColor] = useState('#ffffff');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [currentBrush, setCurrentBrush] = useState(BRUSH_PRESETS[0]);
  const [showBrushPicker, setShowBrushPicker] = useState(false);

  // Pie Menu state
  const [showPieMenu, setShowPieMenu] = useState(false);
  const [pieMenuPosition, setPieMenuPosition] = useState({ x: 0, y: 0 });
  const pieMenuRef = useRef(null);
  const pieMenuDragRef = useRef(null); // { startX, startY, menuStartX, menuStartY }

  // Color wheel state
  const [colorHue, setColorHue] = useState(0);
  const [colorSaturation, setColorSaturation] = useState(100);
  const [colorLightness, setColorLightness] = useState(50);

  // Brush rotation/angle
  const [brushAngle, setBrushAngle] = useState(0);

  // Brush History
  const [brushHistory, setBrushHistory] = useState([]);
  const MAX_BRUSH_HISTORY = 10;

  // Secondary color (for X key swap)
  const [secondaryColor, setSecondaryColor] = useState('#ffffff');

  // Keyboard shortcuts panel
  const [showShortcutsPanel, setShowShortcutsPanel] = useState(false);

  // Shape tool cycling
  const shapeToolsRef = useRef(['rectangle', 'circle', 'line']);
  const shapeToolIndexRef = useRef(0);

  // Custom cursor state
  // Custom cursor state - OPTIMIZED: Use ref for position to avoid re-renders
  const cursorRef = useRef(null);
  const [showBrushCursor, setShowBrushCursor] = useState(false);
  const [isAltPressed, setIsAltPressed] = useState(false);
  const isAltPressedRef = useRef(false); // Ref for immediate access in event handlers
  const isSpacePressedRef = useRef(false); // Ref for spacebar state (panning)
  const previousToolRef = useRef('brush');

  // Inpaint mask drawing state
  const [isDrawingMask, setIsDrawingMask] = useState(false);
  const maskCanvasRef = useRef(null);
  const [maskBrushSize, setMaskBrushSize] = useState(30);
  // OPTIMIZED: Use ref for mask cursor to avoid re-renders
  const maskCursorRef = useRef(null);
  const [showMaskCursor, setShowMaskCursor] = useState(false);
  const [inpaintResult, setInpaintResult] = useState(null);

  // Move tool state
  const [layerOffsets, setLayerOffsets] = useState({});
  const layerOffsetsRef = useRef({});
  const moveStartPosRef = useRef({ x: 0, y: 0 });
  const moveLayerStartRef = useRef({ x: 0, y: 0 });

  // Text tool state
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textMaxWidth, setTextMaxWidth] = useState(300); // UI state for Photoshop-style paragraph constraints

  // Bubble tool state
  const [showBubbleEdit, setShowBubbleEdit] = useState(false);
  const showBubbleEditRef = useRef(false);
  useEffect(() => { showBubbleEditRef.current = showBubbleEdit; }, [showBubbleEdit]);
  const [bubblePanelPos, setBubblePanelPos] = useState({ x: 56, y: 80 });
  const [textPanelPos, setTextPanelPos] = useState({ x: 56, y: 80 });
  const [textFontSize, setTextFontSize] = useState(24);
  const [textFontFamily, setTextFontFamily] = useState('Arial');
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  // Advanced Text Settings
  const [textStrokeColor, setTextStrokeColor] = useState('#000000');
  const [textStrokeWidth, setTextStrokeWidth] = useState(0);
  const [textShadowColor, setTextShadowColor] = useState('#000000');
  const [textShadowBlur, setTextShadowBlur] = useState(0);
  const [textShadowOffsetX, setTextShadowOffsetX] = useState(2);
  const [textShadowOffsetY, setTextShadowOffsetY] = useState(2);
  const [activeTextLayerId, setActiveTextLayerId] = useState(null);
  const textInputRef = useRef(null);

  // Magic Ink state
  const [magicInkProcessing, setMagicInkProcessing] = useState(false);
  const [magicInkStyle, setMagicInkStyle] = useState('comic');
  const [magicInkStrength, setMagicInkStrength] = useState(0.7);

  // Crop tool state
  const [cropRegion, setCropRegion] = useState(null);
  const [showCropOverlay, setShowCropOverlay] = useState(false);

  // Save modal state moved below canvas dimensions

  const [saveTitle, setSaveTitle] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Layers state for UI
  const [layers, setLayers] = useState([
    { id: 1, name: 'Background', visible: true, opacity: 1, locked: false, blendMode: 'normal', hasMask: false, maskDisabled: false }
  ]);
  const [activeLayerId, setActiveLayerId] = useState(1);
  const [showAnimCanvas, setShowAnimCanvas] = useState(false); // AnimCanvas (2D animation studio) overlay

  // Layer clipboard for copy/paste operations
  const [layerClipboard, setLayerClipboard] = useState(null);

  // Layer mask refs and state
  const layerMasksRef = useRef({}); // {layerId: canvas}
  const [editingMask, setEditingMask] = useState(false); // true when painting on mask
  const [viewingMask, setViewingMask] = useState(null); // layerId if viewing mask solo

  // Transform tool state
  const [transformActive, setTransformActive] = useState(false);
  const [transformData, setTransformData] = useState({
    x: 0, y: 0, width: 100, height: 100, rotation: 0, scaleX: 1, scaleY: 1
  });
  const transformInitialRef = useRef(null); // Store initial state for cancel
  const transformLayerRef = useRef(null); // Temp canvas for transform preview
  const [transformDragging, setTransformDragging] = useState(null); // 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w'
  const transformDragStartRef = useRef({ x: 0, y: 0, data: null }); // Start position for drag
  const transformStartMatrixRef = useRef(null); // Cache matrix on start drag
  const transformPivotRef = useRef(null); // {x, y} in local space

  // Layer rename state
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, layerId: null });

  // History
  const historyRef = useRef([]);
  const historyStepRef = useRef(-1);
  const [historyStep, setHistoryStep] = useState(-1);
  const [historyLength, setHistoryLength] = useState(0);
  const [historyDisabled, setHistoryDisabled] = useState(false); // Disabled on canvas taint

  // Pen Pressure Support
  const [pressureSensitivity, setPressureSensitivity] = useState(true);
  const [pressureAffectsSize, setPressureAffectsSize] = useState(true);
  const [pressureAffectsOpacity, setPressureAffectsOpacity] = useState(true);
  const currentPressureRef = useRef(1.0); // 0.0 to 1.0
  const lastPressureUpdateRef = useRef(0); // Throttle displayPressure updates
  const [displayPressure, setDisplayPressure] = useState(1.0); // For UI display

  // ─── Phase 1 Brush Engine: Advanced Properties ───────────────────────────
  const [brushHardness, setBrushHardness] = useState(0.9);   // 0=airbrush soft, 1=hard edge
  const [brushFlow, setBrushFlow] = useState(1.0);           // Per-dab opacity multiplier (builds up like ink)
  const [brushStabilizer, setBrushStabilizer] = useState(0); // 0=off, 1-10=smoothing strength
  const [brushScatter, setBrushScatter] = useState(0);       // 0=off, 0.5=medium scatter
  // Touch velocity → pressure simulation ref
  const touchVelocityRef = useRef({ lastX: 0, lastY: 0, lastTime: 0, smoothedPressure: 1.0 });
  // Stabilizer: rolling buffer of recent input points
  const stabilizerBufferRef = useRef([]);
  // Triple-tap gesture to open pie menu on mobile/tablet (3 taps within 400ms)
  const tripleTapRef = useRef({ count: 0, lastTime: 0, x: 0, y: 0 });

  // View controls — HYBRID: refs for hot path (0 re-renders), state for JSX that reads occasionally
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // Pan offset in screen pixels
  const [viewRotation, setViewRotation] = useState(0); // Photoshop-style rotate view (non-destructive)
  const zoomRef = useRef(1);             // Shadow ref for zoom hot path
  const panRef = useRef({ x: 0, y: 0 }); // Shadow ref for pan hot path
  const viewRotationRef = useRef(0);     // Shadow ref for rotated view hot path
  const panWrapperRef = useRef(null);     // DOM ref for translate wrapper
  const zoomWrapperRef = useRef(null);    // DOM ref for scale wrapper
  const [isPanning, setIsPanning] = useState(false); // Dragging state for pan tool
  const panStartRef = useRef({ x: 0, y: 0 }); // Reference for pan start coordinates
  const zoomDragRef = useRef(null); // { startY, startZoom } for Ctrl+pen drag zoom
  const rotateViewDragRef = useRef(null); // Photoshop-style R + drag rotate view
  const isRotateViewPressedRef = useRef(false);
  const [isRotateViewPressed, setIsRotateViewPressed] = useState(false);
  const touchRef = useRef({ lastDist: 0, lastCenter: null }); // For two-finger pinch-to-zoom

  // Helper: update pan ref + DOM directly (no React re-render)
  const updatePanDirect = useCallback((newPan) => {
    panRef.current = newPan;
    if (panWrapperRef.current) {
      panWrapperRef.current.style.transform = `translate(${newPan.x}px, ${newPan.y}px)`;
    }
  }, []);

  const applyViewTransformDirect = useCallback((newZoom = zoomRef.current, newRotation = viewRotationRef.current) => {
    if (zoomWrapperRef.current) {
      zoomWrapperRef.current.style.transform = `scale(${newZoom}) rotate(${newRotation}deg)`;
    }
  }, []);

  // Helper: update zoom ref + DOM directly (no React re-render)
  const updateZoomDirect = useCallback((newZoom) => {
    zoomRef.current = newZoom;
    applyViewTransformDirect(newZoom, viewRotationRef.current);
  }, [applyViewTransformDirect]);

  // Helper: update rotate-view ref + DOM directly (no React re-render)
  const updateViewRotationDirect = useCallback((newRotation) => {
    const normalized = ((newRotation % 360) + 360) % 360;
    viewRotationRef.current = normalized;
    applyViewTransformDirect(zoomRef.current, normalized);
  }, [applyViewTransformDirect]);

  // Sync refs to state (triggers re-render for JSX that needs pan/zoom/rotation)
  const syncViewState = useCallback(() => {
    setZoom(zoomRef.current);
    setPan({ ...panRef.current });
    setViewRotation(viewRotationRef.current);
  }, []);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const recordingDurationRef = useRef(0); // Use ref to avoid re-renders every second
  const recTimerDisplayRef = useRef(null); // DOM ref for timer display
  const recTimerDisplay2Ref = useRef(null); // DOM ref for second timer in overlay
  const recTimerDisplay3Ref = useRef(null); // DOM ref for download modal
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [showRecordingDownload, setShowRecordingDownload] = useState(false);
  const [processingTimelapse, setProcessingTimelapse] = useState(null); // null or { current, total }

  // Notify parent and protect against accidental close during timelapse processing
  useEffect(() => {
    const isProcessing = !!processingTimelapse;
    if (onProcessingChange) onProcessingChange(isProcessing);

    if (isProcessing) {
      const handler = (e) => {
        e.preventDefault();
        e.returnValue = 'Your timelapse is still being processed. Are you sure you want to leave?';
        return e.returnValue;
      };
      window.addEventListener('beforeunload', handler);
      return () => window.removeEventListener('beforeunload', handler);
    }
  }, [processingTimelapse, onProcessingChange]);

  // Canvas dimensions — use panel dimensions when in panel mode
  const [canvasWidth, setCanvasWidth] = useState(mode === 'panel' && panelWidth ? panelWidth : 1024);
  const [canvasHeight, setCanvasHeight] = useState(mode === 'panel' && panelHeight ? panelHeight : 768);

  // Store the original full-res image URL so Transform can access hidden/cropped areas
  const fullResImageRef = useRef(null);

  // Save modal state (Moved here to access canvasWidth)
  const [showSaveModal, _setShowSaveModal] = useState(false);

  const setShowSaveModal = useCallback((show) => {
    if (show && mode === 'panel' && onSavePanel) {
      // Panel Mode Save Interception — preserve full-res original image + layer data
      try {
        const flatCanvas = document.createElement('canvas');
        flatCanvas.width = canvasWidth;
        flatCanvas.height = canvasHeight;
        const ctx = flatCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        if (mainCanvasRef.current) {
          ctx.drawImage(mainCanvasRef.current, 0, 0);
        }

        // Serialize layers with pixel data + type metadata so they survive reopen
        const dpr = window.devicePixelRatio || 1;
        const serializedLayers = (layersRef.current || []).map(layer => {
          const layerCanvas = layerCanvasesRef.current[layer.id];
          let imageData = null;
          if (layerCanvas) {
            // Convert to logical-sized canvas for serialization (remove DPR scaling)
            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = canvasWidth;
            exportCanvas.height = canvasHeight;
            const exportCtx = exportCanvas.getContext('2d');
            exportCtx.drawImage(layerCanvas, 0, 0, canvasWidth, canvasHeight);
            imageData = exportCanvas.toDataURL('image/png');
          }
          return {
            id: layer.id,
            name: layer.name,
            visible: layer.visible,
            opacity: layer.opacity,
            locked: layer.locked,
            blendMode: layer.blendMode,
            hasMask: layer.hasMask,
            maskDisabled: layer.maskDisabled,
            type: layer.type || 'raster',
            textConfig: layer.textConfig || null,
            bubbleConfig: layer.bubbleConfig || null,
            imageData
          };
        });

        // Always return object with fullImage preserved + preview + layers for rehydration
        const saveData = {
          preview: flatCanvas.toDataURL(),
          fullImage: fullResImageRef.current || null,
          layers: serializedLayers
        };
        onSavePanel(saveData);
      } catch (e) {
        console.error("Panel save error", e);
        toast.error("Failed to save panel");
      }
      return;
    }
    _setShowSaveModal(show);
  }, [mode, onSavePanel, canvasWidth, canvasHeight]);

  // UI state
  const [showLayersPanel, setShowLayersPanel] = useState(true);
  const [showToolProperties] = useState(true);
  const [showAIEditor, setShowAIEditor] = useState(false);
  const [projectName, setProjectName] = useState('Untitled Project');

  // Responsive state
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1536);
  const [isTablet, setIsTablet] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1280 && window.innerWidth < 1536);
  const [showMobileLayersPanel, setShowMobileLayersPanel] = useState(false);
  const [showMobileToolsMenu, setShowMobileToolsMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 1536);
      setIsTablet(w >= 1280 && w < 1536);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // AI Editor state
  const [aiMode, setAiMode] = useState('sketch2ai');
  const [aiModel, setAiModel] = useState('caravaggio'); // 'caravaggio' or 'gemini'
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiNegativePrompt, setAiNegativePrompt] = useState('');
  const [aiStrength, setAiStrength] = useState(0.3);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiGeneratedImage, setAiGeneratedImage] = useState(null);
  const [aiCanvasSnapshot, setAiCanvasSnapshot] = useState(null);
  const [aiMaskCanvas, setAiMaskCanvas] = useState(null);
  const [credits, setCredits] = useState(10);
  const [generationElapsed, setGenerationElapsed] = useState(0);
  const generationTimerRef = useRef(null);

  // Fetch fresh credits from API
  const fetchCredits = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/ai/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Credits fetched:", response.data);
      setCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [API, token]);

  // Add to brush history when brush/settings change
  const addToBrushHistory = useCallback((brush, color, size, opacity) => {
    const historyEntry = {
      brush: brush ? { ...brush } : { ...BRUSH_PRESETS[0] },
      color: color || '#000000',
      size: size || 20,
      opacity: opacity ?? 1,
      timestamp: Date.now()
    };

    setBrushHistory(prev => {
      // Check if same brush already exists at top
      if (prev.length > 0 &&
        prev[0].brush.id === brush.id &&
        prev[0].color === color &&
        prev[0].size === size) {
        return prev;
      }

      const newHistory = [historyEntry, ...prev.filter(h =>
        !(h.brush.id === brush.id && h.color === color && h.size === size)
      )];
      return newHistory.slice(0, MAX_BRUSH_HISTORY);
    });
  }, []);

  // Sync drawing state ref with UI state
  useEffect(() => {
    drawingStateRef.current.currentTool = currentTool;
    setShowBubbleEdit(false);
  }, [currentTool]);

  useEffect(() => {
    drawingStateRef.current.brushSize = brushSize;
  }, [brushSize]);

  // CRITICAL PERFORMANCE FIX: Explicit Canvas GPU Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log("[Memory Cleanup] Unmounting ComicaProV2 - destroying canvas layers to free GPU VRAM");

      // Destroy Layer Canvases
      if (layerCanvasesRef.current) {
        Object.values(layerCanvasesRef.current).forEach(canvas => {
          if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
          }
        });
        layerCanvasesRef.current = {};
      }

      // Destroy Stroke Buffer Canvas
      if (currentStrokeCanvasRef.current) {
        currentStrokeCanvasRef.current.width = 0;
        currentStrokeCanvasRef.current.height = 0;
        currentStrokeCanvasRef.current = null;
      }

      // Destroy Main and Composite Canvases
      if (mainCanvasRef.current) {
        mainCanvasRef.current.width = 0;
        mainCanvasRef.current.height = 0;
      }
      if (compositeCanvasRef.current) {
        compositeCanvasRef.current.width = 0;
        compositeCanvasRef.current.height = 0;
      }

      // Cleanup brush refs
      brushTexturesRef.current = {};
    };
  }, []);

  useEffect(() => {
    drawingStateRef.current.brushColor = brushColor;
    // Update HSL from hex
    const hsl = hexToHsl(brushColor);
    setColorHue(hsl.h);
    setColorSaturation(hsl.s);
    setColorLightness(hsl.l);
  }, [brushColor]);

  useEffect(() => {
    drawingStateRef.current.brushOpacity = brushOpacity;
  }, [brushOpacity]);

  useEffect(() => {
    drawingStateRef.current.fillColor = fillColor;
  }, [fillColor]);

  useEffect(() => {
    drawingStateRef.current.strokeWidth = strokeWidth;
  }, [strokeWidth]);

  useEffect(() => {
    drawingStateRef.current.activeLayerId = activeLayerId;
  }, [activeLayerId]);

  useEffect(() => {
    drawingStateRef.current.currentBrush = currentBrush;
  }, [currentBrush]);

  useEffect(() => {
    drawingStateRef.current.zoom = zoom;
  }, [zoom]);

  // Sync pressure settings with drawing state ref
  useEffect(() => {
    drawingStateRef.current.pressureSensitivity = pressureSensitivity;
  }, [pressureSensitivity]);

  useEffect(() => {
    drawingStateRef.current.pressureAffectsSize = pressureAffectsSize;
  }, [pressureAffectsSize]);

  useEffect(() => {
    drawingStateRef.current.pressureAffectsOpacity = pressureAffectsOpacity;
  }, [pressureAffectsOpacity]);

  // ─── Phase 1 Brush Engine: sync advanced settings to drawing hot-path ────
  useEffect(() => { drawingStateRef.current.brushHardness = brushHardness; }, [brushHardness]);
  useEffect(() => { drawingStateRef.current.brushFlow = brushFlow; }, [brushFlow]);
  useEffect(() => { drawingStateRef.current.brushStabilizer = brushStabilizer; }, [brushStabilizer]);
  useEffect(() => { drawingStateRef.current.brushScatter = brushScatter; }, [brushScatter]);

  useEffect(() => {
    layerOffsetsRef.current = layerOffsets;
  }, [layerOffsets]);

  const lastActiveLayerIdRef = useRef(null);

  // Refs for keyboard shortcut handlers (to avoid initialization issues)
  const undoRef = useRef(null);
  const redoRef = useRef(null);
  const downloadImageRef = useRef(null);
  const saveToHistoryRef = useRef(null);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const key = e.key.toLowerCase();

      // Spacebar - Toggle pie menu (tap to open, tap again to close)
      if (key === ' ' && !e.repeat) {
        e.preventDefault();
        if (showPieMenu) {
          setShowPieMenu(false);
        } else {
          // Get mouse position for pie menu
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            setPieMenuPosition({
              x: Math.min(window.innerWidth - 300, Math.max(150, (window.innerWidth / 2))),
              y: Math.min(window.innerHeight - 300, Math.max(150, (window.innerHeight / 2)))
            });
          }
          setShowPieMenu(true);
          // Add to brush history when opening pie menu
          addToBrushHistory(currentBrush, brushColor, brushSize, brushOpacity);
        }
        return;
      }

      // Ctrl/Cmd combinations
      if (e.ctrlKey || e.metaKey) {
        if (key === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            if (redoRef.current) redoRef.current();
            toast.success('Redo');
          } else {
            if (undoRef.current) undoRef.current();
            toast.success('Undo');
          }
          return;
        }
        if (key === 's') {
          e.preventDefault();
          setShowSaveModal(true);
          return;
        }
        // Ctrl+E - Export or Merge Down (Shift for flatten)
        if (key === 'e') {
          e.preventDefault();
          if (e.shiftKey) {
            flattenLayers();
          } else {
            mergeLayerDown();
          }
          return;
        }
        // Ctrl+J - Duplicate layer
        if (key === 'j') {
          e.preventDefault();
          duplicateLayer();
          return;
        }
        // Ctrl+C - Copy layer
        if (key === 'c') {
          e.preventDefault();
          copyLayer();
          return;
        }
        // Ctrl+X - Cut layer
        if (key === 'x') {
          e.preventDefault();
          cutLayer();
          return;
        }
        // Ctrl+V - Paste as layer
        if (key === 'v') {
          e.preventDefault();
          pasteAsLayer();
          return;
        }
        return;
      }

      // Delete key - Clear layer or cancel transform
      if (key === 'delete' || key === 'backspace') {
        e.preventDefault();
        if (transformActive) {
          cancelTransform();
        } else {
          clearLayer();
        }
        return;
      }

      // Enter key - Apply transform
      if (key === 'enter' && transformActive) {
        e.preventDefault();
        applyTransform();
        return;
      }

      // Escape - Cancel transform or context menu
      if (key === 'escape') {
        if (transformActive) {
          cancelTransform();
        }
        if (contextMenu.show) {
          setContextMenu({ show: false, x: 0, y: 0, layerId: null });
        }
        return;
      }

      // Tool shortcuts
      if (KEYBOARD_SHORTCUTS[key]) {
        e.preventDefault();
        const shortcut = KEYBOARD_SHORTCUTS[key];

        // Special handling for U key - cycle through shape tools
        if (key === 'u') {
          const shapes = shapeToolsRef.current;
          shapeToolIndexRef.current = (shapeToolIndexRef.current + 1) % shapes.length;
          const nextShape = shapes[shapeToolIndexRef.current];
          setCurrentTool(nextShape);
          toast.success(`${nextShape.charAt(0).toUpperCase() + nextShape.slice(1)} Tool (U to cycle)`);
        } else if (key === 't') {
          // Special handling for T key - start/toggle transform
          e.preventDefault();
          if (transformActiveRef.current) {
            applyTransform();
          } else {
            startTransform();
          }
        } else {
          setCurrentTool(shortcut.tool);
          toast.success(`${shortcut.label} (${key.toUpperCase()})`);
        }
        return;
      }

      // Bracket keys for brush size
      if (key === '[') {
        e.preventDefault();
        setBrushSize(prev => Math.max(1, prev - 5));
        toast.success(`Brush size: ${Math.max(1, brushSize - 5)}`);
        return;
      }
      if (key === ']') {
        e.preventDefault();
        setBrushSize(prev => Math.min(200, prev + 5));
        toast.success(`Brush size: ${Math.min(200, brushSize + 5)}`);
        return;
      }

      // Number keys for opacity (1-0 = 10%-100%)
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        const opacity = key === '0' ? 1 : parseInt(key) / 10;
        setBrushOpacity(opacity);
        toast.success(`Opacity: ${Math.round(opacity * 100)}%`);
        return;
      }

      // X - Swap foreground/background colors
      if (key === 'x') {
        e.preventDefault();
        const temp = brushColor;
        setBrushColor(secondaryColor);
        setSecondaryColor(temp);
        toast.success('Colors swapped');
        return;
      }

      // D - Reset to default colors (black/white)
      if (key === 'd') {
        e.preventDefault();
        setBrushColor('#000000');
        setSecondaryColor('#ffffff');
        toast.success('Reset to default colors');
        return;
      }

      // ? or / - Show shortcuts panel
      if (key === '?' || key === '/') {
        e.preventDefault();
        setShowShortcutsPanel(prev => !prev);
        return;
      }

      // Alt key - Temporarily switch to eyedropper
      if (key === 'alt') {
        e.preventDefault();
        if (!isAltPressed) {
          previousToolRef.current = currentTool;
          setCurrentTool('eyedropper');
          setIsAltPressed(true);
          isAltPressedRef.current = true;
        }
        return;
      }

      // Escape - Close modals/panels
      if (key === 'escape') {
        setShowPieMenu(false);
        setShowShortcutsPanel(false);
        setShowBrushPicker(false);
        setSelection(null);
        return;
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();

      // Spacebar release - no longer auto-closes pie menu (it's now a toggle)
      if (key === ' ') {
        e.preventDefault();
        // Don't close — menu stays open until Space, Escape, or outside click
      }

      // Alt release - Return to previous tool
      if (key === 'alt') {
        e.preventDefault();
        if (isAltPressed) {
          setCurrentTool(previousToolRef.current);
          setIsAltPressed(false);
          isAltPressedRef.current = false;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [showPieMenu, brushColor, secondaryColor, brushSize, brushOpacity, currentTool, currentBrush, isAltPressed, addToBrushHistory]);

  // Load brush textures
  useEffect(() => {
    BRUSH_PRESETS.forEach(preset => {
      const img = new Image();
      img.onload = () => {
        brushTexturesRef.current[preset.id] = img;
        console.log(`Loaded texture: ${preset.texture}`);
      };
      img.onerror = () => {
        console.warn(`Failed to load brush texture: ${preset.texture}`);
        toast.error(`Failed to load brush: ${preset.name}`);
        // Create fallback circular brush
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
        gradient.addColorStop(0, 'rgba(0,0,0,1)');
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 128, 128);
        brushTexturesRef.current[preset.id] = canvas;
      };
      img.src = preset.texture;
    });
  }, []);

  // Composite Logic (Hoisted)
  const performComposite = useCallback(() => {
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx = mainCanvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw checkerboard for transparency
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Use ref for latest layers state
    const currentLayers = layersRef.current;
    const currentOffsets = layerOffsetsRef.current || {};

    // Composite all visible layers
    currentLayers.forEach(layer => {
      if (layer.visible && layerCanvasesRef.current[layer.id]) {
        const offset = currentOffsets[layer.id] || { x: 0, y: 0 };
        ctx.globalAlpha = layer.opacity;
        ctx.globalCompositeOperation = layer.blendMode === 'normal' ? 'source-over' : layer.blendMode;

        // First draw the layer (which may have cleared area if being transformed)
        // Fix: content is High-DPI, so we must specify logical size to prevent double-scaling
        ctx.drawImage(layerCanvasesRef.current[layer.id], offset.x, offset.y, canvasWidth, canvasHeight);

        // If this is the active layer and we are drawing a brush stroke, overlay the stroke canvas
        if (layer.id === activeLayerIdRef.current && currentStrokeCanvasRef.current && strokeStateRef.current.isActive && strokeStateRef.current.settings.tool !== 'eraser') {
          ctx.drawImage(currentStrokeCanvasRef.current, offset.x, offset.y, canvasWidth, canvasHeight);
        }

        // If this layer is being transformed, draw the transform preview on top
        if (transformActiveRef.current && layer.id === activeLayerIdRef.current && transformLayerRef.current) {
          const tData = transformDataRef.current;
          const contentCanvas = transformLayerRef.current;

          ctx.save();
          const centerX = tData.x + tData.width / 2;
          const centerY = tData.y + tData.height / 2;

          ctx.translate(centerX, centerY);
          ctx.rotate((tData.rotation * Math.PI) / 180);
          ctx.translate(-centerX, -centerY);

          // Draw the extracted content at the current transform position/size
          ctx.drawImage(
            contentCanvas,
            0, 0, contentCanvas.width, contentCanvas.height,  // source (entire content canvas)
            tData.x, tData.y, tData.width, tData.height       // destination (transform bounds)
          );
          ctx.restore();
        }
      }
    });

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }, [canvasWidth, canvasHeight]);

  // Keep a ref to performComposite so recording capture loop always uses latest
  const performCompositeRef = useRef(performComposite);
  useEffect(() => { performCompositeRef.current = performComposite; }, [performComposite]);

  const requestComposite = useCallback(() => {
    needsCompositeRef.current = true;
  }, []);

  // History management (Hoisted) — Uses IndexedDB to store ImageData on DISK
  // Only lightweight metadata stays in RAM. ImageData is loaded from disk on undo/redo.
  const historyStepCounterRef = useRef(0); // Global step counter for IndexedDB keys
  const historySaveCountRef = useRef(0);   // Counts saves for periodic pruning

  const saveToHistory = useCallback(() => {
    // Skip if history is disabled (canvas tainted)
    if (historyDisabled) return;

    try {
      // Only save the ACTIVE layer
      const activeId = activeLayerIdRef.current || drawingStateRef.current?.activeLayerId;
      if (!activeId || !layerCanvasesRef.current[activeId]) return;

      // Increment global step counter
      historyStepCounterRef.current++;
      const stepId = historyStepCounterRef.current;

      // Trim history to current position (removes redo states)
      historyRef.current = historyRef.current.slice(0, historyStepRef.current + 1);

      // Store lightweight metadata in RAM (no pixel data!)
      historyRef.current.push({
        stepId,
        activeLayerId: String(activeId),
        layers: JSON.parse(JSON.stringify(layers))
      });

      // Limit history entries
      if (historyRef.current.length > MAX_HISTORY) {
        historyRef.current = historyRef.current.slice(historyRef.current.length - MAX_HISTORY);
      }

      historyStepRef.current = historyRef.current.length - 1;
      // Defer state updates to avoid triggering re-renders during painting
      const step = historyStepRef.current;
      const len = historyRef.current.length;
      requestAnimationFrame(() => {
        setHistoryStep(step);
        setHistoryLength(len);
      });

      // DEFERRED: Heavy work (getImageData + IndexedDB write) runs on next idle tick
      // This prevents blocking the pointer event handler (~50ms saved per stroke)
      const canvasToSave = layerCanvasesRef.current[activeId];
      const layersCopy = JSON.parse(JSON.stringify(layers));
      const saveCount = ++historySaveCountRef.current;

      setTimeout(() => {
        try {
          if (!canvasToSave) return;
          const ctx = canvasToSave.getContext('2d');
          const imageData = ctx.getImageData(0, 0, canvasToSave.width, canvasToSave.height);
          saveHistoryEntry(stepId, activeId, imageData, layersCopy).then(() => {
            // Only prune every 5th save (cursor scan is expensive)
            if (saveCount % 5 === 0) {
              const validSteps = historyRef.current.map(h => h.stepId);
              pruneHistory(validSteps);
            }
          });
        } catch (e) {
          console.error('Deferred history save failed:', e);
        }
      }, 0);
    } catch (error) {
      console.error('History Save Error (Canvas Taint?):', error);
      if (error.name === 'SecurityError' || error.message.includes('Tainted') || error.message.includes('cross-origin')) {
        setHistoryDisabled(true);
        toast.error('Canvas tainted by external image. Undo/Redo disabled for this session.', {
          duration: 5000,
          description: 'You can still save your project.'
        });
      }
    }
  }, [layers, historyDisabled]);

  const undo = useCallback(async () => {
    if (historyDisabled || historyStepRef.current <= 0) return;

    historyStepRef.current--;
    const meta = historyRef.current[historyStepRef.current];
    if (!meta) return;

    setLayers(meta.layers);
    setHistoryStep(historyStepRef.current);

    // Load ImageData from IndexedDB (on disk, ~10-20ms)
    const entry = await loadHistoryEntry(meta.stepId);
    if (entry && entry.imageData) {
      const layerId = entry.layerId;
      if (!layerCanvasesRef.current[layerId]) {
        const newCanvas = document.createElement('canvas');
        const dpr = window.devicePixelRatio || 1;
        newCanvas.width = canvasWidth * dpr;
        newCanvas.height = canvasHeight * dpr;
        layerCanvasesRef.current[layerId] = newCanvas;
      }
      const ctx = layerCanvasesRef.current[layerId].getContext('2d');
      ctx.putImageData(entry.imageData, 0, 0);
    }
    requestComposite();
  }, [canvasWidth, canvasHeight, requestComposite]);

  const redo = useCallback(async () => {
    if (historyDisabled || historyStepRef.current >= historyRef.current.length - 1) return;

    historyStepRef.current++;
    const meta = historyRef.current[historyStepRef.current];
    if (!meta) return;

    setLayers(meta.layers);
    setHistoryStep(historyStepRef.current);

    // Load ImageData from IndexedDB (on disk, ~10-20ms)
    const entry = await loadHistoryEntry(meta.stepId);
    if (entry && entry.imageData) {
      const layerId = entry.layerId;
      if (!layerCanvasesRef.current[layerId]) {
        const newCanvas = document.createElement('canvas');
        const dpr = window.devicePixelRatio || 1;
        newCanvas.width = canvasWidth * dpr;
        newCanvas.height = canvasHeight * dpr;
        layerCanvasesRef.current[layerId] = newCanvas;
      }
      const ctx = layerCanvasesRef.current[layerId].getContext('2d');
      ctx.putImageData(entry.imageData, 0, 0);
    }
    requestComposite();
  }, [canvasWidth, canvasHeight, requestComposite]);

  // Cleanup IndexedDB on unmount
  useEffect(() => {
    // Clear stale data on mount
    clearHistory();
    clearRecordingFrames();
    return () => {
      clearHistory();
      clearRecordingFrames();
    };
  }, []);

  // Update refs for keyboard shortcuts
  useEffect(() => {
    undoRef.current = undo;
    redoRef.current = redo;
  }, [undo, redo]);

  // Update saveToHistory ref
  useEffect(() => {
    saveToHistoryRef.current = saveToHistory;
  }, [saveToHistory]);

  // Initialize canvas
  useEffect(() => {
    initializeCanvas();
    fetchCredits();
    // Loop handled by the dedicated renderLoop effect below
  }, []);

  // Initialize canvas with optional image — draw with cover-fit and store original URL
  const initialImageLoadedRef = useRef(false);
  useEffect(() => {
    if (initialImage && mainCanvasRef.current && canvasWidth > 0 && canvasHeight > 0) {
      // Store the original full-res URL for Transform to use later
      fullResImageRef.current = initialImage;

      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        setTimeout(() => {
          const bgCanvas = layerCanvasesRef.current[1]; // background layer
          if (bgCanvas) {
            const bgCtx = bgCanvas.getContext('2d');
            bgCtx.save();
            bgCtx.setTransform(1, 0, 0, 1, 0, 0);
            bgCtx.clearRect(0, 0, bgCanvas.width, bgCanvas.height);

            // Cover-fit: fill the canvas, crop excess (preserves aspect ratio)
            const cw = bgCanvas.width;
            const ch = bgCanvas.height;
            const iw = img.naturalWidth;
            const ih = img.naturalHeight;
            const scale = Math.max(cw / iw, ch / ih);
            const drawW = iw * scale;
            const drawH = ih * scale;
            const drawX = (cw - drawW) / 2;
            const drawY = (ch - drawH) / 2;

            bgCtx.drawImage(img, drawX, drawY, drawW, drawH);
            bgCtx.restore();
          } else {
            const ctx = mainCanvasRef.current?.getContext('2d');
            if (ctx) ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          }
          needsCompositeRef.current = true;
          saveToHistory();
          toast.success("Panel content loaded");
        }, 100);
      };
      img.onerror = (e) => {
        console.error("[ComicaProV2] Failed to load initialImage:", e);
        toast.error("Failed to load panel image");
      };
      img.src = initialImage;
    }
  }, [initialImage, canvasWidth, canvasHeight]);

  const initializeCanvas = useCallback(() => {
    const mainCanvas = mainCanvasRef.current;
    const compositeCanvas = compositeCanvasRef.current;

    if (!mainCanvas || !compositeCanvas) return;

    const dpr = window.devicePixelRatio || 1;

    // Set physical dimensions (scaled)
    mainCanvas.width = canvasWidth * dpr;
    mainCanvas.height = canvasHeight * dpr;
    compositeCanvas.width = canvasWidth * dpr;
    compositeCanvas.height = canvasHeight * dpr;

    // Scale contexts
    const mainCtx = mainCanvas.getContext('2d');
    mainCtx.scale(dpr, dpr);

    const compositeCtx = compositeCanvas.getContext('2d');
    compositeCtx.scale(dpr, dpr);

    // Initialize stroke canvas
    if (!currentStrokeCanvasRef.current) {
      currentStrokeCanvasRef.current = document.createElement('canvas');
    }
    const strokeCanvas = currentStrokeCanvasRef.current;
    strokeCanvas.width = canvasWidth * dpr;
    strokeCanvas.height = canvasHeight * dpr;
    const strokeCtx = strokeCanvas.getContext('2d');
    strokeCtx.scale(dpr, dpr);

    // CRITICAL: Restore layers from Initial Data (Unfreeze/Thaw)
    if (initialLayers && initialLayers.length > 0) {
      // Clear existing refs safely
      layerCanvasesRef.current = {};

      const loadedLayers = [];

      initialLayers.forEach(layer => {
        const layerCanvas = document.createElement('canvas');
        layerCanvas.width = canvasWidth * dpr;
        layerCanvas.height = canvasHeight * dpr;
        const layerCtx = layerCanvas.getContext('2d', { willReadFrequently: true });
        layerCtx.scale(dpr, dpr);

        if (layer.imageData) {
          // Deserialize from Base64 string or Cloudinary URL
          const img = new Image();
          img.crossOrigin = 'anonymous'; // CRITICAL: Prevent canvas taint from external URLs
          img.onload = () => {
            layerCtx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
            needsCompositeRef.current = true;
          };
          img.src = layer.imageData;
        } else if (layer.id === 1) {
          // Fallback background fill if no imageData
          layerCtx.fillStyle = '#ffffff';
          layerCtx.fillRect(0, 0, canvasWidth, canvasHeight);
        }

        layerCanvasesRef.current[layer.id] = layerCanvas;

        // Push safe state object
        loadedLayers.push({
          id: layer.id,
          name: layer.name,
          visible: layer.visible !== false,
          opacity: layer.opacity ?? 1,
          locked: layer.locked || false,
          blendMode: layer.blendMode || 'normal',
          hasMask: layer.hasMask || false,
          maskDisabled: layer.maskDisabled || false,
          type: layer.type || 'raster',
          textConfig: layer.textConfig || null,
          bubbleConfig: layer.bubbleConfig || null
        });
      });

      setLayers(loadedLayers);
      setActiveLayerId(loadedLayers[loadedLayers.length > 1 ? 1 : 0].id); // Prefer first drawing layer

    } else {
      // Standard initialization (no restored data)
      const bgCanvas = document.createElement('canvas');
      bgCanvas.width = canvasWidth * dpr;
      bgCanvas.height = canvasHeight * dpr;
      const bgCtx = bgCanvas.getContext('2d', { willReadFrequently: true });
      bgCtx.scale(dpr, dpr);

      bgCtx.fillStyle = '#ffffff';
      bgCtx.fillRect(0, 0, canvasWidth, canvasHeight);

      layerCanvasesRef.current[1] = bgCanvas;
    }

    needsCompositeRef.current = true;
    saveToHistory();
  }, [canvasWidth, canvasHeight, initialLayers]);

  // Keep a ref to layers for use in animation frame callback
  const layersRef = useRef(layers);
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  // Keep refs for transform state to use in composite loop
  const transformActiveRef = useRef(transformActive);
  const transformDataRef = useRef(transformData);
  const activeLayerIdRef = useRef(activeLayerId);

  useEffect(() => {
    transformActiveRef.current = transformActive;
  }, [transformActive]);

  useEffect(() => {
    transformDataRef.current = transformData;
    // Trigger composite for live preview when transform is active
    if (transformActive) {
      needsCompositeRef.current = true;
    }
  }, [transformData, transformActive]);

  useEffect(() => {
    activeLayerIdRef.current = activeLayerId;
  }, [activeLayerId]);



  // Get mouse/pointer position relative to canvas with pressure support
  // Uses offsetX/Y which correctly handles canvas borders and avoids layout reflow
  const getMousePos = useCallback((e) => {
    const canvas = mainCanvasRef.current;

    // Get pen pressure from PointerEvent (0.0 - 1.0)
    // Falls back to 1.0 for mouse events (no pressure support)
    const pressure = e.pressure !== undefined ? e.pressure : 1.0;
    // If pressure is 0 (pen hovering but not touching), treat as 1.0 for mouse compatibility
    const normalizedPressure = pressure === 0 ? 1.0 : pressure;

    // Use offsetX/Y when available (native events) - more accurate and handles borders
    // offsetX/Y are relative to the padding edge of the target, ignoring borders
    if (e.nativeEvent && e.nativeEvent.offsetX !== undefined) {
      // Return Logical Coordinates (CSS Pixels) matches ctx.scale(dpr, dpr)
      return {
        x: e.nativeEvent.offsetX,
        y: e.nativeEvent.offsetY,
        pressure: normalizedPressure
      };
    }

    // Fallback for synthetic events or when offsetX is unavailable
    const rect = canvas.getBoundingClientRect();
    // Return Logical Coordinates
    return {
      x: (e.clientX - rect.left),
      y: (e.clientY - rect.top),
      pressure: normalizedPressure
    };
  }, []);

  // Stamp brush texture at position
  // OPTIMIZED: Uses cached temp canvas to avoid memory leak from creating thousands of canvas elements per stroke
  const stampBrush = useCallback((ctx, x, y, size, color, brush, isEraser = false, opacity = 1.0, hardness = 0.9, scatter = 0) => {
    if (!brush) return; // Safety check

    // ─── Scatter: randomize dab position ───
    if (scatter > 0) {
      const scatterRadius = size * scatter;
      x += (Math.random() - 0.5) * 2 * scatterRadius;
      y += (Math.random() - 0.5) * 2 * scatterRadius;
    }

    // Check if texture is loaded
    const texture = brushTexturesRef.current[brush.id];
    const isTextureLoaded = !!texture;

    // Apply size jitter
    const jitteredSize = Math.ceil(size * (1 + (Math.random() - 0.5) * 2 * brush.sizeJitter));
    const halfSize = jitteredSize / 2;
    const radius = halfSize;

    // Apply angle jitter
    const angle = (Math.random() - 0.5) * 2 * Math.PI * brush.angleJitter;

    // Get or create cached temp canvas and context
    const minSize = Math.max(jitteredSize, 64);
    if (!stampTempCanvasRef.current) {
      stampTempCanvasRef.current = document.createElement('canvas');
      stampTempCanvasRef.current.width = Math.max(jitteredSize, 256);
      stampTempCanvasRef.current.height = Math.max(jitteredSize, 256);
      stampTempCtxRef.current = stampTempCanvasRef.current.getContext('2d');
    } else if (stampTempCanvasRef.current.width < jitteredSize || stampTempCanvasRef.current.height < jitteredSize) {
      stampTempCanvasRef.current.width = Math.max(stampTempCanvasRef.current.width, jitteredSize);
      stampTempCanvasRef.current.height = Math.max(stampTempCanvasRef.current.height, jitteredSize);
      stampTempCtxRef.current = stampTempCanvasRef.current.getContext('2d');
    } else if (stampTempCanvasRef.current.width > minSize * 4 && jitteredSize < stampTempCanvasRef.current.width / 4) {
      stampTempCanvasRef.current.width = Math.max(jitteredSize * 2, 256);
      stampTempCanvasRef.current.height = Math.max(jitteredSize * 2, 256);
      stampTempCtxRef.current = stampTempCanvasRef.current.getContext('2d');
    }

    const tempCanvas = stampTempCanvasRef.current;
    const tempCtx = stampTempCtxRef.current;
    tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);

    ctx.save();

    if (isEraser) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = opacity;

      if (isTextureLoaded && texture) {
        tempCtx.globalCompositeOperation = 'source-over';
        tempCtx.drawImage(texture, 0, 0, jitteredSize, jitteredSize);
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.drawImage(tempCanvas, 0, 0, jitteredSize, jitteredSize, -halfSize, -halfSize, jitteredSize, jitteredSize);
      } else {
        // ─── Hardness: radial gradient for soft eraser ───
        const coreRadius = radius * Math.max(0.01, hardness);
        const grad = ctx.createRadialGradient(x, y, coreRadius * 0.5, x, y, radius);
        grad.addColorStop(0, 'rgba(0,0,0,1)');
        grad.addColorStop(hardness, 'rgba(0,0,0,1)');
        grad.addColorStop(1.0, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    } else {
      ctx.globalAlpha = opacity;
      ctx.globalCompositeOperation = 'source-over';

      if (isTextureLoaded && texture) {
        tempCtx.globalCompositeOperation = 'source-over';
        tempCtx.drawImage(texture, 0, 0, jitteredSize, jitteredSize);
        tempCtx.globalCompositeOperation = 'source-in';
        tempCtx.fillStyle = color;
        tempCtx.fillRect(0, 0, jitteredSize, jitteredSize);
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.drawImage(tempCanvas, 0, 0, jitteredSize, jitteredSize, -halfSize, -halfSize, jitteredSize, jitteredSize);
      } else {
        // ─── Hardness: radial gradient circle ───
        // hardness=1.0 → crisp hard edge | hardness=0.0 → fully soft airbrush
        const coreStop = Math.max(0.01, Math.min(0.99, hardness));
        const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
        grad.addColorStop(0, color);
        grad.addColorStop(coreStop, color);
        grad.addColorStop(1.0, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }, []);

  // CORE ENGINE: Distance-Based Stamping (The "Correct" Protocol)
  const drawStrokeSegment = useCallback((ctx, x1, y1, x2, y2, startSize, endSize, color, brush, isEraser = false, avgOpacity = 1.0, hardness = 0.9, scatter = 0, flow = 1.0) => {
    if (!brush) return;

    const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    const avgSize = (startSize + endSize) / 2;

    const brushSpacingRatio = brush.spacing || 0.1;
    const spacing = Math.max(0.5, avgSize * brushSpacingRatio);

    const totalDist = dist + strokeStateRef.current.remainder;
    const count = Math.floor(totalDist / spacing);
    const newRemainder = totalDist % spacing;
    const oldRemainder = strokeStateRef.current.remainder;
    strokeStateRef.current.remainder = newRemainder;

    // ─── Flow: per-dab opacity = base opacity × flow ───
    const dabOpacity = avgOpacity * flow;

    for (let i = 0; i < count; i++) {
      const distFromP1 = (i + 1) * spacing - oldRemainder;
      const t = Math.max(0, Math.min(1, distFromP1 / dist));
      const x = x1 + (x2 - x1) * t;
      const y = y1 + (y2 - y1) * t;
      const currentSize = startSize + (endSize - startSize) * t;

      stampBrush(ctx, x, y, currentSize, color, brush, isEraser, dabOpacity, hardness, scatter);
    }
  }, [stampBrush]);

  // Calculate pressure-adjusted brush parameters
  const getPressureAdjustedParams = useCallback((state, pressure) => {
    if (!state.pressureSensitivity) {
      return { size: state.brushSize, opacity: state.brushOpacity };
    }

    let size = state.brushSize;
    let opacity = state.brushOpacity;
    const isTextureBrush = state.currentBrush && state.currentBrush.texture;

    if (state.pressureAffectsSize) {
      // Pressure affects size
      // For texture brushes, prevent them from vanishing into sub-pixel oblivion
      // Padding in texture means "Size 1" might be invisible. Floor it to 3px.
      const minSize = isTextureBrush ? 3.0 : 0.5;
      size = Math.max(minSize, state.brushSize * Math.max(0.1, pressure));
    }

    if (state.pressureAffectsOpacity) {
      // Pressure affects opacity
      // Prevent opacity from dropping below 0.2 for texture brushes to avoid "faint dots" look
      const minOpacity = isTextureBrush ? 0.2 : 0.1;
      opacity = state.brushOpacity * Math.max(minOpacity, pressure);
    }

    return { size, opacity };
  }, []);

  // COMIC TEXT ENGINE: Render Text to Layer
  const renderTextLayer = useCallback((layerId, textConfig) => {
    const canvas = layerCanvasesRef.current[layerId];
    if (!canvas || !textConfig) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Clear canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Setup Font
    const fontSize = textConfig.fontSize * dpr; // Scale font for High-DPI
    const fontFamily = textConfig.fontFamily || 'Arial';
    const fontWeight = textConfig.bold ? 'bold' : 'normal';
    const fontStyle = textConfig.italic ? 'italic' : 'normal';
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

    ctx.textBaseline = 'top';
    ctx.textAlign = 'left'; // We handle alignment manually via X position if needed

    // Setup Shadow
    if (textConfig.shadowBlur > 0 || textConfig.shadowOffsetX !== 0 || textConfig.shadowOffsetY !== 0) {
      ctx.shadowColor = textConfig.shadowColor || '#000000';
      ctx.shadowBlur = textConfig.shadowBlur * dpr;
      ctx.shadowOffsetX = textConfig.shadowOffsetX * dpr;
      ctx.shadowOffsetY = textConfig.shadowOffsetY * dpr;
    } else {
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    // Coordinates (already Logical in config, scale to Physical)
    const x = textConfig.x * dpr;
    const y = textConfig.y * dpr;

    // Line parsing and Text Wrapping Logic
    const lineHeight = fontSize * (textConfig.lineHeight || 1.2);
    const explicitLines = textConfig.content.split('\n');
    const linesToDraw = [];

    const maxWidth = textConfig.maxWidth ? (textConfig.maxWidth * dpr) : null;

    explicitLines.forEach(explicitLine => {
      if (!maxWidth) {
        linesToDraw.push(explicitLine);
        return;
      }

      let words = explicitLine.split(' ');
      let currentLine = '';

      for (let n = 0; n < words.length; n++) {
        let testLine = currentLine + words[n] + ' ';
        let metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && n > 0) {
          linesToDraw.push(currentLine.trim());
          currentLine = words[n] + ' ';
        } else {
          currentLine = testLine;
        }
      }
      linesToDraw.push(currentLine.trim());
    });

    linesToDraw.forEach((line, index) => {
      const lineY = y + (index * lineHeight);

      // 1. Stroke (Outline) - Draw first to be behind fill
      if (textConfig.strokeWidth > 0) {
        ctx.strokeStyle = textConfig.strokeColor || '#000000';
        ctx.lineWidth = textConfig.strokeWidth * dpr;
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
        ctx.strokeText(line, x, lineY);
      }

      // 2. Fill
      ctx.fillStyle = textConfig.color || '#000000';
      ctx.fillText(line, x, lineY);
    });

    // Reset Shadow for safety
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

  }, []);

  // Process stroke buffer (Producer-Consumer Pattern)
  const processStrokeBuffer = useCallback(() => {
    try {
      const state = strokeStateRef.current;
      if (state.buffer.length === 0) return;

      // console.log("Processing buffer:", state.buffer.length);

      const layerCanvas = layerCanvasesRef.current[state.layerId];
      if (!layerCanvas) return;

      // Determine target context: Stroke Canvas for Brush, Layer directly for Eraser
      let targetCtx;
      const strokeCanvas = currentStrokeCanvasRef.current;
      const isEraser = state.settings.tool === 'eraser';

      // SAFETY: Ensure we have a valid target to draw to
      if (!strokeCanvas && (!layerCanvas || !isEraser)) {
        // If no stroke canvas AND (no layer canvas OR we're not erasing directly), we can't do anything.
        // This happens during initialization or reload.
        return;
      }

      if (!isEraser && strokeCanvas) {
        targetCtx = strokeCanvas.getContext('2d');
      } else if (layerCanvas) {
        targetCtx = layerCanvas.getContext('2d');
        // FIX: If drawing directly to local layer (Eraser), we must apply inverse offset
        const currentOffset = layerOffsetsRef.current?.[state.layerId] || { x: 0, y: 0 };
        targetCtx.save();
        targetCtx.translate(-currentOffset.x, -currentOffset.y);
      } else {
        // Should be caught by the first check, but double safety
        return;
      }

      const settings = state.settings;

      // Process all buffered points sequentially
      while (state.buffer.length > 0) {
        const point = state.buffer.shift();

        // --- DYNAMICS ENGINE ---
        // 1. Calculate Velocity (Speed = Distance / Time)
        const dt = point.time - state.lastTime;
        const dist = Math.sqrt((point.x - state.lastX) ** 2 + (point.y - state.lastY) ** 2);

        let velocity = 0;
        if (dt > 0) {
          velocity = dist / dt;
        } else if (dist > 0) {
          // Instant jump (dt=0 but moved)? Use last velocity or assumed high speed
          velocity = state.lastVelocity || 0; // Keep momentum
        } else {
          // Stationary
          velocity = 0;
        }

        // SMOOTHING: Blend with last velocity to remove jitter (Exponential Moving Average)
        // Factor 0.2 means 20% new, 80% old. High smoothing = less beading, slower response.
        const smoothFactor = 0.2;
        velocity = (state.lastVelocity * (1 - smoothFactor)) + (velocity * smoothFactor);
        state.lastVelocity = velocity; // Update state

        // 2. Map Velocity to Pressure Factor (Faster = Thinner)
        // Max speed threshold: ~3px/ms seems reasonable for "fast"
        const maxSpeed = 3.0;
        // Clamp velocity to range [0, 1]
        let speedFactor = Math.min(velocity / maxSpeed, 1.0);

        // Reduce the thinning effect (User said "wired" -> likely too thin)
        // Instead of dropping to 0.4, let's drop to 0.6 minimum
        const minVelocityPressure = 0.6;
        const velocityPressure = 1.0 - (speedFactor * (1.0 - minVelocityPressure));

        // 3. Blend Physical Pressure with Velocity Pressure
        // Mouse Users: Physical is always 1.0, so Result = Velocity Pressure
        // Tablet Users: Both apply. Result = Physical * Velocity
        const finalPressure = point.pressure * velocityPressure;

        // Calculate dynamics based on BLENDED pressure
        const { size, opacity } = getPressureAdjustedParams({
          brushSize: settings.size,
          brushOpacity: settings.opacity,
          currentBrush: settings.brush,
          pressureSensitivity: true,
          pressureAffectsSize: true,
          pressureAffectsOpacity: true
        }, finalPressure);

        // --- RENDERING ---
        if (settings.tool === 'brush' || settings.tool === 'eraser') {
          const midX = (state.lastX + point.x) / 2;
          const midY = (state.lastY + point.y) / 2;
          const isCurrentEraser = settings.tool === 'eraser';
          // ─── Phase 1: read advanced brush params from settings ───
          const brushHardness = settings.brushHardness ?? 0.9;
          const brushScatter  = settings.brushScatter  ?? 0;
          const brushFlow     = settings.brushFlow     ?? 1.0;

          if (state.lastMidX === undefined) {
            // First segment: P0 → Mid (Linear)
            drawStrokeSegment(targetCtx, state.lastX, state.lastY, midX, midY, state.lastSize, size, settings.color, settings.brush, isCurrentEraser, state.lastOpacity, brushHardness, brushScatter, brushFlow);
          } else {
            // Quad Curve: Mid -> Mid
            // Simplest "Correct" way:
            // Flatten Quad Curve into N tiny linear segments.
            // Feed segments into accumulator.

            // How many segments?
            const dist1 = Math.sqrt((state.lastX - state.lastMidX) ** 2 + (state.lastY - state.lastMidY) ** 2);
            const dist2 = Math.sqrt((midX - state.lastX) ** 2 + (midY - state.lastY) ** 2);
            // Resolution: e.g. 5px segments
            const segments = Math.max(1, Math.ceil((dist1 + dist2) / 5));

            for (let i = 1; i <= segments; i++) {
              const t = i / segments;
              const tPrev = (i - 1) / segments;

              // Calc Points
              const P0 = { x: state.lastMidX, y: state.lastMidY };
              const P1 = { x: state.lastX, y: state.lastY };
              const P2 = { x: midX, y: midY };

              const getQ = (t) => ({
                x: (1 - t) ** 2 * P0.x + 2 * (1 - t) * t * P1.x + t ** 2 * P2.x,
                y: (1 - t) ** 2 * P0.y + 2 * (1 - t) * t * P1.y + t ** 2 * P2.y
              });

              const pStart = getQ(tPrev);
              const pEnd = getQ(t);

              // Interp Size
              const sStart = state.lastSize + (size - state.lastSize) * tPrev; // Rough linear interp across curve
              const sEnd = state.lastSize + (size - state.lastSize) * t;

              // Interp Opacity for spacing calculation
              const oStart = state.lastOpacity + (opacity - state.lastOpacity) * tPrev;
              const oEnd = state.lastOpacity + (opacity - state.lastOpacity) * t;
              const avgOp = (oStart + oEnd) / 2;

              drawStrokeSegment(targetCtx, pStart.x, pStart.y, pEnd.x, pEnd.y, sStart, sEnd, settings.color, settings.brush, isCurrentEraser, avgOp, brushHardness, brushScatter, brushFlow);
            }
          }
          state.lastMidX = midX;
          state.lastMidY = midY;
        }

        // Update state for next point
        state.lastX = point.x;
        state.lastY = point.y;
        state.lastTime = point.time;
        state.lastSize = size;       // Store calculated size
        state.lastOpacity = opacity; // Store calculated opacity
        state.points.push(point);

      } // End While Loop

      // Restore context if we translated it
      if (isEraser && targetCtx) {
        targetCtx.restore();
      }

      needsCompositeRef.current = true;
    } catch (err) {
      console.error("Error in processStrokeBuffer:", err);
    } // End Try-Catch
  }, [getPressureAdjustedParams, drawStrokeSegment]);

  // Keep ref updated
  useEffect(() => {
    processStrokeRef.current = processStrokeBuffer;
  }, [processStrokeBuffer]);

  // Animation Loop for Stroke Engine
  useEffect(() => {
    let animationFrameId;

    const renderLoop = () => {
      if (processStrokeRef.current) {
        processStrokeRef.current();
      }

      if (needsCompositeRef.current) {
        performComposite();
        needsCompositeRef.current = false;
      }

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    renderLoop();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [performComposite]);

  // Helper to get logic coordinates from screen event
  const getCanvasPoint = useCallback((e) => {
    // 1. Try container ref, fallback to currentTarget (robustness)
    const container = containerRef.current || e.currentTarget;
    if (!container || !container.getBoundingClientRect) {
      console.warn("getCanvasPoint: No container found");
      return { x: 0, y: 0, pressure: 0.5 };
    }

    const rect = container.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;

    // Apply Pan, Zoom, and Rotate View inverse transform.
    // This is a Photoshop-style view rotation only; artwork pixels stay unchanged.
    const curPan = panRef.current;
    const curZoom = zoomRef.current;
    const rotationRad = -(viewRotationRef.current * Math.PI) / 180;
    const screenX = clientX - rect.left - curPan.x;
    const screenY = clientY - rect.top - curPan.y;
    const cos = Math.cos(rotationRad);
    const sin = Math.sin(rotationRad);
    const x = (screenX * cos - screenY * sin) / curZoom;
    const y = (screenX * sin + screenY * cos) / curZoom;

    let pressure = 1.0; // Default: mouse is always full pressure

    if (e.pointerType === 'pen') {
      // ✏️ Apple Pencil / Wacom stylus — use real hardware pressure
      pressure = e.pressure > 0 ? e.pressure : (e.nativeEvent?.pressure > 0 ? e.nativeEvent.pressure : 1.0);
    } else if (e.pointerType === 'touch') {
      // 👆 Touch (finger) — simulate pressure via velocity
      // Fast swipe = low pressure (thin), slow deliberate stroke = high pressure (thick)
      const tv = touchVelocityRef.current;
      const now = performance.now();
      const dt = Math.max(1, now - tv.lastTime); // ms since last event
      const dx = clientX - tv.lastX;
      const dy = clientY - tv.lastY;
      const speed = Math.sqrt(dx * dx + dy * dy) / dt; // px/ms

      // Map speed → pressure: speed 0 = 1.0, speed 2+ px/ms = 0.2
      // Smooth with exponential moving average to avoid jitter
      const rawPressure = Math.max(0.2, Math.min(1.0, 1.0 - speed * 0.4));
      const alpha = 0.3; // smoothing factor (0=no smooth, 1=instant)
      tv.smoothedPressure = tv.smoothedPressure * (1 - alpha) + rawPressure * alpha;
      pressure = tv.smoothedPressure;

      // Update velocity tracking
      tv.lastX = clientX;
      tv.lastY = clientY;
      tv.lastTime = now;
    }
    // Mouse stays at 1.0 — no pressure data available

    return { x, y, pressure };
  }, []); // No dependencies — reads from refs (touchVelocityRef is a ref, not state)

  // Flood fill implementation
  const getPixelColor = (pixels, x, y, width) => {
    const index = (y * width + x) * 4;
    return [pixels[index], pixels[index + 1], pixels[index + 2], pixels[index + 3]];
  };

  const setPixelColor = (pixels, x, y, width, color) => {
    const index = (y * width + x) * 4;
    pixels[index] = color[0];
    pixels[index + 1] = color[1];
    pixels[index + 2] = color[2];
    pixels[index + 3] = 255;
  };

  const colorsMatch = (a, b, tolerance = 0) => {
    return Math.abs(a[0] - b[0]) <= tolerance &&
      Math.abs(a[1] - b[1]) <= tolerance &&
      Math.abs(a[2] - b[2]) <= tolerance;
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [0, 0, 0];
  };

  const floodFill = (ctx, x, y, fillColorHex) => {
    const bufW = ctx.canvas.width;
    const bufH = ctx.canvas.height;
    const dpr  = bufW / canvasWidth;

    const bx = Math.round(x * dpr);
    const by = Math.round(y * dpr);

    if (bx < 0 || bx >= bufW || by < 0 || by >= bufH) return;

    const imageData = ctx.getImageData(0, 0, bufW, bufH);
    const pixels    = imageData.data;
    const targetColor = getPixelColor(pixels, bx, by, bufW);
    const fillRgb     = hexToRgb(fillColorHex);

    // Only skip if the target pixel is ALREADY the fill color AND opaque.
    // Transparent pixels (alpha < 200) are never considered "already filled"
    // even if their RGB matches the fill color.
    if (colorsMatch(targetColor, fillRgb) && targetColor[3] > 200) return;

    // Use scanline flood fill for better performance
    const filled = new Uint8Array(bufW * bufH); // 0 = not filled, 1 = filled
    // Determine if we're filling a transparent area or a colored area
    const isFillingTransparent = targetColor[3] < 10;
    const tolerance = 40;

    // Smart match: determines if a pixel should be filled
    const matchesTarget = (px, py) => {
      const idx = (py * bufW + px) * 4;
      const r = pixels[idx], g = pixels[idx + 1], b = pixels[idx + 2], a = pixels[idx + 3];

      if (isFillingTransparent) {
        // Filling empty/transparent area: fill any pixel that's not a solid stroke
        // Stop at opaque pixels (alpha > 180 = solid stroke), fill everything else
        return a < 180;
      } else {
        // Filling a colored area: standard color match with tolerance
        return Math.abs(r - targetColor[0]) <= tolerance &&
               Math.abs(g - targetColor[1]) <= tolerance &&
               Math.abs(b - targetColor[2]) <= tolerance &&
               Math.abs(a - targetColor[3]) <= tolerance;
      }
    };

    const stack = [[bx, by]];
    let safety = 0;
    const maxIterations = bufW * bufH;

    while (stack.length > 0) {
      if (safety++ > maxIterations) break;
      const [px, py] = stack.pop();

      if (px < 0 || px >= bufW || py < 0 || py >= bufH) continue;
      if (filled[py * bufW + px]) continue;
      if (!matchesTarget(px, py)) continue;

      // Scanline: find left and right extent
      let left = px, right = px;
      while (left > 0 && !filled[py * bufW + (left - 1)] && matchesTarget(left - 1, py)) left--;
      while (right < bufW - 1 && !filled[py * bufW + (right + 1)] && matchesTarget(right + 1, py)) right++;

      // Fill the scanline
      for (let i = left; i <= right; i++) {
        filled[py * bufW + i] = 1;
        setPixelColor(pixels, i, py, bufW, fillRgb);
      }

      // Push rows above and below
      for (let i = left; i <= right; i++) {
        if (py > 0) stack.push([i, py - 1]);
        if (py < bufH - 1) stack.push([i, py + 1]);
      }
    }

    // ─── Dilation pass: expand fill OVER the stroke edges ───
    // Each pass grows the filled region by 1px, overwriting stroke edge pixels.
    // The fill covers the anti-aliased fringe from on top, eliminating white outlines.
    const DILATE_PASSES = 4; // 4px of expansion covers anti-aliased edges
    for (let pass = 0; pass < DILATE_PASSES; pass++) {
      const toFill = [];
      for (let py = 1; py < bufH - 1; py++) {
        for (let px = 1; px < bufW - 1; px++) {
          if (filled[py * bufW + px]) continue;

          // Must be adjacent to a filled pixel (4-connected)
          if (!(filled[py * bufW + (px - 1)] ||
                filled[py * bufW + (px + 1)] ||
                filled[(py - 1) * bufW + px] ||
                filled[(py + 1) * bufW + px])) continue;

          toFill.push(py * bufW + px);
        }
      }
      // Apply the expansion
      for (const offset of toFill) {
        const idx = offset * 4;
        pixels[idx]     = fillRgb[0];
        pixels[idx + 1] = fillRgb[1];
        pixels[idx + 2] = fillRgb[2];
        pixels[idx + 3] = 255;
        filled[offset] = 1;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  };

  // Touch gestures + context menu prevention — Non-passive listeners
  const wheelSyncTimerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Prevent context menu on canvas (allows pen barrel button for panning)
    const preventContext = (e) => e.preventDefault();
    container.addEventListener('contextmenu', preventContext);

    // Two-finger touch gestures (pinch-to-zoom + two-finger pan)
    let lastTouchDist = 0;
    let lastTouchCenter = null;
    let isTwoFinger = false;

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        isTwoFinger = true;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastTouchDist = Math.sqrt(dx * dx + dy * dy);
        lastTouchCenter = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2
        };
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && isTwoFinger) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const center = {
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2
        };

        // Pinch zoom — direct DOM update
        if (lastTouchDist > 0) {
          const scale = dist / lastTouchDist;
          const newZoom = Math.min(Math.max(0.1, zoomRef.current * scale), 10);
          updateZoomDirect(newZoom);
        }

        // Two-finger pan — direct DOM update
        if (lastTouchCenter) {
          const panDx = center.x - lastTouchCenter.x;
          const panDy = center.y - lastTouchCenter.y;
          const curPan = panRef.current;
          updatePanDirect({ x: curPan.x + panDx, y: curPan.y + panDy });
        }

        lastTouchDist = dist;
        lastTouchCenter = center;
      }
    };

    const handleTouchEnd = () => {
      if (isTwoFinger) {
        syncViewState(); // Sync refs → state when touch gesture ends
      }
      isTwoFinger = false;
      lastTouchDist = 0;
      lastTouchCenter = null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('contextmenu', preventContext);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [updatePanDirect, updateZoomDirect, syncViewState]); // Stable deps only

  // Pointer event handlers (supports pen pressure)
  const handlePointerDown = useCallback((e) => {
    // DEBUG: Visualize Input
    const debugEl = document.getElementById('debug-overlay');
    if (debugEl) {
      debugEl.innerText = `Down: Btn=${e.button} Ctrl=${e.ctrlKey} Meta=${e.metaKey} Type=${e.pointerType}`;
    }

    // Only left mouse button or pen contact (Allow Middle Click and Right Click for Pan)
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1 && e.button !== 2) return;
    // Pen barrel button (button=2/5) also allowed for pan
    if (e.pointerType === 'pen' && e.button !== 0 && e.button !== 2 && e.button !== 5) return;

    // Photoshop-style Rotate View: hold R and drag on the canvas.
    if (isRotateViewPressedRef.current && e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
      rotateViewDragRef.current = {
        centerX,
        centerY,
        startAngle,
        startRotation: viewRotationRef.current
      };
      return;
    }

    // ─── Triple-Tap Gesture: open Pie Menu on mobile/tablet ───
    // 3 rapid taps (within 400ms, within 60px) → open pie menu
    // ONLY triggers OUTSIDE the canvas drawing area to prevent accidental
    // triggers while painting/drawing on the canvas.
    if ((e.pointerType === 'touch' || e.pointerType === 'pen') && e.button === 0) {
      const tt = tripleTapRef.current;
      const now = performance.now();
      const dx = e.clientX - tt.x;
      const dy = e.clientY - tt.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Check if this tap is INSIDE the canvas drawing container
      const canvasContainer = containerRef.current;
      const isInsideCanvas = canvasContainer
        ? canvasContainer.contains(e.target) && e.target.tagName !== 'BUTTON'
        : false;

      if (now - tt.lastTime < 400 && dist < 60) {
        tt.count += 1;
      } else {
        tt.count = 1; // reset
      }
      tt.lastTime = now;
      tt.x = e.clientX;
      tt.y = e.clientY;

      // Only open pie menu if triple-tapped OUTSIDE the canvas area
      // (e.g. on the dark padding/border around the canvas, or toolbar)
      if (tt.count >= 3 && !isInsideCanvas) {
        tt.count = 0; // reset after trigger
        setShowPieMenu(prev => {
          if (prev) return false; // if already open, close it
          setPieMenuPosition({ x: e.clientX, y: e.clientY });
          return true;
        });
        e.preventDefault();
        return; // don't start a brush stroke
      } else if (tt.count >= 3) {
        tt.count = 0; // reset even if inside canvas, so next 3 outside will work
      }
    }


    // Handle Ctrl+Right Click (barrel button) → Pointer-centric Zoom
    // OR Ctrl+Left Click → Pointer-centric Zoom
    const isCtrlZoom = (e.ctrlKey || e.metaKey) && (e.button === 0 || e.button === 2);
    if (isCtrlZoom) {
      e.preventDefault();
      e.target.setPointerCapture(e.pointerId);
      const rect = containerRef.current?.getBoundingClientRect();
      zoomDragRef.current = {
        startY: e.clientY,
        startZoom: zoom,
        // Store cursor position for pointer-centric zoom
        cursorX: rect ? e.clientX - rect.left : e.clientX,
        cursorY: rect ? e.clientY - rect.top : e.clientY
      };
      return;
    }

    // ─── TWO-FINGER TOUCH: Pan + Pinch-Zoom ───
    // When the user puts a second finger down on a touch device, switch to pan/zoom mode
    if (e.pointerType === 'touch') {
      // Track active touch pointers
      const container = containerRef.current;
      if (container) {
        // Count active touches on the canvas
        const activeTouches = (touchRef.current.pointers || new Map());
        activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        touchRef.current.pointers = activeTouches;

        if (activeTouches.size >= 2) {
          // Two fingers — switch to pan/pinch mode, cancel any drawing
          e.preventDefault();
          const pts = [...activeTouches.values()];
          const dx = pts[1].x - pts[0].x;
          const dy = pts[1].y - pts[0].y;
          touchRef.current.lastDist = Math.sqrt(dx * dx + dy * dy);
          touchRef.current.lastCenter = {
            x: (pts[0].x + pts[1].x) / 2,
            y: (pts[0].y + pts[1].y) / 2
          };
          touchRef.current.isTwoFinger = true;
          // Cancel any drawing stroke in progress
          if (drawingStateRef.current.isDrawing) {
            drawingStateRef.current.isDrawing = false;
          }
          return;
        }
      }
    }

    // Handle Pan Triggers (Middle Click OR Ctrl+Middle Click OR Pen Barrel Button (no Ctrl) OR Spacebar OR Pan Tool)
    const isMiddleClick = e.button === 1;
    const isPenBarrelButton = (e.pointerType === 'pen' && !e.ctrlKey && !e.metaKey && (e.button === 2 || e.button === 5));
    const isSpacePan = isSpacePressedRef.current;
    const isPanTool = currentTool === 'pan';

    if (isMiddleClick || isPenBarrelButton || isSpacePan || isPanTool) {
      e.preventDefault(); // Prevent browser autoscroll
      if (!isPanning) {
        setIsPanning(true);
        panStartRef.current = { x: e.clientX, y: e.clientY };
        // CRITICAL: Capture pointer to ensure drag events continue even if cursor leaves
        e.target.setPointerCapture(e.pointerId);
      }
      return;
    }

    // Prevent Drawing with Middle Click or Right Click if it wasn't a Pan trigger
    if (e.button === 1 || e.button === 2) return;

    const pos = getCanvasPoint(e);

    // SAFETY CHECK: If pos is exactly 0,0 but mouse is clearly elsewhere, ignore.
    // This prevents the "Shooting Star" bug where first point is 0,0 due to initialization race.
    // We check if clientX is significantly non-zero while pos is 0,0.
    if (Math.abs(pos.x) < 0.1 && Math.abs(pos.y) < 0.1 && (e.clientX > 50 || e.clientY > 50)) {
      console.warn("Skipping suspicious 0,0 start point");
      return;
    }

    const state = drawingStateRef.current;

    // Capture pointer for drawing too
    if (e.target.setPointerCapture) {
      e.target.setPointerCapture(e.pointerId);
    }

    // Check if Alt is pressed for temporary eyedropper
    // Alt+click = INSTANT canvas pixel pick (fast, like Photoshop)
    // For screen-wide picking, use the "Screen" button in the toolbar
    const isAltEyedropper = isAltPressedRef.current || e.altKey;
    if (isAltEyedropper) {
      const mainCanvas = mainCanvasRef.current;
      if (mainCanvas) {
        try {
          const mainCtx = mainCanvas.getContext('2d');
          // getImageData works in PHYSICAL pixel coords, not logical.
          // The canvas context has ctx.scale(dpr) applied, but getImageData ignores transforms.
          const dpr = window.devicePixelRatio || 1;
          const px = Math.floor(pos.x * dpr);
          const py = Math.floor(pos.y * dpr);
          const imageData = mainCtx.getImageData(px, py, 1, 1);
          const [r, g, b] = imageData.data;
          const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          setBrushColor(hex);
          // Update the eyedropper preview swatch instantly
          const swatch = document.getElementById('eyedropper-preview-swatch');
          if (swatch) swatch.style.backgroundColor = hex;
        } catch (err) {
          console.error("Eyedropper failed (likely tainted canvas):", err);
          toast.error("Cannot pick color: Canvas tainted");
        }
      }
      return;
    }

    state.isDrawing = true;
    state.lastX = pos.x;
    state.lastY = pos.y;
    state.startPos = { x: pos.x, y: pos.y };

    // Clear stroke canvas at start
    const strokeCanvas = currentStrokeCanvasRef.current;
    if (strokeCanvas) {
      const ctx = strokeCanvas.getContext('2d');
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    } // End Clear

    // Initialize Stroke Engine State
    const strokeState = strokeStateRef.current;
    strokeState.isActive = true;
    strokeState.points = [];
    strokeState.buffer = [];
    strokeState.layerId = state.activeLayerId;
    strokeState.lastX = pos.x;
    strokeState.lastY = pos.y;
    strokeState.lastTime = e.timeStamp; // High precision
    strokeState.lastMidX = undefined;
    strokeState.lastMidY = undefined;
    strokeState.lastVelocity = 0;
    strokeState.remainder = 0; // Reset accumulator

    // Snapshot settings FIRST (before reading them for lastSize/lastOpacity)
    strokeState.settings = {
      size: state.brushSize,
      color: state.brushColor,
      opacity: state.brushOpacity,
      brush: state.currentBrush || BRUSH_PRESETS[0], // CRITICAL: fallback to default brush
      tool: state.currentTool
    };

    // NOW safe to read from settings
    strokeState.lastSize = strokeState.settings.size;
    strokeState.lastOpacity = strokeState.settings.opacity;

    // Push initial point to buffer
    strokeState.buffer.push({
      x: pos.x,
      y: pos.y,
      pressure: pos.pressure,
      time: e.timeStamp // High precision
    });

    // Tool-specific handling that doesn't use the stroke engine
    if (['select', 'move', 'crop', 'text', 'rectangle', 'circle', 'line', 'fill', 'bubble'].includes(state.currentTool)) {
      strokeState.isActive = false;
    }

    // ─── UNIVERSAL BUBBLE INTERACTION (any tool) ───
    const currentLayers = layersRef.current;
    const currentActiveId = activeLayerIdRef.current;
    const activeBubbleLayer = currentLayers.find(l => l.id === currentActiveId);
    if (activeBubbleLayer && activeBubbleLayer.type === 'bubble' && activeBubbleLayer.bubbleConfig) {
      const bc = activeBubbleLayer.bubbleConfig;
      const currentZoom = state.zoom || 1;
      const hitRadius = 40 / currentZoom; // generous radius for tail grab

      // 1. Tail tip drag
      if (bc.tailTip) {
        const dist = Math.hypot(pos.x - bc.tailTip.x, pos.y - bc.tailTip.y);
        if (dist < hitRadius) {
          state.isDraggingTail = true;
          strokeState.isActive = false;
          return;
        }
      }

      // 2. Resize handles (corners)
      if (['bubble', 'move'].includes(state.currentTool)) {
        const handles = [
          { id: 'nw', x: bc.x, y: bc.y },
          { id: 'ne', x: bc.x + bc.width, y: bc.y },
          { id: 'sw', x: bc.x, y: bc.y + bc.height },
          { id: 'se', x: bc.x + bc.width, y: bc.y + bc.height },
        ];
        for (const h of handles) {
          if (Math.hypot(pos.x - h.x, pos.y - h.y) < hitRadius) {
            state.isResizingBubble = h.id;
            state.bubbleDragStart = { x: pos.x, y: pos.y };
            state.bubbleConfigStart = { ...bc };
            strokeState.isActive = false;
            return;
          }
        }

        // 3. Body drag (inside bubble bounds)
        if (pos.x >= bc.x && pos.x <= bc.x + bc.width && pos.y >= bc.y && pos.y <= bc.y + bc.height) {
          state.isDraggingBubbleBody = true;
          state.bubbleDragStart = { x: pos.x, y: pos.y };
          state.bubbleConfigStart = { ...bc };
          strokeState.isActive = false;
          setShowBubbleEdit(true);
          return;
        }
      }
    }

    if (state.currentTool === 'select') {
      setSelection(null);
      state.selection = null;
    } else if (state.currentTool === 'move') {
      setShowBubbleEdit(false);
      moveStartPosRef.current = { x: pos.x, y: pos.y };
      const currentOffset = layerOffsets[state.activeLayerId] || { x: 0, y: 0 };
      moveLayerStartRef.current = { ...currentOffset };
    } else if (state.currentTool === 'crop') {
      setCropRegion(null);
      setShowCropOverlay(true);
    } else if (state.currentTool === 'text') {
      setTextPosition({ x: pos.x, y: pos.y });
      setShowTextInput(true);
      state.isDrawing = false;
      setTimeout(() => textInputRef.current?.focus(), 100);
    } else if (state.currentTool === 'bubble') {
      // If already editing a bubble, check for interaction with it first
      const currentBubble = currentLayers.find(l => l.id === currentActiveId);
      if (currentBubble?.type === 'bubble' && showBubbleEditRef.current) {
        const bc = currentBubble.bubbleConfig;
        const pad = 30;
        if (pos.x >= bc.x - pad && pos.x <= bc.x + bc.width + pad &&
            pos.y >= bc.y - pad && pos.y <= bc.y + bc.height + pad + 160) {
          state.isDrawing = false;
          return;
        }
        setShowBubbleEdit(false);
        state.isDrawing = false;
        return;
      }

      // Check if clicking on any existing bubble to select
      let foundHit = false;
      for (let i = currentLayers.length - 1; i >= 0; i--) {
        const layer = currentLayers[i];
        if (!layer.visible || layer.locked || layer.type !== 'bubble') continue;
        const bc = layer.bubbleConfig;
        if (pos.x >= bc.x && pos.x <= bc.x + bc.width &&
            pos.y >= bc.y && pos.y <= bc.y + bc.height) {
          setActiveLayerId(layer.id);
          setShowBubbleEdit(true);
          foundHit = true;
          break;
        }
        const dpr = window.devicePixelRatio || 1;
        const layerCanvas = layerCanvasesRef.current[layer.id];
        if (!layerCanvas) continue;
        try {
          const ctx = layerCanvas.getContext('2d');
          const pixel = ctx.getImageData(Math.floor(pos.x * dpr), Math.floor(pos.y * dpr), 1, 1).data;
          if (pixel[3] > 10) {
            setActiveLayerId(layer.id);
            setShowBubbleEdit(true);
            foundHit = true;
            break;
          }
        } catch (e) {
          console.warn('Bubble hit detection failed', layer.id, e);
        }
      }
      if (!foundHit) {
        addBubbleLayer(pos.x, pos.y);
      }
      state.isDrawing = false;
    } else if (state.currentTool === 'eyedropper') {
      // Pick color from canvas pixel under cursor
      const mainCanvas = mainCanvasRef.current;
      if (mainCanvas) {
        try {
          const mainCtx = mainCanvas.getContext('2d');
          // getImageData works in PHYSICAL pixel coords — scale by DPR
          const dpr = window.devicePixelRatio || 1;
          const px = Math.floor(pos.x * dpr);
          const py = Math.floor(pos.y * dpr);
          const imageData = mainCtx.getImageData(px, py, 1, 1);
          const [r, g, b] = imageData.data;
          const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          setBrushColor(hex);
          toast.success(`Color picked: ${hex}`);
        } catch (err) {
          console.error('Eyedropper failed:', err);
          toast.error('Cannot pick color: Canvas tainted');
        }
      }
      state.isDrawing = false;
    } else if (state.currentTool === 'fill') {
      const activeCanvas = layerCanvasesRef.current[state.activeLayerId];
      if (activeCanvas) {
        const ctx = activeCanvas.getContext('2d');
        floodFill(ctx, Math.floor(pos.x), Math.floor(pos.y), state.brushColor);
        requestComposite();
        if (saveToHistoryRef.current) saveToHistoryRef.current();
      }
      state.isDrawing = false;
    }
  }, [isPanning, getCanvasPoint, requestComposite, layerOffsets, floodFill]);

  const handlePointerMove = useCallback((e) => {
    // 0. VISUAL CURSOR & DEBUG UPDATE (Run always for Mouse/Pen)
    if (cursorRef.current) {
      cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    }

    // Photoshop-style free Rotate View (R + drag)
    if (rotateViewDragRef.current) {
      if (e.pointerType === 'mouse' && e.buttons === 0) {
        rotateViewDragRef.current = null;
        syncViewState();
        return;
      }
      const drag = rotateViewDragRef.current;
      const angle = Math.atan2(e.clientY - drag.centerY, e.clientX - drag.centerX) * (180 / Math.PI);
      const nextRotation = drag.startRotation + (angle - drag.startAngle);
      updateViewRotationDirect(nextRotation);
      setViewRotation(((nextRotation % 360) + 360) % 360);
      return;
    }

    // ─── TWO-FINGER TOUCH: Pinch-Zoom + Pan ───
    if (e.pointerType === 'touch' && touchRef.current.isTwoFinger) {
      // Update pointer position
      const pointers = touchRef.current.pointers;
      if (pointers && pointers.has(e.pointerId)) {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }

      if (pointers && pointers.size >= 2) {
        const pts = [...pointers.values()];
        const dx = pts[1].x - pts[0].x;
        const dy = pts[1].y - pts[0].y;
        const newDist = Math.sqrt(dx * dx + dy * dy);
        const newCenter = {
          x: (pts[0].x + pts[1].x) / 2,
          y: (pts[0].y + pts[1].y) / 2
        };

        const lastDist = touchRef.current.lastDist || newDist;
        const lastCenter = touchRef.current.lastCenter || newCenter;

        // — Pinch-Zoom —
        if (lastDist > 0) {
          const scale = newDist / lastDist;
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const curZoom = zoomRef.current;
            const curPan = panRef.current;
            const newZoom = Math.min(Math.max(0.1, curZoom * scale), 10);
            // Zoom centered on pinch midpoint
            const cx = newCenter.x - rect.left;
            const cy = newCenter.y - rect.top;
            const worldX = (cx - curPan.x) / curZoom;
            const worldY = (cy - curPan.y) / curZoom;
            const zoomedPanX = cx - worldX * newZoom;
            const zoomedPanY = cy - worldY * newZoom;
            updateZoomDirect(newZoom);
            updatePanDirect({ x: zoomedPanX, y: zoomedPanY });
          }
        }

        // — Two-Finger Pan (midpoint delta) —
        const panDx = newCenter.x - lastCenter.x;
        const panDy = newCenter.y - lastCenter.y;
        if (Math.abs(panDx) > 0 || Math.abs(panDy) > 0) {
          const curPan = panRef.current;
          updatePanDirect({ x: curPan.x + panDx, y: curPan.y + panDy });
        }

        touchRef.current.lastDist = newDist;
        touchRef.current.lastCenter = newCenter;
      }
      return;
    }

    // DEBUG INFO — reads from refs (no re-render dependency)
    const debugEl = document.getElementById('debug-overlay');
    if (debugEl && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const curPan = panRef.current;
      const curZoom = zoomRef.current;
      const worldX = (e.clientX - rect.left - curPan.x) / curZoom;
      const worldY = (e.clientY - rect.top - curPan.y) / curZoom;
      debugEl.innerText = `Screen: ${Math.round(e.clientX)},${Math.round(e.clientY)} | P: ${Math.round(curPan.x)},${Math.round(curPan.y)} | Z: ${curZoom.toFixed(2)} | W: ${Math.round(worldX)},${Math.round(worldY)}`;
    }

    // 1a. Handle Ctrl+Drag Zoom (pointer-centric) — uses refs, no re-render
    if (zoomDragRef.current) {
      const dy = zoomDragRef.current.startY - e.clientY; // Drag up = zoom in
      const zoomFactor = 1 + dy * 0.005;
      const newZoom = Math.min(Math.max(0.1, zoomDragRef.current.startZoom * zoomFactor), 10);

      // Pointer-centric: keep cursor world point stationary
      const cx = zoomDragRef.current.cursorX;
      const cy = zoomDragRef.current.cursorY;
      const curPan = panRef.current;
      const curZoom = zoomRef.current;
      const worldX = (cx - curPan.x) / curZoom;
      const worldY = (cy - curPan.y) / curZoom;
      const newPanX = cx - worldX * newZoom;
      const newPanY = cy - worldY * newZoom;

      updateZoomDirect(newZoom);
      updatePanDirect({ x: newPanX, y: newPanY });
      return;
    }

    // 1b. Handle Panning (High Priority) — uses refs + direct DOM, no re-render
    if (panStartRef.current) {
      // Safety Valve: If mouse button is NOT pressed, stop panning immediately.
      if (e.pointerType === 'mouse' && e.buttons === 0) {
        setIsPanning(false);
        panStartRef.current = null;
        syncViewState(); // Sync refs → state only when panning ends
        return;
      }
      // Safety Valve: If pen is not pressed, stop panning
      if (e.pointerType === 'pen' && e.buttons === 0) {
        setIsPanning(false);
        panStartRef.current = null;
        syncViewState();
        return;
      }

      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;

      const curPan = panRef.current;
      updatePanDirect({ x: curPan.x + dx, y: curPan.y + dy });
      panStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    const state = drawingStateRef.current;
    if (!state.isDrawing) return;

    const pos = getCanvasPoint(e);
    state.pressure = pos.pressure;
    currentPressureRef.current = pos.pressure;

    // Display pressure throttle
    const now = performance.now();
    if (now - lastPressureUpdateRef.current > 16) {
      setDisplayPressure(pos.pressure);
      lastPressureUpdateRef.current = now;
    }

    // 1. STROKE ENGINE BUFFERING (Brush, Eraser)
    const strokeState = strokeStateRef.current;
    if (strokeState.isActive) {
      // ─── Stroke Stabilizer: exponential smoothing ───
      // Higher stabilizer = more lag but smoother output (0=off, 10=heavy smoothing)
      let sx = pos.x;
      let sy = pos.y;
      const stabStrength = drawingStateRef.current.brushStabilizer ?? 0;
      if (stabStrength > 0 && (drawingStateRef.current.currentTool === 'brush')) {
        const alpha = 1.0 - Math.min(0.9, stabStrength * 0.08); // alpha: 1=no smooth, 0.1=heavy
        const buf = stabilizerBufferRef.current;
        if (buf.length > 0) {
          const last = buf[buf.length - 1];
          sx = last.x * (1 - alpha) + pos.x * alpha;
          sy = last.y * (1 - alpha) + pos.y * alpha;
        }
        stabilizerBufferRef.current = [{ x: sx, y: sy }]; // keep last smoothed point
      } else {
        stabilizerBufferRef.current = []; // clear when stabilizer off
      }

      strokeState.buffer.push({
        x: sx,
        y: sy,
        pressure: pos.pressure,
        time: e.timeStamp
      });
      return;
    }

    // 2. Legacy/Interactiv Tool Handling (Select, Move, Shapes)

    // ─── Bubble Tail Drag ───
    if (state.isDraggingTail) {
      const activeId = state.activeLayerId;
      const layer = layersRef.current.find(l => l.id === activeId);
      if (layer && layer.type === 'bubble') {
        const newConfig = { ...layer.bubbleConfig, tailTip: { x: pos.x, y: pos.y } };
        renderBubbleLayer(activeId, newConfig);
        setLayers(prev => prev.map(l => l.id === activeId ? { ...l, bubbleConfig: newConfig } : l));
        requestComposite();
      }
      return;
    }

    // ─── Bubble Body Drag ───
    if (state.isDraggingBubbleBody) {
      const layer = layersRef.current.find(l => l.id === state.activeLayerId);
      if (layer && layer.type === 'bubble') {
        const dx = pos.x - state.bubbleDragStart.x;
        const dy = pos.y - state.bubbleDragStart.y;
        const sc = state.bubbleConfigStart;
        const newConfig = {
          ...layer.bubbleConfig,
          x: sc.x + dx,
          y: sc.y + dy,
          tailTip: sc.tailTip ? { x: sc.tailTip.x + dx, y: sc.tailTip.y + dy } : undefined
        };
        renderBubbleLayer(state.activeLayerId, newConfig);
        setLayers(prev => prev.map(l => l.id === state.activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
        requestComposite();
      }
      return;
    }

    // ─── Bubble Resize ───
    if (state.isResizingBubble) {
      const layer = layersRef.current.find(l => l.id === state.activeLayerId);
      if (layer && layer.type === 'bubble') {
        const dx = pos.x - state.bubbleDragStart.x;
        const dy = pos.y - state.bubbleDragStart.y;
        const sc = state.bubbleConfigStart;
        const handle = state.isResizingBubble;
        let newX = sc.x, newY = sc.y, newW = sc.width, newH = sc.height;

        if (handle.includes('e')) newW = Math.max(60, sc.width + dx);
        if (handle.includes('w')) { newX = sc.x + dx; newW = Math.max(60, sc.width - dx); }
        if (handle.includes('s')) newH = Math.max(40, sc.height + dy);
        if (handle.includes('n')) { newY = sc.y + dy; newH = Math.max(40, sc.height - dy); }

        const newConfig = { ...layer.bubbleConfig, x: newX, y: newY, width: newW, height: newH };
        renderBubbleLayer(state.activeLayerId, newConfig);
        setLayers(prev => prev.map(l => l.id === state.activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
        requestComposite();
      }
      return;
    }

    // Selection tool
    if (state.currentTool === 'select') {
      const selRect = {
        x: Math.min(state.startPos.x, pos.x),
        y: Math.min(state.startPos.y, pos.y),
        width: Math.abs(pos.x - state.startPos.x),
        height: Math.abs(pos.y - state.startPos.y)
      };
      state.selection = selRect;
      setSelection(selRect);
      requestComposite();
      return;
    }

    // Move tool
    if (state.currentTool === 'move') {
      const dx = pos.x - moveStartPosRef.current.x;
      const dy = pos.y - moveStartPosRef.current.y;

      setLayerOffsets(prev => ({
        ...prev,
        [state.activeLayerId]: {
          x: moveLayerStartRef.current.x + dx,
          y: moveLayerStartRef.current.y + dy
        }
      }));
      requestComposite();
      return;
    }

    // Shape Preview
    if (['rectangle', 'circle', 'line'].includes(state.currentTool)) {
      const compositeCanvas = compositeCanvasRef.current;
      const compositeCtx = compositeCanvas.getContext('2d');
      compositeCtx.clearRect(0, 0, canvasWidth, canvasHeight);

      // Draw layers
      layers.forEach(layer => {
        if (layer.visible && layerCanvasesRef.current[layer.id]) {
          compositeCtx.globalAlpha = layer.opacity;
          compositeCtx.drawImage(layerCanvasesRef.current[layer.id], 0, 0, canvasWidth, canvasHeight);
        }
      });

      compositeCtx.globalAlpha = state.brushOpacity;
      compositeCtx.strokeStyle = state.brushColor;
      compositeCtx.fillStyle = state.fillColor;
      compositeCtx.lineWidth = state.strokeWidth;

      if (state.currentTool === 'rectangle') {
        const w = pos.x - state.startPos.x;
        const h = pos.y - state.startPos.y;
        compositeCtx.fillRect(state.startPos.x, state.startPos.y, w, h);
        compositeCtx.strokeRect(state.startPos.x, state.startPos.y, w, h);
      } else if (state.currentTool === 'circle') {
        const radius = Math.sqrt((pos.x - state.startPos.x) ** 2 + (pos.y - state.startPos.y) ** 2);
        compositeCtx.beginPath();
        compositeCtx.arc(state.startPos.x, state.startPos.y, radius, 0, 2 * Math.PI);
        compositeCtx.fill();
        compositeCtx.stroke();
      } else if (state.currentTool === 'line') {
        compositeCtx.beginPath();
        compositeCtx.moveTo(state.startPos.x, state.startPos.y);
        compositeCtx.lineTo(pos.x, pos.y);
        compositeCtx.stroke();
      }
      compositeCtx.globalAlpha = 1;

      const mainCtx = mainCanvasRef.current.getContext('2d');

      // Reset transform to identity for 1:1 pixel copy
      mainCtx.save();
      mainCtx.setTransform(1, 0, 0, 1, 0, 0);
      mainCtx.clearRect(0, 0, mainCanvasRef.current.width, mainCanvasRef.current.height);
      mainCtx.drawImage(compositeCanvas, 0, 0);
      mainCtx.restore();
    }
  }, [getCanvasPoint, requestComposite, layerOffsets, canvasWidth, canvasHeight, updateViewRotationDirect, syncViewState]);



  const handlePointerUp = useCallback((e) => {
    // Finish Photoshop-style free Rotate View (R + drag)
    if (rotateViewDragRef.current) {
      rotateViewDragRef.current = null;
      syncViewState();
      return;
    }

    // ─── TWO-FINGER TOUCH CLEANUP ───
    if (e.pointerType === 'touch') {
      const pointers = touchRef.current.pointers;
      if (pointers) {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) {
          touchRef.current.isTwoFinger = false;
          touchRef.current.lastDist = 0;
          touchRef.current.lastCenter = null;
          if (pointers.size === 0) {
            syncViewState(); // Sync refs → React state when fingers lift
          }
        }
      }
    }

    // Stop Ctrl+Pen Drag Zoom — sync state at interaction end
    if (zoomDragRef.current) {
      zoomDragRef.current = null;
      syncViewState();
      return;
    }

    // Stop Panning — sync state at interaction end
    if (panStartRef.current || isPanning) {
      setIsPanning(false);
      panStartRef.current = null;
      syncViewState();
      return;
    }

    const state = drawingStateRef.current;

    if (state.isDraggingTail) {
      state.isDraggingTail = false;
      saveToHistory();
      return;
    }
    if (state.isDraggingBubbleBody) {
      state.isDraggingBubbleBody = false;
      state.bubbleDragStart = null;
      state.bubbleConfigStart = null;
      saveToHistory();
      return;
    }
    if (state.isResizingBubble) {
      state.isResizingBubble = null;
      state.bubbleDragStart = null;
      state.bubbleConfigStart = null;
      saveToHistory();
      return;
    }

    // Stop Drawing
    if (state.isDrawing) {
      state.isDrawing = false;

      // COMMIT STROKE TO ACTIVE LAYER
      const strokeCanvas = currentStrokeCanvasRef.current;
      const activeLayerId = drawingStateRef.current.activeLayerId;
      const layerCanvas = layerCanvasesRef.current[activeLayerId];

      if (strokeCanvas && layerCanvas) {
        const ctx = layerCanvas.getContext('2d');
        // Draw stroke canvas onto layer using logical coordinates (matching performComposite)
        // Both canvases are DPR-scaled, so we draw in logical space to match the preview exactly
        ctx.save();
        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(strokeCanvas, 0, 0, canvasWidth, canvasHeight);
        ctx.restore();

        // Clear stroke buffer
        const strokeCtx = strokeCanvas.getContext('2d');
        strokeCtx.clearRect(0, 0, strokeCanvas.width, strokeCanvas.height);
      }

      const strokeState = strokeStateRef.current;
      strokeState.isActive = false;

      // Final composite trigger (ensure last stroke segment is drawn)
      requestComposite();

      // Save history for brush tools — DEFERRED to not block pointer handler
      if (['brush', 'eraser', 'line', 'rectangle', 'circle', 'fill'].includes(state.currentTool)) {
        // Defer to next tick so handlePointerUp returns immediately
        // This keeps pan/zoom responsive — the heavy work runs after the event loop clears
        setTimeout(() => {
          if (saveToHistoryRef.current) saveToHistoryRef.current();
          if (captureRecordingFrameRef.current) captureRecordingFrameRef.current();
        }, 0);
      }
    }

    // Reset move tool state
    if (state.currentTool === 'move') {
      moveStartPosRef.current = null;
      moveLayerStartRef.current = null;
      // Final composite for move
      requestComposite();
    }

    // Reset crop tool logic if needed (handled by overlay buttons usually)

  }, [isPanning, requestComposite, saveToHistory]);



  // Handle Double Click (Edit Text)
  const handleDoubleClick = useCallback((e) => {
    e.preventDefault();
    const pos = getCanvasPoint(e);

    // Check for layer hit (Top to Bottom)
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      if (!layer.visible || layer.locked) continue;

      const layerCanvas = layerCanvasesRef.current[layer.id];
      if (!layerCanvas) continue;

      // Check pixel alpha at pos
      // Layer Canvas is scaled by DPR
      const dpr = window.devicePixelRatio || 1;
      const ctx = layerCanvas.getContext('2d');

      try {
        // Only check 1 pixel
        const pixel = ctx.getImageData(Math.floor(pos.x * dpr), Math.floor(pos.y * dpr), 1, 1).data;
        if (pixel[3] > 10) { // Threshold for hit
          // HIT!
          setActiveLayerId(layer.id);

          if (layer.type === 'text' && layer.textConfig) {
            // Trigger Edit Mode
            setActiveTextLayerId(layer.id);
            setTextInput(layer.textConfig.content);
            setTextFontSize(layer.textConfig.fontSize);
            setTextFontFamily(layer.textConfig.fontFamily);
            setBrushColor(layer.textConfig.color);
            setTextStrokeColor(layer.textConfig.strokeColor);
            setTextStrokeWidth(layer.textConfig.strokeWidth);

            setTextPosition({ x: layer.textConfig.x, y: layer.textConfig.y });
            setShowTextInput(true);
            // toast.info('Editing text layer');
          } else if (layer.type === 'bubble') {
            setShowBubbleEdit(true);
          }
          return;
        }
      } catch (err) {
        // Ignore CORS errors if any (shouldn't be for local text)
      }
    }
  }, [layers, getCanvasPoint]);

  // Apply text - Creates a new Text Layer


  // Cancel text input
  const cancelText = useCallback(() => {
    setTextInput('');
    setTextPosition(null);
    setShowTextInput(false);
  }, []);

  // Apply crop to all layers
  const applyCrop = useCallback(() => {
    if (!cropRegion || cropRegion.width < 10 || cropRegion.height < 10) {
      toast.error('Please select a larger area to crop');
      return;
    }

    // For each layer, crop the content
    Object.keys(layerCanvasesRef.current).forEach(layerId => {
      const layerCanvas = layerCanvasesRef.current[layerId];
      if (!layerCanvas) return;

      const ctx = layerCanvas.getContext('2d');

      // Get the cropped area
      const imageData = ctx.getImageData(
        cropRegion.x,
        cropRegion.y,
        cropRegion.width,
        cropRegion.height
      );

      // Create temp canvas with crop region data
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvasWidth;
      tempCanvas.height = canvasHeight;
      const tempCtx = tempCanvas.getContext('2d');

      // Put the cropped content at top-left
      tempCtx.putImageData(imageData, 0, 0);

      // Clear and copy back to main canvas
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(tempCanvas, 0, 0);
    });

    setCropRegion(null);
    setShowCropOverlay(false);
    requestComposite();
    saveToHistory();
    toast.success('Canvas cropped - content moved to top-left');
  }, [cropRegion, canvasWidth, canvasHeight, requestComposite]);

  // Cancel crop
  const cancelCrop = useCallback(() => {
    setCropRegion(null);
    setShowCropOverlay(false);
  }, []);



  // Layer management
  const addLayer = useCallback(() => {
    if (layers.length >= MAX_LAYERS) {
      toast.error(`Layer count limited to ${MAX_LAYERS} for performance.`);
      return;
    }

    const newId = Date.now();
    const newLayer = {
      id: newId,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal'
    };

    const newCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    newCanvas.width = canvasWidth * dpr;
    newCanvas.height = canvasHeight * dpr;
    const ctx = newCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    layerCanvasesRef.current[newId] = newCanvas;

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);

    toast.success('Layer added');
    setTimeout(() => saveToHistory(), 50);
  }, [layers.length, canvasWidth, canvasHeight, saveToHistory]);

  // Render Bubble Layer
  const renderBubbleLayer = useCallback((layerId, config) => {
    const canvas = layerCanvasesRef.current[layerId];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Config
    const { shapeId, width, height, x, y, fillColor, strokeColor, strokeWidth, tailTip, shadow, shadowColor = '#000000', shadowOffset = 10 } = config;
    const shape = BUBBLE_LIBRARY.find(s => s.id === shapeId) || BUBBLE_LIBRARY[0];

    const sx = width / 100;
    const sy = height / 100;

    // ─── Compute tail geometry (reused by drawTail) ───
    const computeTailGeometry = () => {
      if (!shape.hasTail || !tailTip) return null;

      const tx = tailTip.x;
      const ty = tailTip.y;

      // The SVG paths are in 0-100 normalized space.
      // The oval_classic path goes from (5,5) to (95,95) → center (50,50), radii (45,45)
      // The rect_rounded path goes from (0,0) to (100,100) → center (50,50), radii (50,50)
      // We need to match the actual shape boundary, not the bounding box.
      
      // Determine shape inset factor (how much the shape is inset from the bounding box)
      let shapeInset = 0; // 0 = fills entire bbox, 0.05 = 5% inset on each side
      if (shape.id === 'oval_classic') shapeInset = 0.05;
      // thought_cloud and shout_jagged have irregular shapes, use small inset
      if (shape.id === 'thought_cloud' || shape.id === 'shout_jagged') shapeInset = 0.05;

      // Actual shape center and radii (accounting for SVG path inset)
      const cx = x + width / 2;
      const cy = y + height / 2;
      const hw = width * (0.5 - shapeInset);  // actual half-width of the rendered shape
      const hh = height * (0.5 - shapeInset); // actual half-height of the rendered shape

      // If tail tip is inside the bubble, don't draw tail
      const ellipseDist = Math.pow((tx - cx) / hw, 2) + Math.pow((ty - cy) / hh, 2);
      if (ellipseDist < 1.1) return null; // inside or barely on edge

      // Find angle from center to tail tip
      const angle = Math.atan2(ty - cy, tx - cx);

      // Edge point on the actual shape surface, pushed slightly INWARD (92%)
      // so the tail base overlaps with the body. Since body draws on top,
      // the overlap is invisible → guaranteed seamless join.
      const edgeX = cx + hw * Math.cos(angle) * 0.92;
      const edgeY = cy + hh * Math.sin(angle) * 0.92;

      // Base width: proportional to bubble size, wider for closer tail
      const maxBaseWidth = Math.min(width, height) * 0.2;
      const minBaseWidth = Math.min(width, height) * 0.08;
      const tipDist = Math.hypot(tx - edgeX, ty - edgeY);
      const baseWidth = Math.max(minBaseWidth, maxBaseWidth * Math.min(1, 80 / Math.max(tipDist, 1)));

      // Perpendicular direction for base spread
      const perpAngle = angle + Math.PI / 2;
      const bx1 = edgeX + Math.cos(perpAngle) * baseWidth;
      const by1 = edgeY + Math.sin(perpAngle) * baseWidth;
      const bx2 = edgeX - Math.cos(perpAngle) * baseWidth;
      const by2 = edgeY - Math.sin(perpAngle) * baseWidth;

      // Control point for the Bézier curves — offset perpendicular for a smooth taper
      const cpOffset = baseWidth * 0.4;
      const midX = (edgeX + tx) / 2;
      const midY = (edgeY + ty) / 2;

      return { tx, ty, bx1, by1, bx2, by2, midX, midY, cpOffset, perpAngle, edgeX, edgeY };
    };

    // ─── Draw tail shape ───
    const drawTail = (offsetX, offsetY, color, isShadow) => {
      const geom = computeTailGeometry();
      if (!geom) return;

      const { tx, ty, bx1, by1, bx2, by2, midX, midY, cpOffset, perpAngle } = geom;

      ctx.save();
      ctx.translate(offsetX, offsetY);

      ctx.beginPath();
      ctx.moveTo(bx1, by1);
      ctx.quadraticCurveTo(
        midX + Math.cos(perpAngle) * cpOffset,
        midY + Math.sin(perpAngle) * cpOffset,
        tx, ty
      );
      ctx.quadraticCurveTo(
        midX - Math.cos(perpAngle) * cpOffset,
        midY - Math.sin(perpAngle) * cpOffset,
        bx2, by2
      );
      ctx.closePath();

      ctx.fillStyle = color;
      ctx.fill();

      // Only stroke the outer edges (not the base that connects to bubble)
      if (!isShadow) {
        ctx.beginPath();
        ctx.moveTo(bx1, by1);
        ctx.quadraticCurveTo(
          midX + Math.cos(perpAngle) * cpOffset,
          midY + Math.sin(perpAngle) * cpOffset,
          tx, ty
        );
        ctx.quadraticCurveTo(
          midX - Math.cos(perpAngle) * cpOffset,
          midY - Math.sin(perpAngle) * cpOffset,
          bx2, by2
        );
        // Don't closePath — leave the base open so stroke doesn't cross the bubble
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      ctx.restore();
    };

    // ─── Draw body shape ───
    const drawBody = (offsetX, offsetY, color, isShadow) => {
      ctx.save();
      ctx.translate(x + offsetX, y + offsetY);
      ctx.scale(sx, sy);

      const path = new Path2D(shape.path);

      ctx.fillStyle = color;
      ctx.fill(path);

      if (!isShadow) {
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = strokeWidth / Math.min(sx, sy);
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke(path);
      }

      ctx.restore();
    };

    ctx.save();
    ctx.scale(dpr, dpr);

    // 1. Shadow
    if (shadow) {
      drawTail(shadowOffset, shadowOffset, shadowColor, true);
      drawBody(shadowOffset, shadowOffset, shadowColor, true);
    }

    // 2. Main: Tail FIRST (fill), then Body ON TOP (fill + stroke)
    //    The body's fill covers the tail base, creating a seamless join
    drawTail(0, 0, fillColor, false);
    drawBody(0, 0, fillColor, false);

    ctx.restore();
  }, []);

  // Add Bubble Layer
  const addBubbleLayer = useCallback((x, y) => {
    if (layers.length >= MAX_LAYERS) {
      toast.error(`Layer count limited to ${MAX_LAYERS}.`);
      return;
    }

    const newId = Date.now();
    const newLayer = {
      id: newId,
      type: 'bubble',
      name: 'Speech Bubble',
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal',
      bubbleConfig: {
        shapeId: BUBBLE_LIBRARY[0].id,
        x: x - 100,
        y: y - 50,
        width: 200,
        height: 100,
        fillColor: '#ffffff',
        strokeColor: '#000000',
        strokeWidth: 3,
        tailTip: { x: x, y: y + 150 }
      }
    };

    // Create canvas
    const newCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    newCanvas.width = canvasWidth * dpr;
    newCanvas.height = canvasHeight * dpr;
    const ctx = newCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    layerCanvasesRef.current[newId] = newCanvas;

    // Render initial
    renderBubbleLayer(newId, newLayer.bubbleConfig);

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);
    requestComposite();
    toast.success('Bubble added');
    setShowBubbleEdit(true);
    setTimeout(() => saveToHistory(), 50);
  }, [layers.length, canvasWidth, canvasHeight, saveToHistory, renderBubbleLayer]);

  // Add Special Text Layer
  const addTextLayer = useCallback((initialText, x, y) => {
    if (layers.length >= MAX_LAYERS) {
      toast.error(`Layer count limited to ${MAX_LAYERS}.`);
      return;
    }

    const newId = Date.now();
    const newLayer = {
      id: newId,
      type: 'text', // Special type
      name: `Text: ${initialText.substring(0, 10)}...`,
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal',
      textConfig: {
        content: initialText,
        x: x,
        y: y,
        fontSize: textFontSize,
        fontFamily: textFontFamily,
        bold: textBold,
        italic: textItalic,
        color: brushColor,
        strokeColor: textStrokeColor,
        strokeWidth: textStrokeWidth,
        shadowColor: textShadowColor,
        shadowBlur: textShadowBlur,
        shadowOffsetX: textShadowOffsetX,
        shadowOffsetY: textShadowOffsetY,
        maxWidth: textMaxWidth,
        lineHeight: 1.2
      }
    };

    // Create canvas for this layer
    const newCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    newCanvas.width = canvasWidth * dpr;
    newCanvas.height = canvasHeight * dpr;
    const ctx = newCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    layerCanvasesRef.current[newId] = newCanvas;

    // Render text to this canvas immediately
    renderTextLayer(newId, newLayer.textConfig);

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);
    setActiveTextLayerId(newId); // Mark as active text layer

    requestComposite(); // Force update display
    toast.success('Text layer created');
    setTimeout(() => saveToHistory(), 50);
  }, [layers.length, canvasWidth, canvasHeight, saveToHistory, renderTextLayer, textFontSize, textFontFamily, textBold, textItalic, brushColor, textStrokeColor, textStrokeWidth, textShadowColor, textShadowBlur, textShadowOffsetX, textShadowOffsetY, textMaxWidth]);

  // Update Text Layer (Live Editing)
  const updateTextLayer = useCallback((layerId, newConfig) => {
    setLayers(prev => prev.map(layer => {
      if (layer.id === layerId) {
        const updatedLayer = { ...layer, textConfig: { ...layer.textConfig, ...newConfig } };
        // Re-render immediately
        renderTextLayer(layerId, updatedLayer.textConfig);
        return updatedLayer;
      }
      return layer;
    }));
    requestComposite();
  }, [renderTextLayer, requestComposite]);


  // Effect: Watch for selection of text layers
  useEffect(() => {
    if (activeLayerId === lastActiveLayerIdRef.current) return;
    lastActiveLayerIdRef.current = activeLayerId;

    const layer = layers.find(l => l.id === activeLayerId);
    if (layer && layer.type === 'text' && layer.textConfig) {
      setActiveTextLayerId(layer.id);
      setTextInput(layer.textConfig.content);
      setTextFontSize(layer.textConfig.fontSize);
      setTextFontFamily(layer.textConfig.fontFamily);
      setBrushColor(layer.textConfig.color);
      setTextStrokeColor(layer.textConfig.strokeColor);
      setTextStrokeWidth(layer.textConfig.strokeWidth);

      setTextPosition({ x: layer.textConfig.x, y: layer.textConfig.y });
      setTextMaxWidth(layer.textConfig.maxWidth || 300);
      setShowTextInput(true);
    } else {
      if (activeTextLayerId) {
        setActiveTextLayerId(null);
        setShowTextInput(false);
      }
    }
  }, [activeLayerId, layers, activeTextLayerId]);

  // Apply text - Creates a new Text Layer OR Finishes Editing
  const applyText = useCallback(() => {
    if (!textInput.trim() || !textPosition) {
      setShowTextInput(false);
      return;
    }

    if (activeTextLayerId) {
      // We are in edit mode, and "Finish/Enter" was pressed.
      // Just close the UI, as updates are real-time.
      setShowTextInput(false);
      setActiveTextLayerId(null);
      toast.success('Text updated');
      return;
    }

    // Use new Text Layer System (Create New)
    addTextLayer(textInput, textPosition.x, textPosition.y);

    // Reset UI state
    setTextInput('');
    setTextPosition(null);
    setShowTextInput(false);
  }, [textInput, textPosition, addTextLayer, activeTextLayerId]);

  const deleteLayer = useCallback((layerId) => {
    if (layers.length <= 1) {
      toast.error('Cannot delete the last layer');
      return;
    }

    delete layerCanvasesRef.current[layerId];

    const newLayers = layers.filter(l => l.id !== layerId);
    setLayers(newLayers);

    if (activeLayerId === layerId) {
      setActiveLayerId(newLayers[0].id);
    }

    requestComposite();
    toast.success('Layer deleted');
    setTimeout(() => saveToHistory(), 50);
  }, [layers, activeLayerId, requestComposite, saveToHistory]);

  const toggleLayerVisibility = useCallback((layerId) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
    ));
    requestComposite();
  }, [requestComposite]);

  const toggleLayerLock = useCallback((layerId) => {
    setLayers(prev => prev.map(l => l.id === layerId ? { ...l, locked: !l.locked } : l));
  }, []);

  const moveLayerUp = useCallback((layerId) => {
    setLayers(prev => {
      const index = prev.findIndex(l => l.id === layerId);
      if (index === -1 || index === prev.length - 1) return prev;

      const newLayers = [...prev];
      const temp = newLayers[index];
      newLayers[index] = newLayers[index + 1];
      newLayers[index + 1] = temp;

      return newLayers;
    });
    requestComposite();
  }, [requestComposite]);

  const moveLayerDown = useCallback((layerId) => {
    setLayers(prev => {
      const index = prev.findIndex(l => l.id === layerId);
      if (index <= 0) return prev;

      const newLayers = [...prev];
      const temp = newLayers[index];
      newLayers[index] = newLayers[index - 1];
      newLayers[index - 1] = temp;

      return newLayers;
    });
    requestComposite();
  }, [requestComposite]);



  const updateLayerOpacity = useCallback((layerId, opacity) => {
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, opacity } : layer
    ));
    requestComposite();
  }, [requestComposite]);

  // ============================================
  // ADVANCED LAYER OPERATIONS
  // ============================================

  // Duplicate layer - creates exact copy
  const duplicateLayer = useCallback((layerId = activeLayerId) => {
    const sourceLayer = layers.find(l => l.id === layerId);
    if (!sourceLayer) return;

    const sourceCanvas = layerCanvasesRef.current[layerId];
    if (!sourceCanvas) return;

    const newId = Date.now();
    const newLayer = {
      ...sourceLayer,
      id: newId,
      name: `${sourceLayer.name} Copy`,
      locked: false,
      hasMask: false,
      maskDisabled: false
    };

    // Copy canvas content
    const newCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    newCanvas.width = canvasWidth * dpr;
    newCanvas.height = canvasHeight * dpr;
    const ctx = newCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.drawImage(sourceCanvas, 0, 0, canvasWidth, canvasHeight);
    layerCanvasesRef.current[newId] = newCanvas;

    // Copy mask if exists
    if (sourceLayer.hasMask && layerMasksRef.current[layerId]) {
      const sourceMask = layerMasksRef.current[layerId];
      const newMask = document.createElement('canvas');
      newMask.width = canvasWidth;
      newMask.height = canvasHeight;
      const maskCtx = newMask.getContext('2d');
      maskCtx.drawImage(sourceMask, 0, 0);
      layerMasksRef.current[newId] = newMask;
      newLayer.hasMask = true;
    }

    // Insert after source layer
    const sourceIndex = layers.findIndex(l => l.id === layerId);
    setLayers(prev => {
      const newLayers = [...prev];
      newLayers.splice(sourceIndex + 1, 0, newLayer);
      return newLayers;
    });
    setActiveLayerId(newId);

    requestComposite();
    toast.success('Layer duplicated');
    setTimeout(() => saveToHistory(), 50);
  }, [activeLayerId, layers, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Copy layer to clipboard
  const copyLayer = useCallback((layerId = activeLayerId) => {
    const sourceCanvas = layerCanvasesRef.current[layerId];
    if (!sourceCanvas) return;

    const sourceLayer = layers.find(l => l.id === layerId);

    // Create clipboard copy
    const clipboardCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    clipboardCanvas.width = canvasWidth * dpr;
    clipboardCanvas.height = canvasHeight * dpr;
    const ctx = clipboardCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.drawImage(sourceCanvas, 0, 0, canvasWidth, canvasHeight);

    setLayerClipboard({
      canvas: clipboardCanvas,
      name: sourceLayer?.name || 'Pasted Layer'
    });

    toast.success('Layer copied');
  }, [activeLayerId, layers, canvasWidth, canvasHeight]);

  // Cut layer (copy + clear)
  const cutLayer = useCallback((layerId = activeLayerId) => {
    copyLayer(layerId);
    clearLayer(layerId);
    toast.success('Layer cut');
  }, [activeLayerId, copyLayer]);

  // Paste as new layer
  const pasteAsLayer = useCallback(() => {
    if (!layerClipboard) {
      toast.error('Nothing to paste');
      return;
    }

    const newId = Date.now();
    const newLayer = {
      id: newId,
      name: `${layerClipboard.name} (Pasted)`,
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal',
      hasMask: false,
      maskDisabled: false
    };

    // Copy from clipboard
    const newCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    newCanvas.width = canvasWidth * dpr;
    newCanvas.height = canvasHeight * dpr;
    const ctx = newCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.drawImage(layerClipboard.canvas, 0, 0, canvasWidth, canvasHeight);
    layerCanvasesRef.current[newId] = newCanvas;

    setLayers(prev => [...prev, newLayer]);
    setActiveLayerId(newId);

    requestComposite();
    toast.success('Pasted as new layer');
    setTimeout(() => saveToHistory(), 50);
  }, [layerClipboard, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Clear layer content
  const clearLayer = useCallback((layerId = activeLayerId) => {
    const canvas = layerCanvasesRef.current[layerId];
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    requestComposite();
    toast.success('Layer cleared');
    setTimeout(() => saveToHistory(), 50);
  }, [activeLayerId, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Merge layer down
  const mergeLayerDown = useCallback((layerId = activeLayerId) => {
    const layerIndex = layers.findIndex(l => l.id === layerId);
    if (layerIndex <= 0) {
      toast.error('No layer below to merge with');
      return;
    }

    const topLayer = layers[layerIndex];
    const bottomLayer = layers[layerIndex - 1];
    const topCanvas = layerCanvasesRef.current[layerId];
    const bottomCanvas = layerCanvasesRef.current[bottomLayer.id];

    if (!topCanvas || !bottomCanvas) return;

    // Draw top onto bottom
    const ctx = bottomCanvas.getContext('2d');
    ctx.globalAlpha = topLayer.opacity;
    ctx.globalCompositeOperation = topLayer.blendMode === 'normal' ? 'source-over' : topLayer.blendMode;
    ctx.drawImage(topCanvas, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // Delete top layer
    delete layerCanvasesRef.current[layerId];
    if (layerMasksRef.current[layerId]) {
      delete layerMasksRef.current[layerId];
    }

    setLayers(prev => prev.filter(l => l.id !== layerId));
    setActiveLayerId(bottomLayer.id);

    requestComposite();
    toast.success('Layers merged');
    setTimeout(() => saveToHistory(), 50);
  }, [activeLayerId, layers, requestComposite, saveToHistory]);

  // Flatten all visible layers
  const flattenLayers = useCallback(() => {
    if (layers.length <= 1) {
      toast.info('Only one layer exists');
      return;
    }

    const visibleLayers = layers.filter(l => l.visible);
    if (visibleLayers.length === 0) {
      toast.error('No visible layers to flatten');
      return;
    }

    // Create flattened canvas
    const flatCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    flatCanvas.width = canvasWidth * dpr;
    flatCanvas.height = canvasHeight * dpr;
    const ctx = flatCanvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Draw layers in order (bottom to top)
    visibleLayers.forEach(layer => {
      const layerCanvas = layerCanvasesRef.current[layer.id];
      if (layerCanvas) {
        ctx.globalAlpha = layer.opacity;
        ctx.globalCompositeOperation = layer.blendMode === 'normal' ? 'source-over' : layer.blendMode;
        ctx.drawImage(layerCanvas, 0, 0, canvasWidth, canvasHeight);
      }
    });
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // Clean up old layers
    layers.forEach(l => {
      delete layerCanvasesRef.current[l.id];
      if (layerMasksRef.current[l.id]) {
        delete layerMasksRef.current[l.id];
      }
    });

    // Create single flattened layer
    const newId = Date.now();
    layerCanvasesRef.current[newId] = flatCanvas;

    setLayers([{
      id: newId,
      name: 'Flattened',
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal',
      hasMask: false,
      maskDisabled: false
    }]);
    setActiveLayerId(newId);

    requestComposite();
    toast.success('Layers flattened');
    setTimeout(() => saveToHistory(), 50);
  }, [layers, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Rename layer
  const renameLayer = useCallback((layerId, newName) => {
    if (!newName.trim()) return;
    setLayers(prev => prev.map(layer =>
      layer.id === layerId ? { ...layer, name: newName.trim() } : layer
    ));
    setRenamingLayerId(null);
    toast.success('Layer renamed');
  }, []);

  // ============================================
  // LAYER MASK FUNCTIONS
  // ============================================

  // Add mask to layer (white = fully visible)
  const addLayerMask = useCallback((layerId = activeLayerId) => {
    const layer = layers.find(l => l.id === layerId);
    if (!layer || layer.hasMask) {
      toast.info('Layer already has a mask');
      return;
    }

    // Create white mask (fully visible)
    const maskCanvas = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    maskCanvas.width = canvasWidth * dpr;
    maskCanvas.height = canvasHeight * dpr;
    const ctx = maskCanvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    layerMasksRef.current[layerId] = maskCanvas;

    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, hasMask: true, maskDisabled: false } : l
    ));

    requestComposite();
    toast.success('Mask added');
    setTimeout(() => saveToHistory(), 50);
  }, [activeLayerId, layers, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Delete mask from layer
  const deleteLayerMask = useCallback((layerId = activeLayerId) => {
    if (layerMasksRef.current[layerId]) {
      delete layerMasksRef.current[layerId];
    }

    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, hasMask: false, maskDisabled: false } : l
    ));
    setViewingMask(null);
    setEditingMask(false);

    requestComposite();
    toast.success('Mask deleted');
    setTimeout(() => saveToHistory(), 50);
  }, [activeLayerId, requestComposite, saveToHistory]);

  // Invert mask
  const invertLayerMask = useCallback((layerId = activeLayerId) => {
    const maskCanvas = layerMasksRef.current[layerId];
    if (!maskCanvas) return;

    const ctx = maskCanvas.getContext('2d');
    try {
      const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        data[i] = 255 - data[i];       // R
        data[i + 1] = 255 - data[i + 1]; // G
        data[i + 2] = 255 - data[i + 2]; // B
      }

      ctx.putImageData(imageData, 0, 0);
      requestComposite();
      toast.success('Mask inverted');
    } catch (err) {
      console.error("Invert mask failed (tainted?):", err);
      toast.error("Cannot invert mask: Canvas tainted");
    }
  }, [activeLayerId, canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // Toggle mask disabled
  const toggleMaskDisabled = useCallback((layerId = activeLayerId) => {
    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, maskDisabled: !l.maskDisabled } : l
    ));
    requestComposite();
  }, [activeLayerId, requestComposite]);

  // ============================================
  // TRANSFORM TOOL FUNCTIONS
  // ============================================

  // Helper to find content bounds (Trim transparent pixels)
  const getContentBounds = (ctx, width, height) => {
    try {
      // console.log(`[ContentBounds] Scanning ${width}x${height}`);
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      let minX = width, minY = height, maxX = 0, maxY = 0;
      let found = false;

      // Sample center pixel to see if we have data at all
      const centerIdx = (Math.floor(height / 2) * width + Math.floor(width / 2)) * 4;
      // console.log(`[ContentBounds] Center Pixel Alpha: ${data[centerIdx+3]}`);

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const index = (y * width + x) * 4;
          if (data[index + 3] > 0) { // Alpha > 0
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
            found = true;
          }
        }
      }

      if (!found) return null;

      return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
    } catch (err) {
      console.warn("getContentBounds failed (tainted?):", err);
      // Fallback: Return null or full bounds if critical?
      // For transform, returning null usually means "nothing to transform"
      return null;
    }
  };

  // Ref to track which layer is being transformed (set at startTransform, read at apply/cancel)
  const transformTargetLayerIdRef = useRef(null);

  // Start transform mode — uses ref for always-latest activeLayerId
  const startTransform = useCallback(() => {
    // If already transforming, apply the current transform first
    if (transformActiveRef.current) {
      console.log('[Transform] Already active, applying current transform first');
      return;
    }

    const currentLayerId = activeLayerIdRef.current || activeLayerId;
    let canvas = layerCanvasesRef.current[currentLayerId];
    if (!canvas) {
      console.warn(`[Transform] No canvas for layer ${currentLayerId}, auto-creating...`);
      const newCanvas = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;
      newCanvas.width = canvasWidth * dpr;
      newCanvas.height = canvasHeight * dpr;
      const ctx = newCanvas.getContext('2d', { willReadFrequently: true });
      ctx.scale(dpr, dpr);
      layerCanvasesRef.current[currentLayerId] = newCanvas;
      canvas = newCanvas;
    }

    // Check if we have a full-res source image (for panel mode)
    const hasFullResImage = fullResImageRef.current && (currentLayerId === 1);

    if (hasFullResImage) {
      // FULL-RES TRANSFORM: Load the original image so user can access hidden/cropped areas
      const fullImg = new Image();
      fullImg.crossOrigin = "Anonymous";
      fullImg.onload = () => {
        const dpr = window.devicePixelRatio || 1;
        const iw = fullImg.naturalWidth;
        const ih = fullImg.naturalHeight;

        // Calculate the cover-fit position (where the image was drawn)
        const coverScale = Math.max((canvasWidth * dpr) / iw, (canvasHeight * dpr) / ih);
        const drawW = iw * coverScale / dpr;
        const drawH = ih * coverScale / dpr;
        const drawX = (canvasWidth - drawW) / 2;
        const drawY = (canvasHeight - drawH) / 2;

        // Transform bounds = full image dimensions at cover position
        const logicalBounds = {
          x: drawX,
          y: drawY,
          width: drawW,
          height: drawH,
          rotation: 0,
          scaleX: 1,
          scaleY: 1
        };

        console.log('[Transform] Full-res image bounds:', logicalBounds);
        setTransformData(logicalBounds);
        transformDataRef.current = logicalBounds;

        // Create content canvas from the full-res image
        const contentCanvas = document.createElement('canvas');
        contentCanvas.width = iw;
        contentCanvas.height = ih;
        const contentCtx = contentCanvas.getContext('2d');
        contentCtx.drawImage(fullImg, 0, 0, iw, ih);

        transformInitialRef.current = {
          ...logicalBounds,
          contentCanvas: contentCanvas
        };
        transformLayerRef.current = contentCanvas;
        transformTargetLayerIdRef.current = currentLayerId;

        // Clear the layer (it will be replaced by the transform preview)
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();

        transformActiveRef.current = true;
        activeLayerIdRef.current = currentLayerId;
        setTransformActive(true);
        setCurrentTool('transform');
        requestComposite();
        toast.info('Transform mode — drag to move, handles to resize. The full image is available to reposition.');
      };
      fullImg.onerror = () => {
        console.warn('[Transform] Failed to load full-res image, falling back to layer content');
        fullResImageRef.current = null;
        startTransform(); // Retry without full-res
      };
      fullImg.src = fullResImageRef.current;
      return;
    }

    // STANDARD TRANSFORM: Extract layer content
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    console.log(`[Transform] Starting for Layer ${currentLayerId}. Canvas Size: ${canvas.width}x${canvas.height}. DPR: ${dpr}`);

    const scanWidth = Math.min(canvas.width, Math.floor(canvasWidth * dpr));
    const scanHeight = Math.min(canvas.height, Math.floor(canvasHeight * dpr));

    const bounds = getContentBounds(ctx, scanWidth, scanHeight);

    if (!bounds) {
      toast.error("Layer is empty — nothing to transform");
      return;
    }

    // Convert Physical Bounds to Logical Bounds
    const layerOffset = layerOffsetsRef.current[currentLayerId] || { x: 0, y: 0 };
    const logicalBounds = {
      x: (bounds.x / dpr) + layerOffset.x,
      y: (bounds.y / dpr) + layerOffset.y,
      width: bounds.width / dpr,
      height: bounds.height / dpr,
      rotation: 0,
      scaleX: 1,
      scaleY: 1
    };

    console.log('[Transform] Content bounds (Logical):', logicalBounds);
    setTransformData(logicalBounds);
    transformDataRef.current = logicalBounds;

    // Extract ONLY the content into a temp canvas
    const contentCanvas = document.createElement('canvas');
    contentCanvas.width = bounds.width;
    contentCanvas.height = bounds.height;
    const contentCtx = contentCanvas.getContext('2d');
    contentCtx.drawImage(
      canvas,
      bounds.x, bounds.y, bounds.width, bounds.height,
      0, 0, bounds.width, bounds.height
    );

    // Store the content canvas, original bounds, and target layer
    transformInitialRef.current = {
      ...logicalBounds,
      contentCanvas: contentCanvas
    };
    transformLayerRef.current = contentCanvas;
    transformTargetLayerIdRef.current = currentLayerId; // Lock which layer we're transforming

    // Clear the ORIGINAL region from the layer
    ctx.clearRect(logicalBounds.x, logicalBounds.y, logicalBounds.width, logicalBounds.height);

    // Update refs immediately for composite loop
    transformActiveRef.current = true;
    activeLayerIdRef.current = currentLayerId;

    setTransformActive(true);
    setCurrentTool('transform');
    requestComposite();
    toast.info('Transform mode — drag to move, handles to resize, T or Enter to apply, Esc to cancel');
  }, [activeLayerId, canvasWidth, canvasHeight, requestComposite]);


  // Apply transform — uses transformTargetLayerIdRef to always target the correct layer
  const applyTransform = useCallback(() => {
    const targetLayerId = transformTargetLayerIdRef.current;
    if (!targetLayerId) {
      console.warn('[Transform] No target layer to apply to');
      return;
    }

    const canvas = layerCanvasesRef.current[targetLayerId];
    if (!canvas) {
      console.error('[Transform] Target layer canvas missing:', targetLayerId);
      return;
    }

    const contentCanvas = transformLayerRef.current;
    if (!contentCanvas) {
      // Already applied or cancelled — silently exit
      return;
    }

    const td = transformDataRef.current;
    if (!td) return;

    console.log('[Transform] Applying to layer:', targetLayerId, 'Data:', td);

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    const layerOffset = layerOffsetsRef.current[targetLayerId] || { x: 0, y: 0 };
    const finalX = td.x - layerOffset.x;
    const finalY = td.y - layerOffset.y;
    const centerX = finalX + td.width / 2;
    const centerY = finalY + td.height / 2;

    ctx.translate(centerX, centerY);
    ctx.rotate((td.rotation * Math.PI) / 180);
    ctx.translate(-centerX, -centerY);

    ctx.drawImage(
      contentCanvas,
      0, 0, contentCanvas.width, contentCanvas.height,
      finalX, finalY, td.width, td.height
    );
    ctx.restore();

    // Clean up all transform state
    transformActiveRef.current = false;
    setTransformActive(false);
    setTransformDragging(null);
    transformInitialRef.current = null;
    transformLayerRef.current = null;
    transformTargetLayerIdRef.current = null;

    requestComposite();
    toast.success('Transform applied');
    setTimeout(() => saveToHistory(), 50);
  }, [requestComposite, saveToHistory]);

  // Cancel transform — uses transformTargetLayerIdRef to restore to the correct layer
  const cancelTransform = useCallback(() => {
    if (!transformActiveRef.current) return;

    const targetLayerId = transformTargetLayerIdRef.current;

    // Restore the content at original position
    if (transformLayerRef.current && transformInitialRef.current && targetLayerId) {
      const canvas = layerCanvasesRef.current[targetLayerId];
      if (canvas) {
        const ctx = canvas.getContext('2d');
        const initial = transformInitialRef.current;
        const contentCanvas = transformLayerRef.current;

        const layerOffset = layerOffsetsRef.current[targetLayerId] || { x: 0, y: 0 };
        const initialX = initial.x - layerOffset.x;
        const initialY = initial.y - layerOffset.y;

        ctx.drawImage(
          contentCanvas,
          0, 0, contentCanvas.width, contentCanvas.height,
          initialX, initialY, initial.width, initial.height
        );
      }
    }

    // Clean up all transform state
    transformActiveRef.current = false;
    setTransformActive(false);
    setTransformDragging(null);
    transformInitialRef.current = null;
    transformLayerRef.current = null;
    transformTargetLayerIdRef.current = null;

    requestComposite();
    toast.info('Transform cancelled');
  }, [requestComposite]);

  // Transform drag handlers
  const handleTransformDragStart = useCallback((e, type) => {
    e.stopPropagation();
    e.preventDefault();

    const startX = e.clientX || e.touches?.[0]?.clientX;
    const startY = e.clientY || e.touches?.[0]?.clientY;

    if (!startX || !startY) return;

    // Cache start state
    transformDragStartRef.current = {
      x: startX,
      y: startY,
      data: { ...transformData }
    };
    // Create Start Matrix (The "Before" state)
    const matrix = createTransformMatrix(transformData);
    transformStartMatrixRef.current = matrix;

    // Calculate Pivot (Opposite Handle)
    const pivot = getPivot(
      type === 'move' || type === 'rotation' ? 'center' : type,
      transformData.x,
      transformData.y,
      transformData.width,
      transformData.height
    );
    transformPivotRef.current = pivot;

    setTransformDragging(type);
  }, [transformData]);


  const handleTransformDragMove = useCallback((e) => {
    if (!transformDragging || !transformDragStartRef.current) return;

    // 0. Get Screen Mouse and Start State
    const mouseX = (e.clientX || e.touches?.[0]?.clientX);
    const mouseY = (e.clientY || e.touches?.[0]?.clientY);
    if (!mouseX || !mouseY) return;

    // Shift = Constrain Proportions
    // Alt = Resize from Center
    const isShift = e.shiftKey;
    const isAlt = e.altKey;

    const startState = transformDragStartRef.current;

    // Calculate global delta in LOGICAL pixels
    const globalDx = (mouseX - startState.x) / zoom;
    const globalDy = (mouseY - startState.y) / zoom;

    // --- MOVE LOGIC ---
    if (transformDragging === 'move') {
      const curData = startState.data;
      setTransformData({
        ...curData,
        x: curData.x + globalDx,
        y: curData.y + globalDy
      });
      needsCompositeRef.current = true;
      return;
    }

    // --- ROTATION LOGIC ---
    if (transformDragging === 'rotation') {
      // Calculate angle relative to center
      const centerX = startState.data.x + startState.data.width / 2;
      const centerY = startState.data.y + startState.data.height / 2;

      const rect = containerRef.current.getBoundingClientRect();
      const centerScreenX = rect.left + pan.x + (centerX * zoom);
      const centerScreenY = rect.top + pan.y + (centerY * zoom);

      const angleStart = Math.atan2(startState.y - centerScreenY, startState.x - centerScreenX);
      const angleEnd = Math.atan2(mouseY - centerScreenY, mouseX - centerScreenX);

      const angleDelta = (angleEnd - angleStart) * (180 / Math.PI);

      setTransformData({
        ...startState.data,
        rotation: (startState.data.rotation + angleDelta) % 360
      });
      needsCompositeRef.current = true;
      return;
    }

    // --- RESIZE LOGIC (Matrix Based) ---
    // 1. Project Global Delta into Local Space (Un-rotated)
    const rad = (startState.data.rotation * Math.PI) / 180;
    const cos = Math.cos(-rad);
    const sin = Math.sin(-rad);

    // Local Delta
    const localDx = globalDx * cos - globalDy * sin;
    const localDy = globalDx * sin + globalDy * cos;

    let newData = { ...startState.data };
    let dW = 0;
    let dH = 0;

    // Apply deltas based on handle
    if (transformDragging.includes('e')) dW += localDx;
    if (transformDragging.includes('w')) dW -= localDx;
    if (transformDragging.includes('s')) dH += localDy;
    if (transformDragging.includes('n')) dH -= localDy;

    // Aspect Ratio Lock (Shift)
    if (isShift) {
      const aspect = startState.data.width / startState.data.height;
      // Dominant axis determines the other
      if (Math.abs(dW) > Math.abs(dH)) {
        // Width is dominant
        if (transformDragging.includes('n') || transformDragging.includes('s')) {
          dH = dW / aspect;
        }
      } else {
        // Height dominant
        if (transformDragging.includes('e') || transformDragging.includes('w')) {
          dW = dH * aspect;
        }
      }

      if (transformDragging === 'e' || transformDragging === 'w') dH = dW / aspect;
      if (transformDragging === 'n' || transformDragging === 's') dW = dH * aspect;
    }

    // Center Resize (Alt) -> Double the growth
    if (isAlt) {
      dW *= 2;
      dH *= 2;
    }

    // Constrain min size
    const newW = Math.max(10, newData.width + dW);
    const newH = Math.max(10, newData.height + dH);

    // Re-calculate effective dW/dH
    const effectiveDW = newW - newData.width;
    const effectiveDH = newH - newData.height;

    newData.width = newW;
    newData.height = newH;

    // 3. Compensate Position (Pivot Logic)
    let localShiftX = 0;
    let localShiftY = 0;

    if (isAlt) {
      // Grow outwards from center
      localShiftX = -effectiveDW / 2;
      localShiftY = -effectiveDH / 2;
    } else {
      // Standard Corner/Edge resizing
      if (transformDragging.includes('w')) localShiftX -= effectiveDW;
      if (transformDragging.includes('n')) localShiftY -= effectiveDH;

      if (isShift) {
        // Center expansion for Single-Axis + Shift
        if (transformDragging === 'e' || transformDragging === 'w') {
          localShiftY -= effectiveDH / 2;
        }
        if (transformDragging === 'n' || transformDragging === 's') {
          localShiftX -= effectiveDW / 2;
        }
      }
    }

    // Rotate this local shift back to Global Space
    const fCos = Math.cos(rad);
    const fSin = Math.sin(rad);

    const globalShiftX = localShiftX * fCos - localShiftY * fSin;
    const globalShiftY = localShiftX * fSin + localShiftY * fCos;

    newData.x += globalShiftX;
    newData.y += globalShiftY;

    setTransformData(newData);
    transformDataRef.current = newData;
    needsCompositeRef.current = true;
  }, [transformDragging, zoom, pan]);

  const handleTransformDragEnd = useCallback(() => {
    setTransformDragging(null);
  }, []);

  // Viewport Interaction Handlers
  // Native Wheel Listener for robust preventDefault (Fixes Browser Zoom conflict)
  // Uses refs + direct DOM for pointer-centric zoom (zero React re-renders)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e) => {
      // Always prevent default to stop browser zoom/scroll
      e.preventDefault();

      // ZOOM — pointer-centric for ALL scroll (both regular and Ctrl+scroll)
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const curPan = panRef.current;
      const curZoom = zoomRef.current;
      const rotation = (viewRotationRef.current * Math.PI) / 180;
      const inverseRotation = -rotation;
      const screenX = mouseX - curPan.x;
      const screenY = mouseY - curPan.y;
      const invCos = Math.cos(inverseRotation);
      const invSin = Math.sin(inverseRotation);
      const worldX = (screenX * invCos - screenY * invSin) / curZoom;
      const worldY = (screenX * invSin + screenY * invCos) / curZoom;

      const zoomDelta = -e.deltaY * 0.001;
      const newZoom = Math.min(64, Math.max(0.05, curZoom * (1 + zoomDelta)));

      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      const rotatedX = (worldX * newZoom) * cos - (worldY * newZoom) * sin;
      const rotatedY = (worldX * newZoom) * sin + (worldY * newZoom) * cos;
      const newPanX = mouseX - rotatedX;
      const newPanY = mouseY - rotatedY;

      const newPan = { x: newPanX, y: newPanY };
      // Direct DOM update (no re-render)
      updateZoomDirect(newZoom);
      updatePanDirect(newPan);

      // Debounced state sync
      clearTimeout(wheelSyncTimerRef.current);
      wheelSyncTimerRef.current = setTimeout(syncViewState, 150);
    };

    container.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', onWheel);
      clearTimeout(wheelSyncTimerRef.current);
    };
  }, [updatePanDirect, updateZoomDirect, syncViewState]);

  // Center canvas on mount/resize logic
  useEffect(() => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      // Center initially
      setPan({
        x: (clientWidth - canvasWidth * zoom) / 2,
        y: (clientHeight - canvasHeight * zoom) / 2
      });
    }
  }, [canvasWidth, canvasHeight]); // Only reset on dimensions change, not zoom (to avoid jump)

  // Global mouse listeners for transform drag
  useEffect(() => {
    if (transformDragging) {
      const handleMouseMove = (e) => handleTransformDragMove(e);
      const handleMouseUp = () => handleTransformDragEnd();

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [transformDragging, handleTransformDragMove, handleTransformDragEnd]);

  // Hidden file input ref for image import
  const importImageInputRef = useRef(null);

  // Import guide image as a new layer
  const handleImportGuideImage = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create new layer for the imported guide
        const newId = Date.now();
        const newLayer = {
          id: newId,
          name: file.name, // Use actual filename
          visible: true,
          opacity: 1, // Default 100% opacity
          locked: false,
          blendMode: 'normal',
          isGuide: false
        };

        // Create canvas for the layer
        const newCanvas = document.createElement('canvas');
        const dpr = window.devicePixelRatio || 1;
        newCanvas.width = canvasWidth * dpr;
        newCanvas.height = canvasHeight * dpr;
        const ctx = newCanvas.getContext('2d');
        ctx.scale(dpr, dpr);

        // Calculate scaling to fit image in canvas while preserving aspect ratio
        const scale = Math.min(
          canvasWidth / img.width,
          canvasHeight / img.height
        );
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const offsetX = (canvasWidth - scaledWidth) / 2;
        const offsetY = (canvasHeight - scaledHeight) / 2;

        // Draw the image centered on the canvas
        ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

        layerCanvasesRef.current[newId] = newCanvas;

        // Add layer at the top (last position)
        setLayers(prev => [...prev, newLayer]);
        setActiveLayerId(newId);
        activeLayerIdRef.current = newId; // Sync ref immediately for transform

        requestComposite();
        toast.success(`Image imported: ${file.name}`);
        setTimeout(() => saveToHistory(), 50);
      };

      img.onerror = () => {
        toast.error('Failed to load image');
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);

    // Reset input so same file can be imported again
    event.target.value = '';
  }, [canvasWidth, canvasHeight, requestComposite, saveToHistory]);

  // AI functionality
  const openAIEditor = () => {
    try {
      // Fetch fresh credits from server when opening AI editor
      fetchCredits();

      performComposite();
      const snapshot = mainCanvasRef.current.toDataURL();
      setAiCanvasSnapshot(snapshot);
      setInpaintResult(null); // Reset inpaint result

      // Remove unused state if it conflicts, or keep it if used by other parts
      // But we rely on maskCanvasRef for the actual drawing

      // Clear mask canvas if it exists (for fresh start)
      if (maskCanvasRef.current) {
        const ctx = maskCanvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      }

      setShowAIEditor(true);
      toast.success('AI Editor opened');
    } catch (error) {
      console.error("Failed to open AI Editor:", error);
      if (error.name === 'SecurityError' || error.message.includes('Tainted')) {
        toast.error("Cannot open AI Editor: Canvas is tainted by external images.");
      } else {
        toast.error("Failed to open AI Editor. See console.");
      }
    }
  };

  const generateAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    if (credits < 1) {
      toast.error('Insufficient credits');
      return;
    }

    setIsGenerating(true);
    setGenerationElapsed(0);
    generationTimerRef.current = setInterval(() => setGenerationElapsed(prev => prev + 1), 1000);

    try {
      let endpoint = `${API}/ai/generate`;
      let requestData = {
        prompt: aiPrompt,
        negative_prompt: aiNegativePrompt,
        width: Math.round(canvasWidth),
        height: Math.round(canvasHeight),
        num_images: 1,
        model_engine: aiModel
      };

      if (aiMode === 'sketch2ai') {
        if (aiModel === 'caravaggio' || aiModel === 'cavaggio') {
          // ═══ CARAVAGGIO UNIFIED SINGLE-PASS PIPELINE ═══
          try {
            toast.info('🎨 Generating Caravaggio illustration (SAM 2 + Flux)...');
            const genResponse = await axios.post(`${API}/ai/color-guided-generate`, {
              sketch_image: aiCanvasSnapshot,
              prompt: aiPrompt,
              style: 'cinematic',
              width: Math.round(canvasWidth),
              height: Math.round(canvasHeight),
              seed: -1,
            }, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 600000 // 10 min for unified VLM + Flux generation
            });

            if (genResponse.data.success && genResponse.data.image_url) {
              // Only final image is displayed in the frontend
              setAiGeneratedImage(genResponse.data.image_url);
              setCredits(prev => prev - (genResponse.data.credits_used || 1));
              toast.success(`🎨 Caravaggio unified generation complete! (${genResponse.data.generation_time}s)`);
            } else {
              throw new Error(genResponse.data.detail || genResponse.data.error || 'Unified Caravaggio generation failed');
            }
          } catch (cavError) {
            console.warn('[CARAVAGGIO] Unified generation failed, falling back to Gemini:', cavError);
            toast.warning('⚠️ Caravaggio (RunPod) is currently busy or out of capacity. Falling back to Gemini to complete your request instantly...');

            // ═══ GEMINI FALLBACK PATH ═══
            requestData.reference_image = aiCanvasSnapshot;
            requestData.strength = aiStrength;
            
            const response = await axios.post(endpoint, requestData, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 600000
            });
            
            if (response.data.success && response.data.data.images) {
              const imageUrl = response.data.data.images[0].url;
              setAiGeneratedImage(imageUrl);
              setCredits(response.data.credits_remaining);
              toast.success('🎨 Image generated successfully with Gemini fallback!');
            } else {
              throw new Error(response.data.detail || 'Gemini fallback generation failed');
            }
          }
        } else {
          // ═══ GEMINI SKETCH PATH (unchanged) ═══
          requestData.reference_image = aiCanvasSnapshot;
          requestData.strength = aiStrength;
          toast.info('AI generating from your sketch...');
          
          const response = await axios.post(endpoint, requestData, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 600000
          });
          
          if (response.data.success && response.data.data.images) {
            const imageUrl = response.data.data.images[0].url;
            setAiGeneratedImage(imageUrl);
            setCredits(response.data.credits_remaining);
            if (response.data.fallback_used) {
              toast.warning(`Caravaggio failed, generated with Gemini instead. Reason: ${response.data.fallback_reason || 'Unknown'}`);
            } else {
              toast.success(`AI ${response.data.type || 'generation'} complete!`);
            }
          } else {
            toast.error("Generation failed: No image returned");
          }
        }
      } else if (aiMode === 'inpaint') {
        // Get mask from the mask canvas ref
        if (!maskCanvasRef.current) {
          toast.error('Please draw a mask first');
          setIsGenerating(false);
          return;
        }

        // Convert red mask to black/white mask for the API
        const maskCanvas = maskCanvasRef.current;
        const tempMask = document.createElement('canvas');
        tempMask.width = canvasWidth;
        tempMask.height = canvasHeight;
        const tempCtx = tempMask.getContext('2d');

        // Fill with black (areas to keep)
        tempCtx.fillStyle = '#000000';
        tempCtx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Get the mask image data
        const maskCtx = maskCanvas.getContext('2d');
        const maskData = maskCtx.getImageData(0, 0, canvasWidth, canvasHeight);
        const tempData = tempCtx.getImageData(0, 0, canvasWidth, canvasHeight);

        // Convert red areas to white (areas to inpaint)
        for (let i = 0; i < maskData.data.length; i += 4) {
          if (maskData.data[i] > 100 && maskData.data[i + 3] > 0) {
            // Red area - make white
            tempData.data[i] = 255;
            tempData.data[i + 1] = 255;
            tempData.data[i + 2] = 255;
            tempData.data[i + 3] = 255;
          }
        }

        tempCtx.putImageData(tempData, 0, 0);
        const maskUrl = tempMask.toDataURL();

        endpoint = `${API}/ai/inpaint`;
        requestData = {
          prompt: aiPrompt,
          negative_prompt: aiNegativePrompt,
          image_url: aiCanvasSnapshot,
          mask_url: maskUrl
        };
        toast.info('AI inpainting masked area...');

        const response = await axios.post(endpoint, requestData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 600000
        });

        if (response.data.success && response.data.data.images) {
          const imageUrl = response.data.data.images[0].url;
          setAiGeneratedImage(imageUrl);
          setInpaintResult(imageUrl);
          setCredits(response.data.credits_remaining);
          toast.success(`AI ${response.data.type || 'generation'} complete!`);
        } else {
          toast.error("Generation failed: No image returned");
        }
      } else {
        // Text-to-image mode
        toast.info('Generating image from text...');

        const response = await axios.post(endpoint, requestData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 600000
        });

        if (response.data.success && response.data.data.images) {
          const imageUrl = response.data.data.images[0].url;
          setAiGeneratedImage(imageUrl);
          setCredits(response.data.credits_remaining);
          if (response.data.fallback_used) {
            toast.warning(`Caravaggio failed, generated with Gemini instead. Reason: ${response.data.fallback_reason || 'Unknown'}`);
          } else {
            toast.success(`AI ${response.data.type || 'generation'} complete!`);
          }
        } else {
          toast.error("Generation failed: No image returned");
        }
      }
    } catch (error) {
      console.error('Error generating:', error);
      let msg = 'Generation failed';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg).join(', ');
      else if (detail) msg = JSON.stringify(detail);
      toast.error(msg);
    } finally {
      setIsGenerating(false);
      clearInterval(generationTimerRef.current);
      generationTimerRef.current = null;
    }
  };

  const appendAIToCanvas = () => {
    if (!aiGeneratedImage) {
      toast.error('No AI image to append');
      return;
    }

    const newId = Date.now();
    const newLayer = {
      id: newId,
      name: `AI Layer ${layers.length + 1}`,
      visible: true,
      opacity: 1,
      locked: false,
      blendMode: 'normal'
    };

    const newCanvas = document.createElement('canvas');
    newCanvas.width = canvasWidth;
    newCanvas.height = canvasHeight;
    const newCtx = newCanvas.getContext('2d');

    const loadImage = (imageUrl, useFetch = false) => {
      if (useFetch) {
        // Fetch the image to bypass CORS
        fetch(imageUrl)
          .then(response => {
            if (!response.ok) throw new Error('Fetch failed');
            return response.blob();
          })
          .then(blob => {
            const objectUrl = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
              newCtx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
              URL.revokeObjectURL(objectUrl);
              finalizeLayer();
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              toast.error('Failed to load AI image');
            };
            img.src = objectUrl;
          })
          .catch(err => {
            console.error('Fetch error:', err);
            toast.error('Could not load AI image');
          });
      } else {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          newCtx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          finalizeLayer();
        };
        img.onerror = () => {
          console.log('Direct load failed, trying fetch...');
          loadImage(imageUrl, true);
        };
        img.src = imageUrl;
      }
    };

    const finalizeLayer = () => {
      // Store the canvas reference first
      layerCanvasesRef.current[newId] = newCanvas;

      // Update layers state
      setLayers(prevLayers => {
        const updatedLayers = [...prevLayers, newLayer];
        // Update the ref immediately for composite
        layersRef.current = updatedLayers;
        return updatedLayers;
      });

      setActiveLayerId(newId);

      // Force composite multiple times to ensure it renders
      setTimeout(() => {
        needsCompositeRef.current = true;
        performComposite();
      }, 50);

      setTimeout(() => {
        needsCompositeRef.current = true;
        performComposite();
        saveToHistory();
      }, 200);

      // Close AI editor
      setShowAIEditor(false);
      setAiGeneratedImage(null);
      setAiPrompt('');

      toast.success('AI layer added to canvas!');
    };

    // Start loading the image (Use fetch=true to avoid CORS tainting)
    loadImage(aiGeneratedImage, true);
  };


  // NOTE: Layer hydration from initialLayers is handled in the main canvas
  // initialization useEffect above (line ~1382). Do NOT add a second hydration
  // block here — it causes layers to be duplicated on editor reopen.

  const downloadImage = () => {
    performComposite();
    const link = document.createElement('a');
    link.download = `${projectName}.png`;
    link.href = mainCanvasRef.current.toDataURL();
    link.click();
    toast.success('Image downloaded!');
  };

  // ── Magic Ink ─────────────────────────────────────────────────────

  const applyMagicInk = useCallback(async () => {
    if (magicInkProcessing) return;
    if (!mainCanvasRef.current || canvasWidth === 0) {
      toast.error('Canvas not ready');
      return;
    }

    setMagicInkProcessing(true);
    const toastId = toast.loading('✨ Inking with Caravaggio...', { duration: 420000 });

    try {
      // Composite all visible layers into mainCanvas
      if (performCompositeRef.current) performCompositeRef.current();

      // Get the canvas as base64 PNG
      const dataUrl = mainCanvasRef.current.toDataURL('image/png');
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.dismiss(toastId);
        toast.error('Please log in to use Magic Ink');
        setMagicInkProcessing(false);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/ai/magic-ink-caravaggio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sketch_image: dataUrl,
          style: magicInkStyle,
          strength: magicInkStrength
        }),
        signal: AbortSignal.timeout(420000) // 7 min timeout for RunPod cold starts
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to process Magic Ink');
      }

      // New Caravaggio endpoint returns: { success, image_url, engine, credits_remaining }
      if (!result.success || !result.image_url) {
        throw new Error('No inked image returned');
      }

      const inkedImageUrl = result.image_url;
      const engineUsed = result.engine || 'caravaggio';

      // Load the inked image — use fetch for Cloudinary URLs to avoid CORS tainting
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        // Try fetch-first for Cloudinary URLs
        if (inkedImageUrl.startsWith('http')) {
          fetch(inkedImageUrl)
            .then(r => r.blob())
            .then(blob => {
              const objUrl = URL.createObjectURL(blob);
              img.onload = () => { URL.revokeObjectURL(objUrl); resolve(); };
              img.onerror = () => { URL.revokeObjectURL(objUrl); reject(new Error('Failed to load inked image')); };
              img.src = objUrl;
            })
            .catch(() => {
              // Fallback to direct load
              img.onload = resolve;
              img.onerror = () => reject(new Error('Failed to load inked image'));
              img.src = inkedImageUrl;
            });
        } else {
          // data: URL — load directly
          img.onload = resolve;
          img.onerror = () => reject(new Error('Failed to load inked image'));
          img.src = inkedImageUrl;
        }
      });

      // Add new layer for the inked result
      if (layers.length >= MAX_LAYERS) {
        toast.dismiss(toastId);
        toast.error(`Layer limit reached (${MAX_LAYERS}). Delete a layer first.`);
        setMagicInkProcessing(false);
        return;
      }

      const newId = Date.now();
      const dpr = window.devicePixelRatio || 1;
      const newCanvas = document.createElement('canvas');
      newCanvas.width = canvasWidth * dpr;
      newCanvas.height = canvasHeight * dpr;
      const ctx = newCanvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const newLayer = {
        id: newId,
        type: 'raster',
        name: `Magic Ink (${magicInkStyle})`,
        visible: true,
        opacity: 1,
        locked: false,
        blendMode: 'normal'
      };

      // Register the canvas in the layer canvases ref
      layerCanvasesRef.current[newId] = newCanvas;

      setLayers(prev => [...prev, newLayer]);
      setActiveLayerId(newId);
      requestComposite();

      toast.dismiss(toastId);
      if (engineUsed === 'gemini') {
        toast.success(`✨ Magic Ink applied via Gemini fallback! (${result.credits_remaining} credits left)`);
      } else {
        toast.success(`✨ Magic Ink applied with Caravaggio! (${result.credits_remaining} credits left)`);
      }
      
      // Save to history for undo
      setTimeout(() => saveToHistory(), 50);

    } catch (err) {
      toast.dismiss(toastId);
      if (err.message?.includes('402') || err.message?.includes('Insufficient credits')) {
        toast.error('Not enough credits for Magic Ink. Purchase more in the store.');
      } else {
        toast.error(`Magic Ink failed: ${err.message}`);
      }
      console.error('Magic Ink error:', err);
    } finally {
      setMagicInkProcessing(false);
    }
  }, [magicInkProcessing, magicInkStyle, magicInkStrength, canvasWidth, canvasHeight, layers.length, saveToHistory, requestComposite]);

  // ── Timelapse Recording ──────────────────────────────────────────

  // Ref-based frame capture function — callable from handlePointerUp
  const captureRecordingFrameRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const startRecording = useCallback(() => {
    const mainCanvas = mainCanvasRef.current;
    if (!mainCanvas) {
      toast.error('Canvas not ready');
      return;
    }

    // Clear any previous recording frames from IndexedDB
    clearRecordingFrames();
    recordedChunksRef.current = []; // Not used for storage anymore, just frame count
    setIsRecording(true);
    recordingDurationRef.current = 0;

    // Create a SMALL offscreen canvas for fast capture (640px wide max)
    const captureCanvas = document.createElement('canvas');
    const aspect = mainCanvas.height / mainCanvas.width;
    captureCanvas.width = 640;
    captureCanvas.height = Math.round(640 * aspect);
    const captureCtx = captureCanvas.getContext('2d');
    recordingCanvasRef.current = captureCanvas;

    // Duration timer — uses DOM refs instead of React state (ZERO re-renders)
    const startTime = Date.now();
    recordingTimerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startTime) / 1000);
      recordingDurationRef.current = secs;
      const mm = Math.floor(secs / 60).toString().padStart(2, '0');
      const ss = (secs % 60).toString().padStart(2, '0');
      const text = `${mm}:${ss}`;
      // Direct DOM updates — no React re-render needed
      if (recTimerDisplayRef.current) recTimerDisplayRef.current.textContent = text;
      if (recTimerDisplay2Ref.current) recTimerDisplay2Ref.current.textContent = `REC ${text}`;
    }, 1000);

    // Frame counter for IndexedDB keys
    let frameCounter = 0;
    let lastCaptureTime = 0;

    // Core frame capture — composites then captures mainCanvas
    const doCapture = () => {
      try {
        if (!recordingTimerRef.current) return;

        // Throttle: max 1 capture per 100ms (light throttle for rapid strokes)
        const now = Date.now();
        if (now - lastCaptureTime < 100) return;
        lastCaptureTime = now;

        const src = mainCanvasRef.current;
        if (!src) return;

        // MUST composite before capture — doCapture runs via setTimeout(0) which fires
        // BEFORE requestAnimationFrame, so mainCanvas might not have the latest stroke yet.
        if (performCompositeRef.current) performCompositeRef.current();

        // Downscale to small canvas
        captureCtx.fillStyle = '#ffffff';
        captureCtx.fillRect(0, 0, captureCanvas.width, captureCanvas.height);
        captureCtx.drawImage(src, 0, 0, captureCanvas.width, captureCanvas.height);

        // Save as Blob (~30% smaller, no base64 string overhead)
        captureCanvas.toBlob((blob) => {
          if (blob) {
            const idx = frameCounter++;
            saveRecordingFrame(idx, blob);
          }
        }, 'image/jpeg', 0.8);
      } catch (err) {
        console.error('doCapture failed:', err);
      }
    };

    // Store in ref for handlePointerUp
    captureRecordingFrameRef.current = doCapture;

    // Regular capture interval — captures every 500ms for smooth timelapse
    // (~120 frames per minute). Always captures regardless of drawing state
    // so idle time is also recorded naturally.
    const intervalId = setInterval(() => {
      if (!recordingTimerRef.current) {
        clearInterval(intervalId);
        return;
      }
      doCapture();
    }, 500);
    // Store interval ID so stopRecording can clear it
    recordingIntervalRef.current = intervalId;

    // Capture initial frame
    doCapture();

    toast.success('🔴 Recording started — draw something!');
  }, [projectName]);

  const stopRecording = useCallback(async () => {
    try {
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
      captureRecordingFrameRef.current = null;
      // Clear the capture interval
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }

      // Show processing overlay IMMEDIATELY so user sees feedback
      setProcessingTimelapse({ current: 0, total: 1 }); // Placeholder until we get real count

      // Small delay to let any pending toBlob() writes finish
      await new Promise(r => setTimeout(r, 500));

      // Get frame count from IndexedDB
      const totalFrames = await getRecordingFrameCount();
      if (totalFrames === 0) {
        toast.error('No frames captured');
        setProcessingTimelapse(null);
        return;
      }

      toast.info(`Processing ${totalFrames} frames into timelapse...`);
      setProcessingTimelapse({ current: 0, total: totalFrames });

      // Re-encode frames at 30fps to produce true timelapse
      const encCanvas = document.createElement('canvas');
      const capCanvas = recordingCanvasRef.current;
      encCanvas.width = capCanvas ? capCanvas.width : 640;
      encCanvas.height = capCanvas ? capCanvas.height : 480;
      const encCtx = encCanvas.getContext('2d');

      const stream = encCanvas.captureStream(0);
      const track = stream.getVideoTracks()[0];

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
          ? 'video/webm;codecs=vp8'
          : 'video/webm';

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 4000000
      });

      const videoChunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) videoChunks.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(videoChunks, { type: mimeType });

        // Auto-download immediately so user never loses their timelapse
        try {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${projectName || 'timelapse'}_recording.webm`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (e) { /* download failed, user can still use modal */ }

        setRecordingBlob(blob);
        setShowRecordingDownload(true);
        setProcessingTimelapse(null);
        const secs = recordingDurationRef.current;
        setTimeout(() => {
          if (recTimerDisplay3Ref.current) {
            recTimerDisplay3Ref.current.textContent = `Duration: ${Math.floor(secs / 60)}m ${secs % 60}s`;
          }
        }, 100);
        clearRecordingFrames();
        recordedChunksRef.current = [];
        toast.success('Timelapse saved & downloaded!');
      };

      recorder.start(100);

      // Replay frames from IndexedDB at ~30fps with timeout safety
      let frameIdx = 0;
      let successfulFrames = 0;

      const encodeNextFrame = async () => {
        if (frameIdx >= totalFrames) {
          // Wait a bit for the last frame to be encoded, then stop
          setTimeout(() => recorder.stop(), 300);
          return;
        }

        try {
          const blob = await loadRecordingFrame(frameIdx);
          if (!blob) {
            // Frame missing — skip it
            frameIdx++;
            setProcessingTimelapse({ current: frameIdx, total: totalFrames });
            setTimeout(encodeNextFrame, 0);
            return;
          }

          const img = new Image();
          const objUrl = URL.createObjectURL(blob);

          // Timeout safety — if img.onload doesn't fire within 3 seconds, skip frame
          const timeoutId = setTimeout(() => {
            URL.revokeObjectURL(objUrl);
            console.warn(`[Recording] Frame ${frameIdx} timed out, skipping`);
            frameIdx++;
            setProcessingTimelapse({ current: frameIdx, total: totalFrames });
            encodeNextFrame();
          }, 3000);

          img.onload = () => {
            clearTimeout(timeoutId);
            encCtx.fillStyle = '#ffffff';
            encCtx.fillRect(0, 0, encCanvas.width, encCanvas.height);
            encCtx.drawImage(img, 0, 0, encCanvas.width, encCanvas.height);
            if (track.requestFrame) track.requestFrame();
            URL.revokeObjectURL(objUrl); // Free memory immediately
            successfulFrames++;

            frameIdx++;
            setProcessingTimelapse({ current: frameIdx, total: totalFrames });
            setTimeout(encodeNextFrame, 33);
          };
          img.onerror = () => {
            clearTimeout(timeoutId);
            URL.revokeObjectURL(objUrl);
            console.warn(`[Recording] Frame ${frameIdx} failed to load, skipping`);
            frameIdx++;
            setProcessingTimelapse({ current: frameIdx, total: totalFrames });
            setTimeout(encodeNextFrame, 0);
          };
          img.src = objUrl;
        } catch (err) {
          console.warn(`[Recording] Error loading frame ${frameIdx}:`, err);
          frameIdx++;
          setProcessingTimelapse({ current: frameIdx, total: totalFrames });
          setTimeout(encodeNextFrame, 0);
        }
      };
      encodeNextFrame();
    } catch (err) {
      console.error('stopRecording failed:', err);
      setProcessingTimelapse(null);
      toast.error('Failed to process timelapse: ' + err.message);
    }
  }, []);

  const downloadRecording = useCallback(() => {
    if (!recordingBlob) return;
    const url = URL.createObjectURL(recordingBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectName || 'timelapse'}_recording.webm`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Timelapse downloaded!');
  }, [recordingBlob, projectName]);

  // Handle save for Panel Mode (embedded)
  const handlePanelSave = () => {
    // Generate composite image for preview
    performComposite();
    const preview = mainCanvasRef.current.toDataURL('image/png');

    // Collect layer data
    const layerData = layers.map(layer => ({
      id: layer.id,
      name: layer.name,
      visible: layer.visible,
      opacity: layer.opacity,
      locked: layer.locked,
      blendMode: layer.blendMode,
      hasMask: layer.hasMask,
      maskDisabled: layer.maskDisabled,
      // Get canvas data if exists
      imageData: layerCanvasesRef.current[layer.id]
        ? layerCanvasesRef.current[layer.id].toDataURL('image/png')
        : null
    }));

    // Pass data back to parent
    if (onSavePanel) {
      onSavePanel({
        preview,
        layers: layerData,
        width: canvasWidth,
        height: canvasHeight,
        fullImage: preview
      });
      toast.success('Panel updated');
    }
  };

  // Save project to backend
  const saveProject = async () => {
    if (!saveTitle.trim()) {
      toast.error('Please enter a title for your project');
      return;
    }

    setIsSaving(true);

    try {
      // Generate composite image for thumbnail
      performComposite();
      const thumbnail = mainCanvasRef.current.toDataURL('image/jpeg', 0.8);

      // Collect layer data for potential reload
      const layerData = layers.map(layer => ({
        id: layer.id,
        name: layer.name,
        visible: layer.visible,
        opacity: layer.opacity,
        locked: layer.locked,
        blendMode: layer.blendMode,
        // Get canvas data if exists
        imageData: layerCanvasesRef.current[layer.id]
          ? layerCanvasesRef.current[layer.id].toDataURL('image/png')
          : null
      }));

      const response = await axios.post(`${API}/projects/save`, {
        title: saveTitle,
        description: saveDescription,
        thumbnail: thumbnail,
        canvas_data: {
          layers: layerData,
          width: canvasWidth,
          height: canvasHeight,
          projectName: projectName
        },
        width: canvasWidth,
        height: canvasHeight,
        is_public: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Project saved successfully!');
        setShowSaveModal(false);
        setSaveTitle('');
        setSaveDescription('');
      }
    } catch (err) {
      console.error('Save error:', err);
      let msg = 'Failed to save project';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg).join(', ');
      else if (detail) msg = JSON.stringify(detail);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Load project from backend
  const loadProject = useCallback(async (projectId) => {
    if (!token || !projectId) return;

    setLoadingProject(true);

    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const project = response.data.project;

      if (!project || !project.canvas_data) {
        toast.error('Project data not found or corrupted');
        setLoadingProject(false);
        return;
      }

      const { canvas_data, title } = project;

      // Set project name
      setProjectName(title || 'Untitled Project');
      setLoadedProjectId(projectId);

      // SAFETY NET: Check for V2 Blueprint and redirect
      if (canvas_data.version === 'v2_blueprint') {
        toast.info("Opening in Blueprint Editor...");
        navigate('/comica', { state: { project: project } });
        return;
      }

      // SAFETY NET: Check for V3 Multi-page Blueprint and redirect
      if (canvas_data.version === 'v3_multipage' || canvas_data.pages) {
        toast.info("Opening in Blueprint Editor...");
        navigate('/comica', { state: { project: project } });
        return;
      }

      // Legacy loading logic
      const actualWidth = canvas_data.width || canvasWidth;
      const actualHeight = canvas_data.height || canvasHeight;
      if (canvas_data.width && canvas_data.height) {
        setCanvasWidth(actualWidth);
        setCanvasHeight(actualHeight);
      }
      // Restore layers from canvas_data
      if (canvas_data.layers && canvas_data.layers.length > 0) {
        const restoredLayers = canvas_data.layers.map((layerData, index) => ({
          id: layerData.id || index + 1,
          name: layerData.name || `Layer ${index + 1}`,
          visible: layerData.visible !== false,
          opacity: layerData.opacity ?? 1,
          locked: layerData.locked || false,
          blendMode: layerData.blendMode || 'normal'
        }));

        // First, create the layer canvases for all restored layers
        restoredLayers.forEach(layer => {
          if (!layerCanvasesRef.current[layer.id]) {
            const newCanvas = document.createElement('canvas');
            const dpr = window.devicePixelRatio || 1;
            newCanvas.width = actualWidth * dpr;
            newCanvas.height = actualHeight * dpr;
            const ctx = newCanvas.getContext('2d');
            ctx.scale(dpr, dpr);
            layerCanvasesRef.current[layer.id] = newCanvas;
          }
        });

        setLayers(restoredLayers);
        setActiveLayerId(restoredLayers[0]?.id || 1);

        // Load layer images after canvas creation
        const loadLayerImages = async () => {
          for (const layerData of canvas_data.layers) {
            if (layerData.imageData) {
              const img = new Image();
              img.crossOrigin = 'anonymous';

              await new Promise((resolve) => {
                img.onload = () => {
                  const layerCanvas = layerCanvasesRef.current[layerData.id];
                  if (layerCanvas) {
                    const ctx = layerCanvas.getContext('2d');
                    ctx.clearRect(0, 0, actualWidth, actualHeight);
                    // Use logical dimensions to draw image
                    ctx.drawImage(img, 0, 0, actualWidth, actualHeight);
                  }
                  resolve();
                };
                img.onerror = () => {
                  console.warn(`Failed to load layer image for layer ${layerData.id}`);
                  resolve(); // Continue loading other layers
                };
                img.src = layerData.imageData;
              });
            }
          }

          // Trigger composite after all layers loaded
          requestComposite();

          toast.success(`Project "${title}" loaded successfully!`);
          setLoadingProject(false);
        };

        // Wait a bit for React state updates then load images
        setTimeout(loadLayerImages, 150);
      } else {
        toast.info('Project loaded but no layer data found');
        setLoadingProject(false);
      }

    } catch (err) {
      console.error('Load project error:', err);
      let msg = 'Failed to load project';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg).join(', ');
      else if (detail) msg = JSON.stringify(detail);
      toast.error(msg);
      setLoadingProject(false);
    }
  }, [API, token, canvasWidth, canvasHeight, requestComposite]);

  // Load project from URL parameter on mount (ONLY if not in panel mode)
  useEffect(() => {
    if (mode === 'panel') return;
    const projectId = searchParams.get('project');
    if (projectId && !loadedProjectId) {
      loadProject(projectId);
    }
  }, [searchParams, loadedProjectId, loadProject, mode]);

  // Update downloadImage ref for keyboard shortcuts
  useEffect(() => {
    downloadImageRef.current = downloadImage;
  });

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Track modifier keys
      if (e.code === 'Space') isSpacePressedRef.current = true;
      if (e.key === 'Alt') isAltPressedRef.current = true;
      if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        isRotateViewPressedRef.current = true;
        setIsRotateViewPressed(true);
        return;
      }

      // Photoshop-style Rotate View: Alt+R rotates clockwise, Alt+Shift+R counter-clockwise.
      if (e.altKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        const delta = e.shiftKey ? -15 : 15;
        const nextRotation = (viewRotationRef.current + delta + 360) % 360;
        updateViewRotationDirect(nextRotation);
        setViewRotation(nextRotation);
        toast.info(`Canvas rotated ${Math.round(nextRotation)} deg`);
        return;
      }

      if (e.altKey && e.key === '0') {
        e.preventDefault();
        updateViewRotationDirect(0);
        setViewRotation(0);
        toast.info('Canvas rotation reset');
        return;
      }

      // Focus (Fit to Screen)
      if (e.key.toLowerCase() === 'f') {
        const container = containerRef.current;
        if (container) {
          const { clientWidth, clientHeight } = container;
          // Calculate zoom to fit with 10% padding
          const fitZoom = Math.min(
            (clientWidth * 0.9) / canvasWidth,
            (clientHeight * 0.9) / canvasHeight
          );
          const newZoom = fitZoom;
          const newPan = {
            x: (clientWidth - canvasWidth * fitZoom) / 2,
            y: (clientHeight - canvasHeight * fitZoom) / 2
          };
          setZoom(newZoom);
          setPan(newPan);
          // Keep refs in sync
          zoomRef.current = newZoom;
          panRef.current = newPan;
          updatePanDirect(newPan);
          updateZoomDirect(newZoom);
          toast.info('Canvas Focused');
        }
        return;
      }

      // Tool Shortcuts
      const key = e.key.toLowerCase();
      if (KEYBOARD_SHORTCUTS[key]) {
        setCurrentTool(KEYBOARD_SHORTCUTS[key].tool);
        toast.info(`Tool: ${KEYBOARD_SHORTCUTS[key].label}`);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') isSpacePressedRef.current = false;
      if (e.key === 'Alt') isAltPressedRef.current = false;
      if (e.key.toLowerCase() === 'r') {
        isRotateViewPressedRef.current = false;
        setIsRotateViewPressed(false);
        rotateViewDragRef.current = null;
        syncViewState();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [canvasWidth, canvasHeight, updatePanDirect, updateZoomDirect, updateViewRotationDirect, syncViewState]); // Re-bind if dimensions change (needed for focus calculation)

  // Apply brush from history
  const applyBrushFromHistory = (historyItem) => {
    setCurrentBrush(historyItem.brush);
    setBrushColor(historyItem.color);
    setBrushSize(historyItem.size);
    setBrushOpacity(historyItem.opacity);
    setCurrentTool('brush');
    toast.success(`Applied: ${historyItem.brush.name}`);
  };

  // Update color from wheel
  const updateColorFromWheel = (h, s, l) => {
    const hex = hslToHex(h, s, l);
    setBrushColor(hex);
    setColorHue(h);
    setColorSaturation(s);
    setColorLightness(l);
  };

  const tools = [
    { name: 'select', icon: MousePointer, title: 'Select (M)', shortcut: 'M' },
    { name: 'move', icon: Move, title: 'Move (V)', shortcut: 'V' },
    { name: 'brush', icon: Brush, title: 'Brush (B)', shortcut: 'B' },
    { name: 'eraser', icon: Eraser, title: 'Eraser (E)', shortcut: 'E' },
    { name: 'fill', icon: PaintBucket, title: 'Fill (G)', shortcut: 'G' },
    { name: 'eyedropper', icon: Pipette, title: 'Eyedropper (I)', shortcut: 'I' },
    { name: 'text', icon: Type, title: 'Text (Y)', shortcut: 'Y' },
    { name: 'bubble', icon: MessageCircle, title: 'Bubble (Q)', shortcut: 'Q' },
    // { name: 'line', icon: Minus, title: 'Line (L)', shortcut: 'L' },
    // { name: 'rectangle', icon: Square, title: 'Rectangle (U)', shortcut: 'U' },
    // { name: 'circle', icon: Circle, title: 'Circle (U)', shortcut: 'U' },
    // { name: 'crop', icon: Crop, title: 'Crop (C)', shortcut: 'C' },
    { name: 'transform', icon: Maximize2, title: 'Transform (T)', shortcut: 'T' },
    { name: 'magicink', icon: Wand2, title: 'Magic Ink', special: true },
    { name: 'ai', icon: Sparkles, title: 'AI Tools', special: true },
    { name: 'animcanvas', icon: AnimCanvasIcon, title: 'AnimCanvas — 2D Animation Studio', special: true },
  ];

  // Color wheel click handler
  const handleColorWheelClick = (e, size = 140) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const x = e.clientX - rect.left - centerX;
    const y = e.clientY - rect.top - centerY;

    const distance = Math.sqrt(x * x + y * y);
    const maxRadius = size / 2;

    if (distance <= maxRadius) {
      const angle = Math.atan2(y, x) * (180 / Math.PI) + 180;
      const saturation = Math.min(100, (distance / maxRadius) * 100);
      updateColorFromWheel(angle, saturation, colorLightness);
    }
  };

  return (
    <div className="fixed inset-0 w-screen h-screen overflow-hidden flex flex-col bg-[#151515] text-[#d7d7d7]">
      {/* AnimCanvas — 2D animation studio overlay (self-contained; does not modify comic editor state) */}
      {showAnimCanvas && (
        <React.Suspense fallback={<div className="fixed inset-0 z-[1000] bg-[#151515] flex items-center justify-center"><Loader2 className="w-10 h-10 text-purple-400 animate-spin" /></div>}>
          <AnimCanvasEditor
            onClose={() => setShowAnimCanvas(false)}
            initialAnimation={initialAnimation || null}
            defaultSize={mode === 'panel' && panelWidth && panelHeight ? { width: panelWidth, height: panelHeight } : null}
            onSaveToPanel={mode === 'panel' && typeof onSavePanel === 'function'
              ? (data) => { setShowAnimCanvas(false); onSavePanel(data); }
              : undefined}
          />
        </React.Suspense>
      )}
      {/* Loading Overlay when loading a project */}
      {loadingProject && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-gray-800/90 rounded-2xl p-8 flex flex-col items-center space-y-4 border border-purple-500/30 shadow-xl shadow-purple-500/20">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin" />
            <p className="text-white font-medium text-lg">Loading Project...</p>
            <p className="text-gray-400 text-sm">Restoring layers and canvas data</p>
          </div>
        </div>
      )}



      <div className="h-screen flex flex-col min-w-0 overflow-hidden bg-[#151515]">
        {/* Top Menu Bar */}
        <div className="bg-[#2b2b2b]/95 backdrop-blur border-b border-[#111] px-2 py-1.5 shadow-[0_1px_0_rgba(255,255,255,0.06)_inset]">
          <div className="flex items-center gap-2 overflow-x-auto text-[#d7d7d7]">
            {/* Logo + Project Name */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="bg-[#1f1f1f] border border-[#3a3a3a] p-2 rounded-md shrink-0 shadow-inner">
                <Aperture className="w-4 h-4 text-primary" />
              </div>
              <Input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="hidden sm:block h-8 w-32 2xl:w-64 bg-[#202020] border-[#3a3a3a] hover:border-[#5a5a5a] focus-visible:ring-1 focus-visible:ring-[#4f8cff] text-[#f1f1f1] font-semibold px-2 rounded-sm"
                placeholder="Project Name"
                data-testid="project-name-input"
              />
            </div>

            {/* Action Buttons — scrollable together */}
            <div className="flex items-center gap-1.5 shrink-0 ml-auto">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={undo}
                      disabled={historyStep <= 0}
                      data-testid="undo-btn"
                    >
                      <Undo className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Undo (Ctrl+Z)</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={redo}
                      disabled={historyStep >= historyLength - 1}
                      data-testid="redo-btn"
                    >
                      <Redo className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Redo (Ctrl+Shift+Z)</TooltipContent>
                </Tooltip>

                <Separator orientation="vertical" className="h-6 mx-1 shrink-0" />

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowShortcutsPanel(true)}
                      data-testid="shortcuts-btn"
                    >
                      <Keyboard className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Shortcuts (?)</TooltipContent>
                </Tooltip>

                <Separator orientation="vertical" className="h-6 mx-1 shrink-0" />

                {/* Layers toggle for mobile */}
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowMobileLayersPanel(v => !v)}
                    title="Layers"
                  >
                    <Layers className="w-4 h-4" />
                  </Button>
                )}

                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => mode === 'panel' ? handlePanelSave() : setShowSaveModal(true)}
                    data-testid="save-btn"
                    className="gap-1.5 px-2 sm:px-3 bg-[#343434] border border-[#4a4a4a] text-[#e8e8e8] hover:bg-[#454545]"
                  >
                    <Save className="w-4 h-4" />
                    <span className="hidden sm:inline">Save</span>
                  </Button>

                  <Button
                    variant="default"
                    size="sm"
                    onClick={downloadImage}
                    data-testid="export-btn"
                    className="gap-1.5 px-2 sm:px-3 bg-[#4f8cff] text-white hover:bg-[#6aa0ff] shadow-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Export</span>
                  </Button>
                </div>

                {/* Recording Button */}
                <Separator orientation="vertical" className="h-6 mx-1 shrink-0" />
                {!isRecording ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={startRecording}
                    className="gap-1.5 px-2 sm:px-3 bg-[#343434] border border-[#4a4a4a] text-[#e8e8e8] hover:bg-[#454545]"
                    title="Record timelapse"
                  >
                    <Video className="w-4 h-4" />
                    <span className="hidden sm:inline">Record</span>
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={stopRecording}
                    className="gap-1.5 px-2 sm:px-3 animate-pulse"
                    title="Stop recording"
                  >
                    <StopCircle className="w-4 h-4" />
                    <span ref={recTimerDisplayRef} className="hidden sm:inline">00:00</span>
                  </Button>
                )}
              </TooltipProvider>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 flex overflow-hidden">
          {/* Left Toolbar — desktop only */}
          <div className={cn("hidden 2xl:flex w-14 shrink-0 bg-[#252525] border-r border-[#111] flex-col items-center py-3 gap-1.5 overflow-y-auto z-10 shadow-[1px_0_0_rgba(255,255,255,0.05)_inset]")}>
            <TooltipProvider delayDuration={0}>
              {tools.map(tool => (
                <Tooltip key={tool.name} placement="right">
                  <TooltipTrigger asChild>
                    <Button
                      variant={currentTool === tool.name ? "default" : "ghost"}
                      size="icon"
                      onClick={() => tool.name === 'ai' ? openAIEditor() : tool.name === 'animcanvas' ? setShowAnimCanvas(true) : setCurrentTool(tool.name)}
                      className={cn(
                        "h-10 w-10 rounded-md transition-all text-[#d0d0d0] hover:bg-[#3a3a3a] hover:text-white border border-transparent",
                        tool.special && "bg-[#3a2f4f] text-[#f1e9ff] hover:bg-[#493b63] border-[#6c55a1]",
                        currentTool === tool.name && !tool.special && "bg-[#4f8cff] text-white hover:bg-[#6aa0ff] shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_0_14px_rgba(79,140,255,0.28)] border-[#8bb6ff] scale-105"
                      )}
                      data-testid={`tool-${tool.name}`}
                    >
                      <tool.icon className="w-5 h-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={10}>
                    <p className="font-semibold">{tool.title}</p>
                    {tool.shortcut && <span className="text-xs text-muted-foreground ml-2">({tool.shortcut})</span>}
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>

          {/* Canvas Area */}
          {/* Canvas Area — min-w-0 prevents flex-1 from expanding to content width */}
          <div className="flex-1 min-w-0 flex flex-col bg-[#1b1b1b] overflow-hidden">
            {/* Properties Bar */}
            {showToolProperties && (
              <div className="bg-[#303030]/95 backdrop-blur border-b border-[#151515] shrink-0 z-20 overflow-x-auto w-full shadow-[0_1px_0_rgba(255,255,255,0.06)_inset]">
                <div className="flex items-center px-2 py-1.5 gap-2 w-max text-[#dcdcdc]">

                {/* Brush Picker */}
                {(currentTool === 'brush' || currentTool === 'eraser') && (
                  <Popover open={showBrushPicker} onOpenChange={setShowBrushPicker}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="w-[130px] 2xl:w-[180px] justify-between shrink-0 bg-[#242424] border-[#555] text-[#e5e5e5] hover:bg-[#343434]">
                        {currentBrush.name}
                        <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0">
                      <ScrollArea className="h-72">
                        <div className="p-1">
                          {BRUSH_PRESETS.map(brush => (
                            <Button
                              key={brush.id}
                              variant={currentBrush.id === brush.id ? "secondary" : "ghost"}
                              className="w-full justify-start text-sm font-normal"
                              onClick={() => {
                                setCurrentBrush(brush);
                                setShowBrushPicker(false);
                              }}
                            >
                              <div className="flex flex-col items-start gap-1">
                                <span>{brush.name}</span>
                                {brush.id === 'zhong_fenghua' && (
                                  <span className="text-[10px] text-muted-foreground">Default</span>
                                )}
                              </div>
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                )}

                <Separator orientation="vertical" className="h-8" />

                {/* Common Properties */}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5 w-28 shrink-0">
                    <Label className="text-xs text-muted-foreground w-7 shrink-0">Size</Label>
                    <Slider
                      value={[brushSize]}
                      min={1}
                      max={200}
                      step={1}
                      onValueChange={(vals) => setBrushSize(vals[0])}
                      className="flex-1"
                    />
                    <span className="text-xs w-6 text-right">{brushSize}</span>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Label className="text-xs text-muted-foreground shrink-0">Color</Label>
                    <div className="flex items-center gap-1 border border-[#555] rounded-sm p-1 bg-[#1f1f1f]">
                      <input
                        type="color"
                        value={brushColor}
                        onChange={(e) => setBrushColor(e.target.value)}
                        className="w-6 h-6 rounded cursor-pointer border-none bg-transparent p-0"
                      />
                    </div>
                    {'EyeDropper' in window && (
                      <button
                        onClick={async () => {
                          try {
                            const dropper = new window.EyeDropper();
                            const result = await dropper.open();
                            if (result?.sRGBHex) {
                              setBrushColor(result.sRGBHex);
                              toast.success(`Color picked: ${result.sRGBHex}`);
                            }
                          } catch (e) { /* cancelled */ }
                        }}
                        className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs bg-[#242424] hover:bg-[#3a3a3a] border border-[#444] text-[#ddd] transition-colors"
                        title="Pick a color from anywhere on screen"
                      >
                        <Pipette className="w-3.5 h-3.5" />
                        <span>Screen</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <Label className="text-xs text-muted-foreground shrink-0">Fill</Label>
                  <div className="flex items-center gap-1 border border-[#555] rounded-sm p-1 bg-[#1f1f1f]">
                    <input
                      type="color"
                      value={fillColor}
                      onChange={(e) => setFillColor(e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-none bg-transparent p-0"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-1.5 w-28 shrink-0">
                  <Label className="text-xs text-muted-foreground w-10 shrink-0">Opacity</Label>
                  <Slider
                    value={[brushOpacity]}
                    min={0}
                    max={1}
                    step={0.05}
                    onValueChange={(vals) => setBrushOpacity(vals[0])}
                    className="flex-1"
                  />
                  <span className="text-xs w-8 text-right">{Math.round(brushOpacity * 100)}%</span>
                </div>

                {/* ─── Phase 1 Brush Engine UI Controls ─── */}
                {currentTool === 'brush' && (
                  <>
                    <Separator orientation="vertical" className="h-8 shrink-0" />

                    {/* Hardness */}
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Label className="text-xs text-muted-foreground w-12 shrink-0">Hard</Label>
                      <Slider
                        value={[brushHardness]}
                        min={0} max={1} step={0.05}
                        onValueChange={(vals) => setBrushHardness(vals[0])}
                        className="flex-1"
                        title="Hardness: 0=soft airbrush, 1=crisp edge"
                      />
                      <span className="text-xs w-8 text-right">{Math.round(brushHardness * 100)}%</span>
                    </div>

                    <Separator orientation="vertical" className="h-8 shrink-0" />

                    {/* Flow */}
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Label className="text-xs text-muted-foreground w-10 shrink-0">Flow</Label>
                      <Slider
                        value={[brushFlow]}
                        min={0.05} max={1} step={0.05}
                        onValueChange={(vals) => setBrushFlow(vals[0])}
                        className="flex-1"
                        title="Flow: per-dab opacity. Low=builds up like ink"
                      />
                      <span className="text-xs w-8 text-right">{Math.round(brushFlow * 100)}%</span>
                    </div>

                    <Separator orientation="vertical" className="h-8 shrink-0" />

                    {/* Stabilizer */}
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Label className="text-xs text-muted-foreground w-12 shrink-0" title="Stabilizer: smooths shaky strokes">Stab</Label>
                      <Slider
                        value={[brushStabilizer]}
                        min={0} max={10} step={1}
                        onValueChange={(vals) => setBrushStabilizer(vals[0])}
                        className="flex-1"
                        title="Stabilizer: 0=raw input, 10=very smooth"
                      />
                      <span className="text-xs w-6 text-right">{brushStabilizer}</span>
                    </div>

                    <Separator orientation="vertical" className="h-8 shrink-0" />

                    {/* Scatter */}
                    <div className="flex items-center gap-1.5 w-28 shrink-0">
                      <Label className="text-xs text-muted-foreground w-12 shrink-0">Scatter</Label>
                      <Slider
                        value={[brushScatter]}
                        min={0} max={2} step={0.1}
                        onValueChange={(vals) => setBrushScatter(vals[0])}
                        className="flex-1"
                        title="Scatter: randomize dab positions"
                      />
                      <span className="text-xs w-8 text-right">{Math.round(brushScatter * 100)}%</span>
                    </div>
                  </>
                )}

                {/* Pressure Settings */}
                {(currentTool === 'brush' || currentTool === 'eraser') && (
                  <>
                    <Separator orientation="vertical" className="h-8" />
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Switch
                          checked={pressureSensitivity}
                          onCheckedChange={setPressureSensitivity}
                          id="pressure-mode"
                        />
                        <Label htmlFor="pressure-mode" className="text-xs cursor-pointer shrink-0">Pressure</Label>
                      </div>

                      {pressureSensitivity && (
                        <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={pressureAffectsSize}
                              onChange={(e) => setPressureAffectsSize(e.target.checked)}
                              className="accent-primary"
                              id="p-size"
                            />
                            <Label htmlFor="p-size" className="text-xs font-normal">Size</Label>
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={pressureAffectsOpacity}
                              onChange={(e) => setPressureAffectsOpacity(e.target.checked)}
                              className="accent-primary"
                              id="p-opacity"
                            />
                            <Label htmlFor="p-opacity" className="text-xs font-normal">Opacity</Label>
                          </div>
                          {/* Pressure Indicator */}
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-muted-foreground">P:</span>
                            <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all duration-75"
                                style={{ width: `${displayPressure * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Text Tool Properties */}
                {currentTool === 'text' && (
                  <>
                    <Separator orientation="vertical" className="h-8" />
                    <Select value={textFontFamily} onValueChange={setTextFontFamily}>
                      <SelectTrigger className="w-[140px] h-8 text-xs">
                        <SelectValue placeholder="Font" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Verdana">Verdana</SelectItem>
                        <SelectItem value="Comic Sans MS">Comic Sans MS</SelectItem>
                      </SelectContent>
                    </Select>

                    <Input
                      type="number"
                      value={textFontSize}
                      onChange={(e) => setTextFontSize(parseInt(e.target.value) || 24)}
                      className="w-16 h-8 text-xs"
                    />

                    <div className="flex items-center border rounded-md">
                      <Button
                        variant={textBold ? "secondary" : "ghost"}
                        size="icon"
                        className="h-8 w-8 rounded-none border-r"
                        onClick={() => setTextBold(!textBold)}
                      >
                        <span className="font-bold">B</span>
                      </Button>
                      <Button
                        variant={textItalic ? "secondary" : "ghost"}
                        size="icon"
                        className="h-8 w-8 rounded-none"
                        onClick={() => setTextItalic(!textItalic)}
                      >
                        <span className="italic">I</span>
                      </Button>
                    </div>
                  </>
                )}

                {/* Crop Tool Properties */}
                {currentTool === 'crop' && cropRegion && (
                  <>
                    <Separator orientation="vertical" className="h-8" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {Math.round(cropRegion.width)} Ã— {Math.round(cropRegion.height)}
                    </span>
                    <Button size="sm" onClick={applyCrop} className="bg-green-600 hover:bg-green-700 h-7 text-xs">
                      Apply
                    </Button>
                    <Button size="sm" variant="secondary" onClick={cancelCrop} className="h-7 text-xs">
                      Cancel
                    </Button>
                  </>
                )}

                {/* Magic Ink Properties */}
                {currentTool === 'magicink' && (
                  <>
                    <Separator orientation="vertical" className="h-8" />
                    <span className="text-xs font-medium text-purple-400 whitespace-nowrap">Style</span>
                    <Select value={magicInkStyle} onValueChange={setMagicInkStyle}>
                      <SelectTrigger className="w-[110px] h-8 text-xs">
                        <SelectValue placeholder="Style" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="comic">🦸 Comic</SelectItem>
                        <SelectItem value="manga">⛩️ Manga</SelectItem>
                        <SelectItem value="realistic">🎨 Realistic</SelectItem>
                      </SelectContent>
                    </Select>

                    <span className="text-xs text-muted-foreground whitespace-nowrap">Strength</span>
                    <input
                      type="range"
                      min="30"
                      max="100"
                      value={Math.round(magicInkStrength * 100)}
                      onChange={(e) => setMagicInkStrength(parseInt(e.target.value) / 100)}
                      className="w-20 h-1 accent-purple-500"
                    />
                    <span className="text-xs text-muted-foreground w-8">{Math.round(magicInkStrength * 100)}%</span>

                    <Button
                      size="sm"
                      onClick={applyMagicInk}
                      disabled={magicInkProcessing}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white h-7 text-xs px-3 font-semibold"
                    >
                      {magicInkProcessing ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Inking...</>
                      ) : (
                        <><Wand2 className="w-3 h-3 mr-1" /> Ink It!</>
                      )}
                    </Button>
                  </>
                )}

                <Separator orientation="vertical" className="h-8 ml-auto shrink-0" />

                {/* View Controls */}
                <div className="flex items-center gap-1 bg-[#232323] border border-[#444] rounded-sm p-1 shrink-0 text-[#e5e5e5]">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const nz = Math.max(0.1, zoom - 0.1); setZoom(nz); zoomRef.current = nz; updateZoomDirect(nz); }} title="Zoom out">
                    <ZoomOut className="w-3 h-3" />
                  </Button>
                  <span className="text-xs w-8 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const nz = Math.min(5, zoom + 0.1); setZoom(nz); zoomRef.current = nz; updateZoomDirect(nz); }} title="Zoom in">
                    <ZoomIn className="w-3 h-3" />
                  </Button>
                  <Separator orientation="vertical" className="h-5 bg-[#4a4a4a]" />
                  <Button variant="ghost" size="icon" className={cn("h-7 w-7", isRotateViewPressed && "bg-[#4f8cff]/30 text-white")} onClick={() => { const next = (viewRotation - 15 + 360) % 360; setViewRotation(next); updateViewRotationDirect(next); }} title="Hold R and drag canvas, or click to rotate left (Alt+Shift+R)">
                    <RotateCcw className="w-3 h-3" />
                  </Button>
                  <span className="text-xs w-10 text-center tabular-nums">{Math.round(viewRotation)} deg</span>
                  <Button variant="ghost" size="icon" className={cn("h-7 w-7", isRotateViewPressed && "bg-[#4f8cff]/30 text-white")} onClick={() => { const next = (viewRotation + 15) % 360; setViewRotation(next); updateViewRotationDirect(next); }} title="Hold R and drag canvas, or click to rotate right (Alt+R)">
                    <RotateCcw className="w-3 h-3 scale-x-[-1]" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] uppercase tracking-wide" onClick={() => { setViewRotation(0); updateViewRotationDirect(0); }} title="Reset view rotation (Alt+0)">
                    Reset
                  </Button>
                </div>
                </div>
              </div>
            )}

            {/* Canvas */}
            <div
              ref={containerRef}
              className={`flex-1 overflow-hidden relative touch-none pb-0 md:pb-0 bg-[#161616] bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.055)_1px,transparent_0)] [background-size:18px_18px]`}
              style={{ cursor: (isPanning ? 'grabbing' : (currentTool === 'pan' || isSpacePressedRef.current) ? 'grab' : 'crosshair') }}
              data-testid="canvas-container"
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onPointerCancel={handlePointerUp}
              // onMouseMove removed - handled by onPointerMove
              onMouseEnter={() => setShowBrushCursor(true)}
              onMouseLeave={() => setShowBrushCursor(false)}
            >
              {/* DEBUG OVERLAY */}
              <div
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  background: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  zIndex: 9999,
                  pointerEvents: 'none',
                  whiteSpace: 'pre-wrap',
                  display: 'none' // Hidden in production
                }}
                id="debug-overlay"
              >
                Debug Info
              </div>

              {/* Custom Brush Cursor */}
              {showBrushCursor && ['brush', 'eraser'].includes(currentTool) && (
                <div
                  ref={cursorRef}
                  className="fixed pointer-events-none z-50 transition-none"
                  style={{
                    left: 0,
                    top: 0,
                    transform: 'translate(-100px, -100px)' // Initial off-screen
                  }}
                >
                  <div className="relative" style={{ transform: 'translate(-50%, -50%)' }}>
                    {/* Outer circle - brush size */}
                    <div
                      className="rounded-full border-2 absolute"
                      style={{
                        width: brushSize * zoom,
                        height: brushSize * zoom,
                        borderColor: currentTool === 'eraser' ? 'rgba(255,100,100,0.8)' : 'rgba(255,255,255,0.8)',
                        left: -(brushSize * zoom) / 2,
                        top: -(brushSize * zoom) / 2,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.3)'
                      }}
                    />
                    {/* Center dot */}
                    <div
                      className="w-1 h-1 rounded-full bg-white absolute"
                      style={{
                        left: -2,
                        top: -2,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.5)'
                      }}
                    />
                    {/* Color preview for brush */}
                    {currentTool === 'brush' && (
                      <div
                        className="absolute rounded-full opacity-50"
                        style={{
                          width: brushSize * zoom * 0.7,
                          height: brushSize * zoom * 0.7,
                          backgroundColor: brushColor,
                          left: -(brushSize * zoom * 0.7) / 2,
                          top: -(brushSize * zoom * 0.7) / 2,
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {showBrushCursor && currentTool === 'eyedropper' && (
                <div
                  ref={cursorRef}
                  className="fixed pointer-events-none z-50 flex items-center transition-none"
                  style={{
                    left: 0,
                    top: 0,
                    transform: 'translate(-100px, -100px)' // Initial off-screen
                  }}
                >
                  {/* Offset so the pipette tip aligns with cursor hotspot */}
                  <div style={{ transform: 'translate(-2px, -20px)' }}>
                    <Pipette className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                  <div
                    className="w-6 h-6 rounded-md border-2 border-white shadow-lg"
                    id="eyedropper-preview-swatch"
                    style={{ backgroundColor: brushColor, marginLeft: 4, marginTop: -12 }}
                  />
                </div>
              )}

              {/* Movable Wrapper for Pan/Zoom — DOM refs for direct transform updates */}
              <div
                ref={panWrapperRef}
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px)`,
                  transformOrigin: '0 0',
                  width: '100%',
                  height: '100%'
                }}
              >
                {/* Canvas Container */}
                <div
                  ref={zoomWrapperRef}
                  className="relative bg-white shadow-[0_22px_60px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)] ring-1 ring-black/80"
                  style={{
                    width: canvasWidth,
                    height: canvasHeight,
                    transform: `scale(${zoom}) rotate(${viewRotation}deg)`,
                    transformOrigin: '0 0',
                    cursor: (() => {
                      if (isRotateViewPressed || rotateViewDragRef.current) return 'grab';
                      if (isPanning) return 'grabbing';
                      if (currentTool === 'pan' || isSpacePressedRef.current) return 'grab';
                      if (['brush', 'eraser'].includes(currentTool)) return 'none'; // custom cursor overlay
                      // Custom SVG crosshair: black cross with white outline, visible on any background
                      return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Cline x1='16' y1='4' x2='16' y2='28' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3Cline x1='4' y1='16' x2='28' y2='16' stroke='white' stroke-width='3' stroke-linecap='round'/%3E%3Cline x1='16' y1='4' x2='16' y2='28' stroke='%23222' stroke-width='1.5' stroke-linecap='round'/%3E%3Cline x1='4' y1='16' x2='28' y2='16' stroke='%23222' stroke-width='1.5' stroke-linecap='round'/%3E%3Ccircle cx='16' cy='16' r='1.5' fill='%23222'/%3E%3C/svg%3E") 16 16, crosshair`;
                    })()
                  }}
                  onDoubleClick={handleDoubleClick}
                >
                  {/* [NEW] Shape Mask Overlay (Visual Guide) */}
                  {panelPoints && (
                    <svg
                      className="absolute inset-0 pointer-events-none z-[60]"
                      width={canvasWidth}
                      height={canvasHeight}
                    >
                      <defs>
                        <mask id="panel-mask">
                          <rect x="0" y="0" width="100%" height="100%" fill="white" />
                          <polygon
                            points={panelPoints.map(p => `${(p.x / 100) * canvasWidth},${(p.y / 100) * canvasHeight}`).join(' ')}
                            fill="black"
                          />
                        </mask>
                      </defs>
                      {/* Dark Overlay with Hole */}
                      <rect
                        x="0" y="0" width="100%" height="100%"
                        fill="rgba(0,0,0,0.5)"
                        mask="url(#panel-mask)"
                      />
                      {/* Outline of the shape */}
                      <polygon
                        points={panelPoints.map(p => `${(p.x / 100) * canvasWidth},${(p.y / 100) * canvasHeight}`).join(' ')}
                        fill="none"
                        stroke="rgba(255, 255, 255, 0.8)"
                        strokeWidth="2"
                        strokeDasharray="4 4"
                      />
                    </svg>
                  )}

                  {/* Rendering Canvas (Bottom - Composite) */}
                  <canvas
                    ref={mainCanvasRef}
                    width={canvasWidth * (window.devicePixelRatio || 1)}
                    height={canvasHeight * (window.devicePixelRatio || 1)}
                    style={{
                      width: canvasWidth,
                      height: canvasHeight,
                      display: 'block',
                      touchAction: 'none'
                    }}
                    className="bg-white shadow-sm"
                    data-testid="main-canvas"
                  />
                  {/* Selection overlay */}
                  {selection && (
                    <div
                      className="absolute border-2 border-dashed border-blue-400 bg-blue-400/10 pointer-events-none"
                      style={{
                        left: selection.x,
                        top: selection.y,
                        width: selection.width,
                        height: selection.height,
                        animation: 'marching-ants 0.5s linear infinite'
                      }}
                      data-testid="selection-overlay"
                    />
                  )}

                  {/* Crop overlay */}
                  {cropRegion && currentTool === 'crop' && (
                    <>
                      {/* Darkened areas outside crop */}
                      <div
                        className="absolute bg-black/60 pointer-events-none"
                        style={{ left: 0, top: 0, right: 0, height: cropRegion.y }}
                      />
                      <div
                        className="absolute bg-black/60 pointer-events-none"
                        style={{ left: 0, top: cropRegion.y + cropRegion.height, right: 0, bottom: 0 }}
                      />
                      <div
                        className="absolute bg-black/60 pointer-events-none"
                        style={{ left: 0, top: cropRegion.y, width: cropRegion.x, height: cropRegion.height }}
                      />
                      <div
                        className="absolute bg-black/60 pointer-events-none"
                        style={{ left: cropRegion.x + cropRegion.width, top: cropRegion.y, right: 0, height: cropRegion.height }}
                      />
                      {/* Crop selection border */}
                      <div
                        className="absolute border-2 border-white border-dashed pointer-events-none"
                        style={{
                          left: cropRegion.x,
                          top: cropRegion.y,
                          width: cropRegion.width,
                          height: cropRegion.height,
                        }}
                        data-testid="crop-overlay"
                      >
                        {/* Corner handles */}
                        <div className="absolute -left-1 -top-1 w-2 h-2 bg-white" />
                        <div className="absolute -right-1 -top-1 w-2 h-2 bg-white" />
                        <div className="absolute -left-1 -bottom-1 w-2 h-2 bg-white" />
                        <div className="absolute -right-1 -bottom-1 w-2 h-2 bg-white" />
                        {/* Size label */}
                        <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                          {Math.round(cropRegion.width)} Ã— {Math.round(cropRegion.height)}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Hidden composite canvas for preview */}
                  <canvas
                    ref={compositeCanvasRef}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* === OVERLAYS (outside zoomWrapper to avoid CSS transform issues) === */}

            {/* Bubble Properties Overlay */}
            {activeLayerId && layers.find(l => l.id === activeLayerId)?.type === 'bubble' && showBubbleEdit && (
              <>
                <div
                  className="fixed z-50 p-3 bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-2xl border border-blue-500/80 flex flex-col gap-2"
                  style={{
                    left: bubblePanelPos?.x ?? 56,
                    top: bubblePanelPos?.y ?? 80,
                    minWidth: '260px',
                    cursor: 'default'
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    // Draggable panel: start drag on header area
                    if (e.target.closest('[data-bubble-drag]')) {
                      const startX = e.clientX - (bubblePanelPos?.x ?? 56);
                      const startY = e.clientY - (bubblePanelPos?.y ?? 80);
                      const onMove = (ev) => {
                        setBubblePanelPos({ x: ev.clientX - startX, y: ev.clientY - startY });
                      };
                      const onUp = () => {
                        window.removeEventListener('pointermove', onMove);
                        window.removeEventListener('pointerup', onUp);
                      };
                      window.addEventListener('pointermove', onMove);
                      window.addEventListener('pointerup', onUp);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                >
                  <div data-bubble-drag className="text-xs font-semibold text-gray-400 mb-1 flex justify-between items-center cursor-move">
                    <span>Edit Bubble</span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon" variant="ghost" className="h-5 w-5 hover:bg-red-500/20 text-red-400"
                              onClick={() => {
                                // Delete Layer
                                deleteLayer(activeLayerId);
                                setShowBubbleEdit(false);
                              }}
                              title="Delete Bubble"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            <Button
                              size="icon" variant="ghost" className="h-5 w-5 hover:bg-green-500/20 text-green-400"
                              onClick={() => setShowBubbleEdit(false)}
                              title="Done"
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-[10px] text-blue-400 mb-2">Drag yellow dot for tail</div>

                        {/* Shape Selection */}
                        <div className="grid grid-cols-5 gap-1 mb-2">
                          {BUBBLE_LIBRARY.map(shape => (
                            <div
                              key={shape.id}
                              onClick={() => {
                                const layer = layers.find(l => l.id === activeLayerId);
                                const newConfig = { ...layer.bubbleConfig, shapeId: shape.id };
                                renderBubbleLayer(activeLayerId, newConfig);
                                setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
                                requestComposite();
                              }}
                              className={cn(
                                "w-8 h-8 border rounded flex items-center justify-center cursor-pointer hover:bg-gray-800",
                                layers.find(l => l.id === activeLayerId).bubbleConfig.shapeId === shape.id ? "border-blue-500 bg-blue-500/20" : "border-gray-700"
                              )}
                              title={shape.name}
                            >
                              <svg viewBox="0 0 100 100" className="w-6 h-6 fill-current text-white">
                                <path d={shape.path} />
                              </svg>
                            </div>
                          ))}
                        </div>

                        {/* Colors */}
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-center">
                            <input
                              type="color"
                              value={layers.find(l => l.id === activeLayerId).bubbleConfig.fillColor}
                              onChange={(e) => {
                                const val = e.target.value;
                                const layer = layers.find(l => l.id === activeLayerId);
                                const newConfig = { ...layer.bubbleConfig, fillColor: val };
                                renderBubbleLayer(activeLayerId, newConfig);
                                setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
                                requestComposite();
                              }}
                              className="w-6 h-6 p-0 border-0 rounded overflow-hidden cursor-pointer"
                            />
                            <span className="text-[10px] text-gray-400">Fill</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <input
                              type="color"
                              value={layers.find(l => l.id === activeLayerId).bubbleConfig.strokeColor}
                              onChange={(e) => {
                                const val = e.target.value;
                                const layer = layers.find(l => l.id === activeLayerId);
                                const newConfig = { ...layer.bubbleConfig, strokeColor: val };
                                renderBubbleLayer(activeLayerId, newConfig);
                                setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
                                requestComposite();
                              }}
                              className="w-6 h-6 p-0 border-0 rounded overflow-hidden cursor-pointer"
                            />
                            <span className="text-[10px] text-gray-400">Stroke</span>
                          </div>

                          {/* Shadow Toggle */}
                          <div className="flex flex-col items-center ml-2 border-l pl-2 border-gray-700">
                            <div
                              className={cn("w-6 h-6 rounded border cursor-pointer mb-0.5 flex items-center justify-center", layers.find(l => l.id === activeLayerId).bubbleConfig.shadow ? "bg-purple-500 border-purple-500" : "bg-gray-800 border-gray-600")}
                              onClick={() => {
                                const layer = layers.find(l => l.id === activeLayerId);
                                const newConfig = { ...layer.bubbleConfig, shadow: !layer.bubbleConfig.shadow };
                                renderBubbleLayer(activeLayerId, newConfig);
                                setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
                                requestComposite();
                              }}
                            >
                              <div className="text-[10px] text-white font-bold">3D</div>
                            </div>
                            <span className="text-[10px] text-gray-400">Shadow</span>
                          </div>
                          <div className="flex flex-col ml-1 flex-1">
                            <span className="text-[10px] text-gray-400">Border Width</span>
                            <Slider
                              min={0} max={20} step={1}
                              value={[layers.find(l => l.id === activeLayerId).bubbleConfig.strokeWidth]}
                              onValueChange={(vals) => {
                                const val = vals[0];
                                const layer = layers.find(l => l.id === activeLayerId);
                                const newConfig = { ...layer.bubbleConfig, strokeWidth: val };
                                renderBubbleLayer(activeLayerId, newConfig);
                                setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, bubbleConfig: newConfig } : l));
                                requestComposite();
                              }}
                            />
                          </div>
                        </div>
                      </div>

                {/* Visual Handle for Tail */}
                {(() => {
                  const bc = layers.find(l => l.id === activeLayerId).bubbleConfig;
                  return (
                    <>
                      {/* Tail dot */}
                      <div
                        className="absolute w-5 h-5 bg-yellow-400 rounded-full border-2 border-white shadow-lg z-[60] cursor-grab active:cursor-grabbing"
                        style={{
                          left: bc.tailTip.x * zoom + pan.x,
                          top: bc.tailTip.y * zoom + pan.y,
                          transform: 'translate(-50%, -50%)',
                        }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                drawingStateRef.current.isDraggingTail = true;
                                strokeStateRef.current.isActive = false;
                                // Capture pointer for reliable drag
                                e.target.setPointerCapture(e.pointerId);
                              }}
                              onPointerMove={(e) => {
                                if (!drawingStateRef.current.isDraggingTail) return;
                                e.stopPropagation();
                                const pos = getCanvasPoint(e);
                                const activeId = activeLayerIdRef.current;
                                const layer = layersRef.current.find(l => l.id === activeId);
                                if (layer && layer.type === 'bubble') {
                                  const newConfig = { ...layer.bubbleConfig, tailTip: { x: pos.x, y: pos.y } };
                                  renderBubbleLayer(activeId, newConfig);
                                  setLayers(prev => prev.map(l => l.id === activeId ? { ...l, bubbleConfig: newConfig } : l));
                                  requestComposite();
                                }
                              }}
                              onPointerUp={(e) => {
                                if (!drawingStateRef.current.isDraggingTail) return;
                                e.stopPropagation();
                                drawingStateRef.current.isDraggingTail = false;
                                e.target.releasePointerCapture(e.pointerId);
                              }}
                            />
                      {/* Resize handles at bubble corners */}
                      {['nw', 'ne', 'sw', 'se'].map(corner => {
                        const hx = corner.includes('e') ? bc.x + bc.width : bc.x;
                        const hy = corner.includes('s') ? bc.y + bc.height : bc.y;
                        return (
                          <div
                            key={corner}
                            className="absolute w-3 h-3 bg-white border-2 border-blue-500 rounded-sm z-[60]"
                            style={{
                              left: hx * zoom + pan.x,
                              top: hy * zoom + pan.y,
                              transform: 'translate(-50%, -50%)',
                              cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                              pointerEvents: 'none'
                            }}
                          />
                        );
                      })}
                      {/* Bounding box outline */}
                      <div
                        className="absolute border border-dashed border-blue-400/60 z-[59] pointer-events-none"
                        style={{
                          left: bc.x * zoom + pan.x,
                          top: bc.y * zoom + pan.y,
                          width: bc.width * zoom,
                          height: bc.height * zoom,
                        }}
                      />
                    </>
                  );
                })()}
                    </>
                  )}

            {/* Text input overlay — outside zoomWrapper */}
            {showTextInput && textPosition && (
              <div
                className="fixed z-50 p-3 bg-gray-900 rounded-lg shadow-2xl border border-purple-500 flex flex-col gap-2"
                style={{
                  left: textPanelPos?.x ?? 56,
                  top: textPanelPos?.y ?? 80,
                  minWidth: '250px',
                  cursor: 'default'
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (e.target.closest('[data-text-drag]')) {
                    const startX = e.clientX - (textPanelPos?.x ?? 56);
                    const startY = e.clientY - (textPanelPos?.y ?? 80);
                    const onMove = (ev) => {
                      setTextPanelPos({ x: ev.clientX - startX, y: ev.clientY - startY });
                    };
                    const onUp = () => {
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
              >
                      <div data-text-drag className="text-xs font-semibold text-gray-400 mb-1 cursor-move">
                        {activeTextLayerId ? 'Edit Comic Text' : 'New Comic Text'}
                      </div>
                      <Textarea
                        value={textInput}
                        onChange={(e) => {
                          const val = e.target.value;
                          setTextInput(val);
                          if (activeTextLayerId) updateTextLayer(activeTextLayerId, { content: val });
                        }}
                        onMouseUp={(e) => {
                          // Photoshop-style text bounding box sync
                          const newWidth = e.target.offsetWidth;
                          if (newWidth) {
                            setTextMaxWidth(newWidth);
                            if (activeTextLayerId) updateTextLayer(activeTextLayerId, { maxWidth: newWidth });
                          }
                        }}
                        placeholder="Type..."
                        className="bg-gray-800/80 backdrop-blur-sm border-purple-500/50 text-white min-h-[60px] font-bold"
                        style={{
                          fontFamily: textFontFamily,
                          fontSize: `${textFontSize}px`,
                          width: `${textMaxWidth}px`,
                          resize: 'horizontal',
                          lineHeight: 1.2,
                          color: brushColor,
                          textShadow: textStrokeWidth > 0 ? `-1px -1px 0 ${textStrokeColor}, 1px -1px 0 ${textStrokeColor}, -1px 1px 0 ${textStrokeColor}, 1px 1px 0 ${textStrokeColor}` : 'none'
                        }}
                        autoFocus
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col">
                          <label className="text-[10px] text-gray-400">Font</label>
                          <select
                            value={textFontFamily}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTextFontFamily(val);
                              if (activeTextLayerId) updateTextLayer(activeTextLayerId, { fontFamily: val });
                            }}
                            className="bg-gray-800 text-white text-xs border border-gray-700 rounded h-6"
                          >
                            {TEXT_FONTS.map(f => (
                              <option key={f.name} value={f.name}>{f.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col">
                          <label className="text-[10px] text-gray-400">Size</label>
                          <input
                            type="number"
                            value={textFontSize}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setTextFontSize(val);
                              if (activeTextLayerId) updateTextLayer(activeTextLayerId, { fontSize: val });
                            }}
                            className="bg-gray-800 text-white text-xs border border-gray-700 rounded h-6 px-1"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex flex-col items-center">
                          <input
                            type="color"
                            value={brushColor}
                            onChange={(e) => {
                              const val = e.target.value;
                              setBrushColor(val);
                              if (activeTextLayerId) updateTextLayer(activeTextLayerId, { color: val });
                            }}
                            className="w-6 h-6 p-0 border-0 rounded overflow-hidden cursor-pointer"
                          />
                          <span className="text-[10px] text-gray-400">Fill</span>
                        </div>

                        <div className="w-px h-6 bg-gray-700 mx-1" />

                        <div className="flex flex-col items-center">
                          <input
                            type="color"
                            value={textStrokeColor}
                            onChange={(e) => {
                              const val = e.target.value;
                              setTextStrokeColor(val);
                              if (activeTextLayerId) updateTextLayer(activeTextLayerId, { strokeColor: val });
                            }}
                            className="w-6 h-6 p-0 border-0 rounded overflow-hidden cursor-pointer"
                          />
                          <span className="text-[10px] text-gray-400">Stroke</span>
                        </div>

                        <div className="flex flex-col ml-1">
                          <span className="text-[10px] text-gray-400">Width</span>
                          <input
                            type="number"
                            value={textStrokeWidth}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setTextStrokeWidth(val);
                              if (activeTextLayerId) updateTextLayer(activeTextLayerId, { strokeWidth: val });
                            }}
                            className="w-10 h-6 bg-gray-800 text-white text-xs border border-gray-700 rounded px-1"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end space-x-2 mt-2">
                        <Button size="sm" variant="ghost" onClick={cancelText} className="h-7 text-xs">
                          Cancel
                        </Button>
                        <Button size="sm" onClick={applyText} className="h-7 text-xs bg-purple-600 hover:bg-purple-700">
                          {activeTextLayerId ? 'Finish Editing' : 'Add Text Layer'}
                        </Button>
                      </div>
                    </div>
            )}
          </div>

          {/* Right Panel (Layers) — desktop/tablet only */}
          {showLayersPanel && (
            <div className="hidden 2xl:flex w-72 min-w-72 max-w-72 basis-72 shrink-0 overflow-hidden bg-[#252525] border-l border-[#111] flex-col pointer-events-auto shadow-2xl z-20 text-[#d8d8d8]" style={{ width: '18rem', minWidth: '18rem', maxWidth: '18rem' }}>
              <div className="px-3 py-2 border-b border-[#111] flex items-center justify-between min-w-0 max-w-full overflow-hidden bg-[#303030] shadow-[0_1px_0_rgba(255,255,255,0.06)_inset]">
                <h3 className="font-semibold text-xs uppercase tracking-[0.18em] text-[#cfcfcf]">Layers</h3>
                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={addLayer}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>New Layer</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                          onClick={() => importImageInputRef.current?.click()}
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Import Guide Image</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            if (window.confirm('Delete active layer?')) deleteLayer(activeLayerId);
                          }}
                          disabled={layers.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete Layer</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {/* Hidden file input for image import */}
                <input
                  ref={importImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImportGuideImage}
                  className="hidden"
                  data-testid="import-guide-input"
                />
              </div>

              <ScrollArea className="flex-1 w-full max-w-full overflow-x-hidden">
                <div className="w-full max-w-full min-w-0 overflow-hidden p-2 space-y-1">
                  {layers.slice().reverse().map(layer => (
                    <div
                      key={layer.id}
                      className={cn(
                        "group rounded-sm border p-2 mb-1 max-w-full min-w-0 overflow-hidden transition-all cursor-pointer hover:bg-[#343434]",
                        activeLayerId === layer.id ? "bg-[#33415f] border-[#6aa0ff] shadow-[inset_3px_0_0_#6aa0ff]" : "border-[#303030] bg-[#202020]"
                      )}
                      onClick={() => setActiveLayerId(layer.id)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({ show: true, x: e.clientX, y: e.clientY, layerId: layer.id });
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setRenamingLayerId(layer.id);
                        setRenameValue(layer.name);
                      }}
                    >
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1 mb-2 min-w-0 max-w-full overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0 max-w-full overflow-hidden">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-6 w-6 shrink-0", !layer.visible && "text-muted-foreground")}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLayerVisibility(layer.id);
                            }}
                          >
                            {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </Button>

                          {/* Inline rename or display name */}
                          {renamingLayerId === layer.id ? (
                            <div className="min-w-0 max-w-full flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
                              <input
                                type="text"
                                size={1}
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onBlur={() => renameLayer(layer.id, renameValue)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') renameLayer(layer.id, renameValue);
                                  if (e.key === 'Escape') setRenamingLayerId(null);
                                }}
                                className="block w-full min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium bg-transparent border-b border-primary focus:outline-none"
                                style={{ width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          ) : (
                            <span className="block min-w-0 max-w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium select-none" title={layer.name}>{layer.name}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          {/* Mask indicator/toggle */}
                          {layer.hasMask && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-5 w-5 shrink-0",
                                layer.maskDisabled ? "text-muted-foreground" : "text-green-400"
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (e.shiftKey) {
                                  toggleMaskDisabled(layer.id);
                                } else if (e.altKey) {
                                  setViewingMask(viewingMask === layer.id ? null : layer.id);
                                }
                              }}
                              title="Shift+Click to disable, Alt+Click to view"
                            >
                              <ImageIcon className="w-3 h-3" />
                            </Button>
                          )}

                          {/* Lock button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-6 w-6 shrink-0", layer.locked && "text-amber-500")}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLayerLock(layer.id);
                            }}
                          >
                            {layer.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3 opacity-0 group-hover:opacity-50" />}
                          </Button>

                          {/* More actions menu button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setContextMenu({ show: true, x: e.clientX, y: e.clientY, layerId: layer.id });
                            }}
                          >
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {activeLayerId === layer.id && (
                        <div className="space-y-2 px-1 animate-in fade-in zoom-in-95 duration-200 min-w-0 overflow-hidden">
                          {/* Quick action buttons */}
                          <div className="flex items-center gap-1 mb-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs flex-1"
                              onClick={(e) => { e.stopPropagation(); duplicateLayer(layer.id); }}
                            >
                              <Copy className="w-3 h-3 mr-1" /> Duplicate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn("h-6 text-xs", layer.hasMask ? "text-red-400" : "")}
                              onClick={(e) => {
                                e.stopPropagation();
                                layer.hasMask ? deleteLayerMask(layer.id) : addLayerMask(layer.id);
                              }}
                            >
                              <ImageIcon className="w-3 h-3 mr-1" /> {layer.hasMask ? 'Del Mask' : 'Add Mask'}
                            </Button>
                          </div>

                          {!layer.locked && (
                            <div className="flex items-center gap-1 mb-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={(e) => { e.stopPropagation(); moveLayerUp(layer.id); }}
                                disabled={layers.findIndex(l => l.id === layer.id) === layers.length - 1}
                              >
                                <ArrowUp className="w-3 h-3 mr-1" /> Up
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs flex-1"
                                onClick={(e) => { e.stopPropagation(); moveLayerDown(layer.id); }}
                                disabled={layers.findIndex(l => l.id === layer.id) === 0}
                              >
                                <ArrowDown className="w-3 h-3 mr-1" /> Down
                              </Button>
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground w-8">Opacity</span>
                            <Slider
                              value={[layer.opacity]}
                              min={0}
                              max={1}
                              step={0.01}
                              onValueChange={(vals) => updateLayerOpacity(layer.id, vals[0])}
                              className="flex-1"
                            />
                            <span className="text-[10px] w-6 text-right">{Math.round(layer.opacity * 100)}%</span>
                          </div>

                          <Select
                            value={layer.blendMode}
                            onValueChange={(val) => updateLayerBlendMode(layer.id, val)}
                          >
                            <SelectTrigger className="h-7 text-xs w-full">
                              <SelectValue placeholder="Mode" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="multiply">Multiply</SelectItem>
                              <SelectItem value="screen">Screen</SelectItem>
                              <SelectItem value="overlay">Overlay</SelectItem>
                              <SelectItem value="darken">Darken</SelectItem>
                              <SelectItem value="lighten">Lighten</SelectItem>
                              <SelectItem value="color-dodge">Color Dodge</SelectItem>
                              <SelectItem value="color-burn">Color Burn</SelectItem>
                              <SelectItem value="hard-light">Hard Light</SelectItem>
                              <SelectItem value="soft-light">Soft Light</SelectItem>
                              <SelectItem value="difference">Difference</SelectItem>
                              <SelectItem value="exclusion">Exclusion</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="p-2 border-t border-[#111] bg-[#303030] text-center">
                <span className="text-[10px] text-muted-foreground">
                  {layers.length} Layer{layers.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Layer Context Menu */}
        {contextMenu.show && (
          <>
            {/* Backdrop to close menu on click outside */}
            <div
              className="fixed inset-0 z-[99]"
              onClick={() => setContextMenu({ show: false, x: 0, y: 0, layerId: null })}
            />
            {/* Menu */}
            <div
              className="fixed z-[100] bg-[#262626] border border-[#4a4a4a] rounded-sm shadow-2xl py-1 min-w-[180px] text-[#e5e5e5]"
              style={{ left: contextMenu.x, top: contextMenu.y }}
              onClick={(e) => {
                // Close on selection, but stop propagation if we don't want the backdrop to fire?
                // Actually, we want the menu items to handle their action and then the menu unmounts.
                // The backdrop handles clicks OUTSIDE.
                // Clicking INSIDE should also close it? Yes, usually.
                setContextMenu({ show: false, x: 0, y: 0, layerId: null });
              }}
            >
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => duplicateLayer(contextMenu.layerId)}
              >
                <Copy className="w-4 h-4" /> Duplicate <span className="ml-auto text-xs text-gray-400">Ctrl+J</span>
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => copyLayer(contextMenu.layerId)}
              >
                <Copy className="w-4 h-4" /> Copy <span className="ml-auto text-xs text-gray-400">Ctrl+C</span>
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => cutLayer(contextMenu.layerId)}
              >
                <Scissors className="w-4 h-4" /> Cut <span className="ml-auto text-xs text-gray-400">Ctrl+X</span>
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={pasteAsLayer}
                disabled={!layerClipboard}
              >
                <ClipboardPaste className="w-4 h-4" /> Paste as Layer <span className="ml-auto text-xs text-gray-400">Ctrl+V</span>
              </button>
              <div className="border-t border-gray-600 my-1" />
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => mergeLayerDown(contextMenu.layerId)}
              >
                <Merge className="w-4 h-4" /> Merge Down <span className="ml-auto text-xs text-gray-400">Ctrl+E</span>
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={flattenLayers}
              >
                <Layers className="w-4 h-4" /> Flatten All <span className="ml-auto text-xs text-gray-400">Ctrl+Shift+E</span>
              </button>
              <div className="border-t border-gray-600 my-1" />
              {layers.find(l => l.id === contextMenu.layerId)?.hasMask ? (
                <>
                  <button
                    className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                    onClick={() => invertLayerMask(contextMenu.layerId)}
                  >
                    <RotateCcw className="w-4 h-4" /> Invert Mask
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 text-red-400"
                    onClick={() => deleteLayerMask(contextMenu.layerId)}
                  >
                    <Trash2 className="w-4 h-4" /> Delete Mask
                  </button>
                </>
              ) : (
                <button
                  className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                  onClick={() => addLayerMask(contextMenu.layerId)}
                >
                  <ImageIcon className="w-4 h-4" /> Add Mask
                </button>
              )}
              <div className="border-t border-gray-600 my-1" />
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => clearLayer(contextMenu.layerId)}
              >
                <Trash2 className="w-4 h-4" /> Clear Layer <span className="ml-auto text-xs text-gray-400">Delete</span>
              </button>
              <button
                className="w-full px-4 py-2 text-left text-sm hover:bg-[#3a3a3a] flex items-center gap-2"
                onClick={() => {
                  setRenamingLayerId(contextMenu.layerId);
                  setRenameValue(layers.find(l => l.id === contextMenu.layerId)?.name || '');
                }}
              >
                <Type className="w-4 h-4" /> Rename
              </button>
              {layers.length > 1 && (
                <button
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 text-red-400"
                  onClick={() => deleteLayer(contextMenu.layerId)}
                >
                  <Trash2 className="w-4 h-4" /> Delete Layer
                </button>
              )}
            </div>
          </>
        )}

        {/* Transform Overlay */}
        {transformActive && (
          <div className="fixed inset-0 z-40 pointer-events-none">
            {/* Transform bounding box */}
            <div
              className="absolute border-2 border-blue-500 pointer-events-auto"
              style={{
                left: transformData.x * zoom + (window.innerWidth - canvasWidth * zoom) / 2,
                top: transformData.y * zoom + 100,
                width: transformData.width * zoom,
                height: transformData.height * zoom,
                transform: `rotate(${transformData.rotation}deg)`,
                transformOrigin: 'center'
              }}
            >
              {/* Corner handles */}
              {['nw', 'ne', 'sw', 'se'].map(pos => (
                <div
                  key={pos}
                  className="absolute w-3 h-3 bg-white border-2 border-blue-500 hover:bg-blue-200"
                  style={{
                    left: pos.includes('w') ? -6 : 'auto',
                    right: pos.includes('e') ? -6 : 'auto',
                    top: pos.includes('n') ? -6 : 'auto',
                    bottom: pos.includes('s') ? -6 : 'auto',
                    cursor: `${pos}-resize`
                  }}
                  onMouseDown={(e) => handleTransformDragStart(e, pos)}
                />
              ))}
              {/* Edge handles */}
              {['n', 's', 'e', 'w'].map(pos => (
                <div
                  key={pos}
                  className="absolute w-2 h-2 bg-white border border-blue-500 hover:bg-blue-200"
                  style={{
                    left: pos === 'w' ? -4 : pos === 'e' ? 'auto' : '50%',
                    right: pos === 'e' ? -4 : 'auto',
                    top: pos === 'n' ? -4 : pos === 's' ? 'auto' : '50%',
                    bottom: pos === 's' ? -4 : 'auto',
                    transform: (pos === 'n' || pos === 's') ? 'translateX(-50%)' : 'translateY(-50%)',
                    cursor: `${pos === 'n' || pos === 's' ? 'ns' : 'ew'}-resize`
                  }}
                  onMouseDown={(e) => handleTransformDragStart(e, pos)}
                />
              ))}
              {/* Center drag handle for move */}
              <div
                className="absolute inset-0 cursor-move"
                onMouseDown={(e) => handleTransformDragStart(e, 'move')}
              />
            </div>

            {/* Transform toolbar */}
            <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-gray-800 rounded-lg shadow-xl p-2 flex items-center gap-2 pointer-events-auto">
              <span className="text-xs text-gray-400 px-2">Transform</span>
              <Button size="sm" variant="outline" onClick={applyTransform} className="text-green-400">
                Apply (Enter)
              </Button>
              <Button size="sm" variant="outline" onClick={cancelTransform} className="text-red-400">
                Cancel (Esc)
              </Button>
            </div>
          </div>
        )}

        {/* ═══ MOBILE BOTTOM TOOLBAR ═══ */}
        {/* Visible only on mobile (<768px) — replaces the desktop left sidebar */}
        <div className="2xl:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#252525]/95 backdrop-blur border-t border-[#111] shadow-2xl">
          <div className="flex items-center justify-around px-1 py-1 overflow-x-auto gap-0.5">
            <TooltipProvider delayDuration={0}>
              {tools.map(tool => (
                <Button
                  key={tool.name}
                  variant={currentTool === tool.name ? "default" : "ghost"}
                  size="icon"
                  onClick={() => tool.name === 'ai' ? openAIEditor() : tool.name === 'animcanvas' ? setShowAnimCanvas(true) : setCurrentTool(tool.name)}
                  className={cn(
                    "h-11 w-11 rounded-md shrink-0 transition-all text-[#d0d0d0] hover:bg-[#3a3a3a] border border-transparent",
                    tool.special && "bg-[#3a2f4f] text-[#f1e9ff] hover:bg-[#493b63] border-[#6c55a1]",
                    currentTool === tool.name && !tool.special && "bg-[#4f8cff] text-white hover:bg-[#6aa0ff] shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_0_14px_rgba(79,140,255,0.28)] border-[#8bb6ff]"
                  )}
                  data-testid={`mobile-tool-${tool.name}`}
                >
                  <tool.icon className="w-5 h-5" />
                </Button>
              ))}
            </TooltipProvider>
          </div>
        </div>

        {/* ═══ MOBILE LAYERS BOTTOM SHEET ═══ */}
        {/* Appears on mobile when user taps the Layers button in the header */}
        {showMobileLayersPanel && (
          <div
            className="2xl:hidden fixed inset-0 z-50 flex flex-col justify-end"
            onClick={(e) => { if (e.target === e.currentTarget) setShowMobileLayersPanel(false); }}
          >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileLayersPanel(false)} />
            <div className="relative bg-[#252525] text-[#d8d8d8] rounded-t-xl border-t border-[#111] shadow-2xl max-h-[70vh] flex flex-col animate-in slide-in-from-bottom-4 duration-200">
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
              </div>
              {/* Header */}
              <div className="px-4 pb-3 border-b flex items-center justify-between">
                <h3 className="font-semibold text-xs uppercase tracking-[0.18em] text-[#cfcfcf]">Layers</h3>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={addLayer}>
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-purple-400"
                    onClick={() => importImageInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive"
                    onClick={() => { if (window.confirm('Delete active layer?')) deleteLayer(activeLayerId); }}
                    disabled={layers.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowMobileLayersPanel(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {/* Layer list */}
              <div className="overflow-y-auto flex-1 p-2">
                {[...layers].reverse().map(layer => (
                  <div
                    key={layer.id}
                    onClick={() => setActiveLayerId(layer.id)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer mb-1 transition-all",
                      activeLayerId === layer.id
                        ? "bg-primary/20 border border-primary/40"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                    >
                      {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                    </Button>
                    <span className="block min-w-0 max-w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium" title={layer.name}>{layer.name}</span>
                    {activeLayerId === layer.id && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Slider
                          value={[layer.opacity]}
                          min={0}
                          max={1}
                          step={0.05}
                          onValueChange={(vals) => updateLayerOpacity(layer.id, vals[0])}
                          className="w-20"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-xs text-muted-foreground w-8">{Math.round(layer.opacity * 100)}%</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Editor Overlay */}
        {showAIEditor && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-0 lg:p-4">
            <Card className="bg-gray-800 border-purple-500/50 w-full h-full lg:max-w-6xl lg:w-full lg:max-h-[90vh] lg:h-auto flex flex-col shadow-2xl overflow-hidden rounded-none lg:rounded-xl">
              <div className="px-3 py-2 lg:p-4 border-b border-purple-500/30 flex items-center justify-between bg-gray-900/50 shrink-0">
                <div className="flex items-center gap-2 overflow-x-auto">
                  <Sparkles className="w-5 h-5 lg:w-6 lg:h-6 text-purple-400 shrink-0" />
                  <h2 className="text-lg lg:text-2xl font-bold text-white shrink-0">AI Studio</h2>
                  <div
                    onClick={() => navigate('/credits')}
                    className={cn(
                      "px-3 py-1 rounded-full border cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-2",
                      credits < 3 ? 'bg-red-900/50 border-red-500/50' : 'bg-gray-700 border-purple-500/50'
                    )}
                    title="Click to buy more credits"
                  >
                    <span className={cn("text-sm font-semibold", credits < 3 ? 'text-red-400' : 'text-purple-400')}>
                      Credits: {credits}
                    </span>
                    {credits < 3 && <span className="text-xs text-red-400 font-bold">âš ï¸ Low</span>}
                  </div>
                  {credits < 5 && (
                    <Button
                      size="sm"
                      onClick={() => navigate('/credits')}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-full h-7"
                      data-testid="get-more-credits-btn"
                    >
                      Get More
                    </Button>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowAIEditor(false);
                    // Memory cleanup: release base64 strings from React state
                    setAiCanvasSnapshot(null);
                    setAiGeneratedImage(null);
                    setAiMaskCanvas(null);
                    setInpaintResult(null);
                  }}
                  className="text-gray-400 hover:text-white hover:bg-white/10"
                  data-testid="close-ai-editor-btn"
                >
                  <X className="w-6 h-6" />
                </Button>
              </div>

              {/* Panels: stack vertically on mobile, horizontal on lg */}
              <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Left Side - Reference Canvas / Mask Drawing */}
                <div className="flex-1 min-h-0 p-3 lg:p-4 flex flex-col bg-gray-900/30 overflow-hidden" style={{minHeight: '200px'}}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold">
                      {aiMode === 'inpaint'
                        ? (inpaintResult ? 'Inpaint Result (Click to use as new reference)' : 'Draw Mask (Paint areas to regenerate)')
                        : 'Your Canvas (Reference)'}
                    </h3>
                    {aiMode === 'inpaint' && (
                      <div className="flex items-center space-x-2">
                        {inpaintResult && (
                          <Button
                            size="sm"
                            onClick={() => {
                              // Use inpaint result as new reference
                              setAiCanvasSnapshot(inpaintResult);
                              setInpaintResult(null);
                              // Clear mask
                              if (maskCanvasRef.current) {
                                const ctx = maskCanvasRef.current.getContext('2d');
                                ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                              }
                              toast.success('Using result as new reference');
                            }}
                            className="bg-green-600/50 hover:bg-green-600 text-white h-7 text-xs"
                          >
                            Use as Reference
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            // Clear mask and result
                            if (maskCanvasRef.current) {
                              const ctx = maskCanvasRef.current.getContext('2d');
                              ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                            }
                            setInpaintResult(null);
                          }}
                          className="h-7 text-xs"
                        >
                          Clear Mask
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Mask Brush Size Slider for Inpaint */}
                  {aiMode === 'inpaint' && !inpaintResult && (
                    <div className="flex items-center space-x-3 mb-3 bg-gray-700/50 rounded-lg p-3 border border-gray-700">
                      <span className="text-gray-400 text-xs whitespace-nowrap font-medium">Mask Brush:</span>
                      <Slider
                        min={5}
                        max={100}
                        step={1}
                        value={[maskBrushSize]}
                        onValueChange={(vals) => setMaskBrushSize(vals[0])}
                        className="flex-1"
                      />
                      <span className="text-white text-xs w-10 text-right tabular-nums">{maskBrushSize}px</span>
                    </div>
                  )}

                  <div
                    className="flex-1 bg-gray-950 rounded-lg flex items-center justify-center overflow-hidden relative border border-gray-800"
                    onMouseEnter={() => aiMode === 'inpaint' && !inpaintResult && setShowMaskCursor(true)}
                    onMouseLeave={() => setShowMaskCursor(false)}
                  >
                    {/* Show inpaint result if available */}
                    {aiMode === 'inpaint' && inpaintResult ? (
                      <img
                        src={inpaintResult}
                        alt="Inpaint result"
                        className="max-w-full max-h-full object-contain cursor-pointer"
                        onClick={() => {
                          setAiCanvasSnapshot(inpaintResult);
                          setInpaintResult(null);
                          if (maskCanvasRef.current) {
                            const ctx = maskCanvasRef.current.getContext('2d');
                            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                          }
                          toast.success('Using result as new reference');
                        }}
                      />
                    ) : aiCanvasSnapshot ? (
                      <>
                        <img
                          src={aiCanvasSnapshot}
                          alt="Canvas reference"
                          className="max-w-full max-h-full object-contain"
                          style={{ pointerEvents: aiMode === 'inpaint' ? 'none' : 'auto' }}
                        />
                        {/* Mask canvas overlay for inpainting */}
                        {aiMode === 'inpaint' && (
                          <>
                            <canvas
                              ref={maskCanvasRef}
                              width={canvasWidth}
                              height={canvasHeight}
                              className="absolute inset-0"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                margin: 'auto',
                                cursor: 'none'
                              }}
                              onMouseDown={(e) => {
                                setIsDrawingMask(true);
                                const canvas = maskCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const scaleX = canvas.width / rect.width;
                                const scaleY = canvas.height / rect.height;
                                const x = (e.clientX - rect.left) * scaleX;
                                const y = (e.clientY - rect.top) * scaleY;

                                const ctx = canvas.getContext('2d');
                                ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
                                ctx.beginPath();
                                ctx.arc(x, y, maskBrushSize, 0, 2 * Math.PI);
                                ctx.fill();
                              }}
                              onMouseMove={(e) => {
                                const canvas = maskCanvasRef.current;
                                const rect = canvas.getBoundingClientRect();
                                const scaleX = canvas.width / rect.width;
                                const scaleY = canvas.height / rect.height;
                                const x = (e.clientX - rect.left) * scaleX;
                                const y = (e.clientY - rect.top) * scaleY;

                                // OPTIMIZED: Direct DOM update for mask cursor
                                if (maskCursorRef.current) {
                                  // Use FIXED positioning (clientX/Y) to avoid offsets caused by container/canvas relative positioning
                                  const scale = rect.width / canvas.width;
                                  const size = maskBrushSize * 2 * scale;

                                  // Center the fixed cursor on the mouse
                                  maskCursorRef.current.style.transform = `translate(${e.clientX - size / 2}px, ${e.clientY - size / 2}px)`;
                                  maskCursorRef.current.style.width = `${size}px`;
                                  maskCursorRef.current.style.height = `${size}px`;
                                }

                                if (!isDrawingMask) return;

                                const ctx = canvas.getContext('2d');
                                ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
                                ctx.beginPath();
                                ctx.arc(x, y, maskBrushSize, 0, 2 * Math.PI);
                                ctx.fill();
                              }}
                              onMouseUp={() => setIsDrawingMask(false)}
                              onMouseLeave={() => setIsDrawingMask(false)}
                            />
                            {/* Mask brush cursor */}
                            {showMaskCursor && (
                              <div
                                ref={maskCursorRef}
                                className="fixed pointer-events-none rounded-full border-2 border-red-500 transition-none z-[100]"
                                style={{
                                  left: 0,
                                  top: 0,
                                  backgroundColor: 'rgba(255, 0, 0, 0.2)',
                                  boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                                  transform: 'translate(-100px, -100px)' // Initial off-screen
                                }}
                              />
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-gray-500">
                        <p>Open AI Studio from the editor to use inpainting</p>
                      </div>
                    )}
                  </div>
                  {aiMode === 'inpaint' && !inpaintResult && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-red-500/50 block"></span>
                      Paint red over the areas you want the AI to regenerate.
                    </p>
                  )}
                </div>

                {/* Middle - AI Controls */}
                <div className="w-full lg:w-80 shrink-0 border-t lg:border-t-0 lg:border-x border-purple-500/30 bg-gray-900/80 p-3 lg:p-5 flex flex-col space-y-4 lg:space-y-6 overflow-y-auto max-h-52 lg:max-h-none">
                  <div>
                    <h3 className="text-white font-semibold mb-2 text-xs uppercase tracking-wider text-purple-400">AI Mode</h3>
                    <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto pb-1">
                      <Button
                        variant={aiMode === 'generate' ? "default" : "secondary"}
                        onClick={() => setAiMode('generate')}
                        className={cn("justify-start shrink-0", aiMode === 'generate' ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white")}
                        data-testid="ai-mode-generate"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Text â†’ Image
                      </Button>
                      <Button
                        variant={aiMode === 'sketch2ai' ? "default" : "secondary"}
                        onClick={() => setAiMode('sketch2ai')}
                        className={cn("justify-start shrink-0", aiMode === 'sketch2ai' ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white")}
                        data-testid="ai-mode-sketch"
                      >
                        <Brush className="w-4 h-4 mr-2" />
                        Sketch â†’ AI
                      </Button>
                      <Button
                        variant={aiMode === 'inpaint' ? "default" : "secondary"}
                        onClick={() => {
                          setAiMode('inpaint');
                          // Initialize mask canvas when switching to inpaint mode
                          setTimeout(() => {
                            if (maskCanvasRef.current) {
                              const ctx = maskCanvasRef.current.getContext('2d');
                              ctx.clearRect(0, 0, canvasWidth, canvasHeight);
                            }
                          }, 100);
                        }}
                        className={cn("justify-start shrink-0", aiMode === 'inpaint' ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white")}
                        data-testid="ai-mode-inpaint"
                      >
                        <Eraser className="w-4 h-4 mr-2" />
                        Inpaint (Mask)
                      </Button>
                    </div>
                  </div>

                  {/* Model Engine Selector */}
                  <div>
                    <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider text-amber-400">Model Engine</h3>
                    <div className="flex flex-col space-y-2">
                      <button
                        onClick={() => setAiModel('gemini')}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${aiModel === 'gemini'
                          ? 'bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border border-cyan-500/50 text-cyan-300'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                          }`}
                      >
                        <Sparkles className="w-4 h-4" />
                        <div className="flex-1 text-left">
                          <span>Gemini</span>
                          <span className="block text-xs opacity-60">Google AI &#8226; Active</span>
                        </div>
                        {aiModel === 'gemini' && <Check className="w-4 h-4 text-cyan-400" />}
                      </button>
                      <button
                        onClick={() => setAiModel('cavaggio')}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${aiModel === 'cavaggio'
                          ? 'bg-gradient-to-r from-amber-600/30 to-orange-600/30 border border-amber-500/50 text-amber-300'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-transparent'
                          }`}
                      >
                        <Palette className="w-4 h-4" />
                        <div className="flex-1 text-left">
                          <span>CARAVAGGIO</span>
                          <span className="block text-xs opacity-60">Custom Model &#8226; Active</span>
                        </div>
                        {aiModel === 'cavaggio' && <Check className="w-4 h-4 text-amber-400" />}
                      </button>
                    </div>
                    {aiModel === 'cavaggio' && (
                      <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-xs text-emerald-400">
                          &#10024; CARAVAGGIO v1.0 is live! Generating with our custom comic-style model. May take ~30s on first request.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Label className="text-white font-semibold">Prompt</Label>
                    <Textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Describe what you want to generate..."
                      className="bg-gray-800/50 border-purple-500/30 focus:border-purple-500 text-white placeholder:text-gray-500/50 min-h-[100px]"
                      data-testid="ai-prompt-input"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="text-white font-semibold">Negative Prompt</Label>
                    <Textarea
                      value={aiNegativePrompt}
                      onChange={(e) => setAiNegativePrompt(e.target.value)}
                      placeholder="What to avoid..."
                      className="bg-gray-800/50 border-purple-500/30 focus:border-purple-500 text-white placeholder:text-gray-500/50 min-h-[80px]"
                      data-testid="ai-negative-prompt-input"
                    />
                  </div>

                  {aiMode === 'sketch2ai' && (
                    <div className="space-y-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                      <div className="flex justify-between items-center">
                        <Label className="text-white font-semibold">Strength</Label>
                        <span className="text-purple-400 font-mono text-sm">{aiStrength.toFixed(1)}</span>
                      </div>
                      <Slider
                        min={0.1}
                        max={1}
                        step={0.1}
                        value={[aiStrength]}
                        onValueChange={(vals) => setAiStrength(vals[0])}
                        className="py-2"
                        data-testid="ai-strength-slider"
                      />
                      <div className="flex justify-between text-[10px] text-gray-400">
                        <span>Low: Follow sketch</span>
                        <span>High: More creative</span>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={generateAI}
                    disabled={isGenerating || credits < 1}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg shadow-lg shadow-purple-900/20"
                    data-testid="generate-ai-btn"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate (1 credit)
                      </>
                    )}
                  </Button>

                  {aiGeneratedImage && (
                    <Button
                      onClick={appendAIToCanvas}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg shadow-lg shadow-green-900/20"
                      data-testid="append-ai-btn"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Append to Canvas
                    </Button>
                  )}
                </div>

                {/* Right Side - AI Result */}
                <div className="flex-1 min-h-0 p-3 lg:p-4 flex flex-col bg-gray-900/30 border-t lg:border-t-0 border-purple-500/30" style={{minHeight: '200px'}}>
                  <h3 className="text-white font-semibold mb-3">AI Generated Result</h3>
                  <div className="flex-1 bg-gray-950 rounded-lg flex items-center justify-center overflow-hidden border border-gray-800 relative">
                    {aiGeneratedImage ? (
                      <img
                        src={aiGeneratedImage}
                        alt="AI generated"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : isGenerating ? (
                      <div className="text-center flex flex-col items-center">
                        {/* Animated generating placeholder */}
                        <div className="relative w-24 h-24 mb-6">
                          <div className="absolute inset-0 rounded-full border-4 border-purple-500/20" />
                          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-500 animate-spin" />
                          <div className="absolute inset-2 rounded-full border-4 border-transparent border-b-pink-500 animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-purple-400 animate-pulse" />
                          </div>
                        </div>
                        <p className="text-purple-300 font-semibold text-lg mb-2">Creating your artwork...</p>
                        <p className="text-gray-400 text-sm mb-3">
                          {generationElapsed < 10 ? 'Warming up AI engine...' :
                           generationElapsed < 30 ? 'Analyzing your input...' :
                           generationElapsed < 60 ? 'Generating image...' :
                           generationElapsed < 120 ? 'Still working — complex generation in progress...' :
                           'Almost there — hang tight!'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                          <span className="font-mono">{Math.floor(generationElapsed / 60).toString().padStart(2, '0')}:{(generationElapsed % 60).toString().padStart(2, '0')}</span>
                          <span>elapsed</span>
                        </div>
                        <div className="mt-4 w-48 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 rounded-full animate-pulse" style={{ width: `${Math.min(95, generationElapsed * 1.5)}%`, transition: 'width 1s ease-out' }} />
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 flex flex-col items-center">
                        <Sparkles className="w-16 h-16 mb-4 opacity-20" />
                        <p>AI result will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div >
        )}

        {/* Pie Menu — floating panel, does NOT block canvas */}
        {
          showPieMenu && (
            <div
              ref={pieMenuRef}
              className="fixed bg-[#252525]/95 backdrop-blur-xl rounded-lg border border-[#5a5a5a] shadow-2xl p-5 z-[200] text-[#e5e5e5]"
              style={{
                left: (pieMenuPosition?.x || window.innerWidth / 2) - 175,
                top: (pieMenuPosition?.y || window.innerHeight / 2) - 200,
                width: 350,
                minHeight: 400
              }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              data-testid="pie-menu"
            >
              {/* Pie Menu Header — drag handle */}
              <div
                ref={(el) => { if (el) el._dragHandle = true; }}
                className="flex items-center justify-between mb-4 cursor-grab active:cursor-grabbing select-none"
                style={{ touchAction: 'none' }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  const handle = e.currentTarget;
                  try { handle.setPointerCapture(e.pointerId); } catch (err) { }
                  // Read CURRENT position from the actual DOM element (not state which may be stale)
                  const menuEl = pieMenuRef.current;
                  if (!menuEl) return;
                  const rect = menuEl.getBoundingClientRect();
                  pieMenuDragRef.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    menuStartLeft: rect.left,
                    menuStartTop: rect.top,
                  };
                }}
                onPointerMove={(e) => {
                  if (!pieMenuDragRef.current) return;
                  e.stopPropagation();
                  const dx = e.clientX - pieMenuDragRef.current.startX;
                  const dy = e.clientY - pieMenuDragRef.current.startY;
                  // ⚡ Direct DOM update — zero React re-renders, zero lag at 120Hz pen
                  const menuEl = pieMenuRef.current;
                  if (menuEl) {
                    menuEl.style.left = `${pieMenuDragRef.current.menuStartLeft + dx}px`;
                    menuEl.style.top  = `${pieMenuDragRef.current.menuStartTop  + dy}px`;
                  }
                }}
                onPointerUp={(e) => {
                  e.stopPropagation();
                  if (!pieMenuDragRef.current) return;
                  // Persist final position into React state ONCE (on release)
                  const menuEl = pieMenuRef.current;
                  if (menuEl) {
                    const rect = menuEl.getBoundingClientRect();
                    setPieMenuPosition({ x: rect.left + 175, y: rect.top + 200 });
                  }
                  pieMenuDragRef.current = null;
                }}
              >
                <h3 className="text-white font-bold text-lg flex items-center">
                  <Command className="w-5 h-5 mr-2 text-purple-400" />
                  Quick Settings
                </h3>
                <span className="text-xs text-gray-500">⠿ drag</span>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => setShowPieMenu(false)}
                  className="text-gray-400 hover:text-white ml-2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-700 transition-colors"
                  title="Close (Space or Escape)"
                >×</button>
              </div>

              {/* Color Wheel - Inline */}
              <div className="flex justify-center mb-4">
                <div className="relative" style={{ width: 140, height: 140 }}>
                  {/* Color wheel background */}
                  <div
                    className="absolute inset-0 rounded-full cursor-crosshair"
                    style={{
                      background: `conic-gradient(from 180deg, 
                        hsl(0, 100%, 50%), 
                        hsl(60, 100%, 50%), 
                        hsl(120, 100%, 50%), 
                        hsl(180, 100%, 50%), 
                        hsl(240, 100%, 50%), 
                        hsl(300, 100%, 50%), 
                        hsl(360, 100%, 50%))`,
                      WebkitMask: `radial-gradient(circle, transparent 20%, black 20%)`,
                      mask: `radial-gradient(circle, transparent 20%, black 20%)`
                    }}
                    onPointerDown={(e) => handleColorWheelClick(e, 140)}
                    onPointerMove={(e) => e.buttons > 0 && handleColorWheelClick(e, 140)}
                  />

                  {/* Saturation overlay */}
                  <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: `radial-gradient(circle, white 0%, transparent 70%)`,
                      WebkitMask: `radial-gradient(circle, transparent 20%, black 20%)`,
                      mask: `radial-gradient(circle, transparent 20%, black 20%)`
                    }}
                  />

                  {/* Current color indicator */}
                  <div
                    className="absolute w-4 h-4 rounded-full border-2 border-white shadow-lg pointer-events-none"
                    style={{
                      backgroundColor: brushColor,
                      left: `calc(50% + ${Math.cos((colorHue - 180) * Math.PI / 180) * (colorSaturation / 100) * 60}px - 8px)`,
                      top: `calc(50% + ${Math.sin((colorHue - 180) * Math.PI / 180) * (colorSaturation / 100) * 60}px - 8px)`
                    }}
                  />

                  {/* Center color preview */}
                  <div
                    className="absolute rounded-full border-2 border-gray-600"
                    style={{
                      width: 49,
                      height: 49,
                      left: 45.5,
                      top: 45.5,
                      backgroundColor: brushColor
                    }}
                  />
                </div>
              </div>

              {/* Color Swatches */}
              <div className="flex justify-center space-x-2 mb-4">
                <div
                  className="w-8 h-8 rounded border-2 border-white/50 cursor-pointer"
                  style={{ backgroundColor: brushColor }}
                  title="Primary Color"
                />
                <button
                  onClick={() => {
                    const temp = brushColor;
                    setBrushColor(secondaryColor);
                    setSecondaryColor(temp);
                  }}
                  className="text-gray-400 hover:text-white px-1"
                  title="Swap Colors (X)"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <div
                  className="w-8 h-8 rounded border-2 border-gray-500 cursor-pointer"
                  style={{ backgroundColor: secondaryColor }}
                  title="Secondary Color"
                  onClick={() => {
                    const temp = brushColor;
                    setBrushColor(secondaryColor);
                    setSecondaryColor(temp);
                  }}
                />
              </div>

              {/* Screen Color Picker — picks from anywhere */}
              {'EyeDropper' in window && (
                <button
                  onClick={async () => {
                    try {
                      const dropper = new window.EyeDropper();
                      const result = await dropper.open();
                      if (result?.sRGBHex) {
                        setBrushColor(result.sRGBHex);
                        toast.success(`Color picked: ${result.sRGBHex}`);
                      }
                    } catch (e) { /* cancelled */ }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors mb-3"
                  title="Pick a color from anywhere on screen (even outside Chrome)"
                >
                  <Pipette className="w-4 h-4" />
                  <span>Pick from Screen</span>
                </button>
              )}

              {/* Lightness Slider */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Lightness</span>
                  <span>{Math.round(colorLightness)}%</span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  value={[colorLightness]}
                  onValueChange={(vals) => updateColorFromWheel(colorHue, colorSaturation, vals[0])}
                  className="w-full"
                  style={{
                    '--slider-track-background': `linear-gradient(to right, black, ${hslToHex(colorHue, colorSaturation, 50)}, white)`
                  }}
                />
              </div>

              {/* Brush Size */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Brush Size</span>
                  <span>{brushSize}px</span>
                </div>
                <Slider
                  min={1}
                  max={200}
                  value={[brushSize]}
                  onValueChange={(vals) => setBrushSize(vals[0])}
                  className="w-full"
                />
              </div>

              {/* Opacity */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Opacity</span>
                  <span>{Math.round(brushOpacity * 100)}%</span>
                </div>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={[brushOpacity]}
                  onValueChange={(vals) => setBrushOpacity(vals[0])}
                  className="w-full"
                />
              </div>

              {/* Brush Angle */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>Brush Angle</span>
                  <span>{brushAngle}Â°</span>
                </div>
                <Slider
                  min={0}
                  max={360}
                  value={[brushAngle]}
                  onValueChange={(vals) => setBrushAngle(vals[0])}
                  className="w-full"
                />
              </div>

              {/* Brush History */}
              {brushHistory.length > 0 && (
                <div>
                  <div className="flex items-center text-xs text-gray-400 mb-2">
                    <History className="w-3 h-3 mr-1" />
                    <span>Recent Brushes</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brushHistory.slice(0, 6).map((item, index) => (
                      <button
                        key={index}
                        onClick={() => applyBrushFromHistory(item)}
                        className="w-10 h-10 rounded-lg border border-gray-600 hover:border-purple-500 flex items-center justify-center transition-all"
                        style={{ backgroundColor: item.color }}
                        title={`${item.brush.name} - ${item.size}px`}
                      >
                        <div
                          className="w-6 h-6 rounded-full bg-gray-900/50"
                          style={{ transform: `scale(${item.size / 50})` }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        }

        {/* Keyboard Shortcuts Panel */}
        {
          showShortcutsPanel && (
            <div
              className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-center justify-center"
              onClick={() => setShowShortcutsPanel(false)}
            >
              <div
                className="bg-[#252525] border border-[#555] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto shadow-2xl text-[#e5e5e5]"
                onClick={(e) => e.stopPropagation()}
                data-testid="shortcuts-panel"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center">
                    <Keyboard className="w-6 h-6 mr-2 text-purple-400" />
                    Keyboard Shortcuts
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowShortcutsPanel(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {/* Tools */}
                  <div>
                    <h3 className="text-purple-400 font-semibold mb-3 text-sm uppercase tracking-wider">Tools</h3>
                    <div className="space-y-2">
                      {[
                        ['B', 'Brush Tool'],
                        ['E', 'Eraser Tool'],
                        ['G', 'Paint Bucket'],
                        ['I', 'Eyedropper'],
                        ['Alt', 'Temp Eyedropper (hold)'],
                        ['R + Drag', 'Rotate Canvas View'],
                        ['M', 'Marquee Selection'],
                        ['V', 'Move Tool'],
                        ['T', 'Text Tool'],
                        // ['L', 'Line Tool'],
                        // ['U', 'Shape Tools (cycle)'],
                        // ['C', 'Crop Tool'],
                      ].map(([key, action]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-300">{action}</span>
                          <kbd className="bg-gray-800 px-2 py-1 rounded text-purple-400 font-mono text-xs">{key}</kbd>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div>
                    <h3 className="text-purple-400 font-semibold mb-3 text-sm uppercase tracking-wider">Actions</h3>
                    <div className="space-y-2">
                      {[
                        ['Space', 'Quick Settings (Pie Menu)'],
                        ['Ctrl+Z', 'Undo'],
                        ['Ctrl+Shift+Z', 'Redo'],
                        ['Ctrl+S', 'Save'],
                        ['Ctrl+E', 'Export'],
                        ['[', 'Decrease Brush Size'],
                        [']', 'Increase Brush Size'],
                        ['1-9, 0', 'Opacity 10%-100%'],
                        ['X', 'Swap Colors'],
                        ['D', 'Default Colors'],
                        ['?', 'Toggle This Panel'],
                        ['Esc', 'Close Menus/Deselect'],
                      ].map(([key, action]) => (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <span className="text-gray-300">{action}</span>
                          <kbd className="bg-gray-800 px-2 py-1 rounded text-purple-400 font-mono text-xs">{key}</kbd>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-700 text-center text-gray-500 text-sm">
                  Press <kbd className="bg-gray-800 px-2 py-0.5 rounded text-purple-400 font-mono text-xs">?</kbd> anytime to toggle this panel
                </div>
              </div>
            </div>
          )
        }

        {/* Recording Indicator Overlay */}
        {isRecording && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[300] bg-red-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg animate-pulse">
            <div className="w-3 h-3 rounded-full bg-white animate-ping" />
            <span className="font-mono font-bold" ref={recTimerDisplay2Ref}>
              REC 00:00
            </span>
            <button
              onClick={stopRecording}
              className="ml-2 bg-white/20 hover:bg-white/40 rounded-full px-3 py-0.5 text-sm transition-colors"
            >
              Stop
            </button>
          </div>
        )}

        {/* Processing Timelapse Overlay — blocks ALL interaction */}
        {processingTimelapse && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999]" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gray-900/95 border border-purple-500/40 rounded-2xl p-8 text-center shadow-2xl max-w-sm">
              <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <h3 className="text-white text-xl font-bold mb-2">⏳ Rendering Timelapse...</h3>
              <p className="text-amber-400 text-sm font-medium mb-4">
                ⚠️ Please do not close this window or navigate away.
                <br />Your recording will be lost if you leave.
              </p>
              <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
                <div
                  className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-150"
                  style={{ width: `${Math.round((processingTimelapse.current / processingTimelapse.total) * 100)}%` }}
                />
              </div>
              <p className="text-gray-300 text-sm font-mono">
                {Math.round((processingTimelapse.current / processingTimelapse.total) * 100)}% — Frame {processingTimelapse.current} of {processingTimelapse.total}
              </p>
              <p className="text-gray-500 text-xs mt-2">
                ~{Math.ceil((processingTimelapse.total - processingTimelapse.current) * 0.04)}s remaining
              </p>
            </div>
          </div>
        )}

        {/* Recording Download Modal */}
        {showRecordingDownload && recordingBlob && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[300] p-4">
            <div
              className="bg-gray-900 rounded-2xl border border-purple-500/30 p-6 max-w-lg w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-400" />
                  Timelapse Ready!
                </h3>
                <button
                  onClick={() => { setShowRecordingDownload(false); setRecordingBlob(null); }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Video Preview */}
              <div className="bg-black rounded-lg overflow-hidden mb-4">
                <video
                  src={URL.createObjectURL(recordingBlob)}
                  controls
                  autoPlay
                  loop
                  className="w-full max-h-64 object-contain"
                />
              </div>

              <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                <span ref={recTimerDisplay3Ref}>Duration: 0m 0s</span>
                <span>Size: {(recordingBlob.size / 1024 / 1024).toFixed(1)} MB</span>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="default"
                  className="flex-1 gap-2"
                  onClick={downloadRecording}
                >
                  <Download className="w-4 h-4" />
                  Download Timelapse
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => { setShowRecordingDownload(false); setRecordingBlob(null); }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Save Project Modal */}
        {
          showSaveModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSaveModal(false)}>
              <Card
                className="bg-gray-900 border-purple-500/50 max-w-md w-full p-6 shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                  <Save className="w-6 h-6 text-purple-400" />
                  Save Project
                </h2>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Project Title *</Label>
                    <Input
                      type="text"
                      value={saveTitle}
                      onChange={(e) => setSaveTitle(e.target.value)}
                      placeholder="Enter project title..."
                      className="bg-gray-800 border-gray-700 text-white"
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">Description (optional)</Label>
                    <Textarea
                      value={saveDescription}
                      onChange={(e) => setSaveDescription(e.target.value)}
                      placeholder="Add a description..."
                      rows={3}
                      className="bg-gray-800 border-gray-700 text-white resize-none"
                    />
                  </div>

                  <div className="pt-2 text-sm text-gray-400">
                    Your project will be saved to your profile and visible in your dashboard.
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="secondary"
                    onClick={() => setShowSaveModal(false)}
                    className="flex-1"
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveProject}
                    disabled={isSaving || !saveTitle.trim()}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Project
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            </div>
          )
        }
      </div >
    </div >
  );
};

export default ComicaProV2;
